import {t, useLanguage} from '../i18n';
import {BusinessRegionContext,useBusinessRegion,regionMoney} from '../utils/businessRegionContext';
import {SessionServiceFields} from "../components/SessionPrograms";
import EventLogisticsFields from "../components/events/EventLogisticsFields";
import EventResourcesEditor,{EventResourceRequirements} from "../components/events/EventResourcesEditor";
import {eventScheduleSettings} from "../utils/eventScheduleSettings";
import {validateServiceBasics} from "../utils/businessEditorValidation";
import {usePendingEditor} from "../hooks/useEditorNavigation";
// LOOP SERVICE PRESENTATION LABEL POLISH V1
// LOOP SERVICE DESCRIPTION V1 — optional 160-char public service descriptions.
// LOOP FROM PRICE V1 — optional fixed/from pricing for standard services.
// src/sections/ServicesSection.jsx
// LOOP PHASE 2.9 — Service materials stock risk polish
// LOOP PHASE 2.5B — Service materials + margin editor
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Lucide from "lucide-react";
import * as FaIcons from "react-icons/fa";
import * as GiIcons from "react-icons/gi";
import * as MdIcons from "react-icons/md";
import { createPortal } from "react-dom";
import { updateDoc, doc, serverTimestamp, getDoc, writeBatch, collection, getDocs } from "firebase/firestore";
import { loopBusinessBulkUpdateCollection, loopBusinessNow, loopBusinessUpdateDoc } from "../utils/businessNativeSafeWrites";
import { db as defaultDb } from "../firebase";
import { IMAGE_UPLOAD_ACCEPT, prepareImageFileForUpload } from "../utils/imageUploadUtils";
import {
  BOOKING_PAYMENT_POLICY_VERSION,
  bookingPaymentBalanceCents,
  bookingPaymentDueNowCents,
  bookingPaymentPolicyEligibility,
  formatBookingPaymentPolicySummary,
  getBookingPaymentPolicyEditorValue,
  serializeBookingPaymentPolicy,
} from "../utils/bookingPaymentPolicy";

import { SERVICE_ICON_OPTIONS } from "../data/serviceIconOptions";
import { useLoopPaymentActivation } from "../components/finance/LoopPaymentActivation";

function Field({ label, children }) { return <label className="svc-field"><span>{label}</span>{children}</label>; }
function ToggleCard({ active, title, text, onClick, disabled }) { return <button type="button" className={`svc-toggle ${active ? 'active' : ''}`} onClick={onClick} disabled={disabled}><strong>{title}</strong><span>{text}</span></button>; }
function EventConcurrencyChoice({ repeatable, onChange, maxConcurrent = 1, onMaxConcurrentChange = () => {} }) {
  const safeMax = Math.max(1, Number(maxConcurrent || 1));

  return (
    <div className="svc-concurrency-block">
      <div className="svc-concurrency-head">
        <strong>{t("Disponibilidad de este servicio")}</strong>
        <span>{t("Define si este mismo servicio puede reservarse varias veces al mismo tiempo.")}</span>
      </div>

      <div className="svc-concurrency-grid">
        <button
          type="button"
          className={`svc-concurrency-card ${!repeatable ? "active" : ""}`}
          onClick={() => onChange(false)}
        >
          <div className="svc-concurrency-icon">🔒</div>
          <div>
            <strong>{t("Servicio/personaje único")}</strong>
            <span>{t("Solo una reserva de este servicio puede existir en el mismo horario. Ideal para personajes únicos.")}</span>
          </div>
        </button>

        <div
          role="button"
          tabIndex={0}
          className={`svc-concurrency-card svc-concurrency-repeatable ${repeatable ? "active" : ""}`}
          onClick={() => onChange(true)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onChange(true); }}
        >
          <div className="svc-concurrency-icon">🔁</div>
          <div className="svc-concurrency-content">
            <strong>{t("Servicio repetible")}</strong>
            <span>{t("Permite varias reservas de este mismo servicio si todavía hay capacidad total.")}</span>

            {repeatable ? (
              <div className="svc-repeat-capacity" onClick={(e) => e.stopPropagation()}>
                <label>{t("Cantidad máxima simultánea")}</label>
                <div className="svc-repeat-stepper">
                  <button type="button" onClick={() => onMaxConcurrentChange(Math.max(1, safeMax - 1))}>−</button>
                  <strong>{safeMax}</strong>
                  <button type="button" onClick={() => onMaxConcurrentChange(safeMax + 1)}>+</button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function Sheet({ open, title, onClose, children, footer, dangerAction = null }) {
  if (!open) return null;

  const modal = (
    <div className="svc-modal svc-modal-top-layer" onClick={onClose}>
      <div className="svc-sheet svc-sheet-top-layer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="svc-sheet-head">
          <div>
            <h3>{title}</h3>
            <span>{t("Haz cambios rápidos sin salir de la sección.")}</span>
          </div>
          <button type="button" onClick={onClose}>✕</button>
        </div>
        <div className="svc-sheet-body">{children}</div>
        {(footer || dangerAction) ? (
          <div className="svc-sheet-foot">
            {dangerAction ? <div className="svc-sheet-danger">{dangerAction}</div> : null}
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
}

function makeCharacterId(name, idx) {
  return `character-${String(name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/gi, "-")}-${idx}`;
}

function normalizeCharacterRows(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row, idx) => {
      const name = String(row?.name || "").trim();
      if (!name) return null;
      const priceRaw = row?.price;
      const price = priceRaw === "" || priceRaw == null ? 0 : Number(priceRaw || 0);
      return {
        id: row?.id || makeCharacterId(name, idx),
        name,
        price: Number.isFinite(price) ? price : 0,
        included: false,
        type: "character",
      };
    })
    .filter(Boolean);
}

function characterRowsFromText(text) {
  return String(text || "")
    .split(",")
    .map((x, idx) => {
      const name = x.trim();
      if (!name) return null;
      return { id: makeCharacterId(name, idx), name, price: 0 };
    })
    .filter(Boolean);
}

function CharacterRowsEditor({ value, onChange, included = false, choosePrimary = false }) {
  const rows = Array.isArray(value) ? value : [];

  const updateRow = (idx, patch) => {
    const next = rows.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    onChange(next);
  };

  const addRow = () => {
    onChange([...rows, { id: `character-${Date.now()}`, name: "", price: 0 }]);
  };

  const removeRow = (idx) => {
    onChange(rows.filter((_, i) => i !== idx));
  };

  return (
    <div className="svc-character-editor">
      <div className="svc-character-editor-head">
        <strong>{choosePrimary ? "Personajes para elegir" : included ? "Personajes del paquete" : "Personajes adicionales"}</strong>
        <button type="button" onClick={addRow}>{t("Agregar personaje")}</button>
      </div>

      {rows.length === 0 ? (
        <div className="svc-character-empty">
          {choosePrimary ? "Agrega todas las opciones, incluido Mario. El cliente elegirá una como principal." : included ? "Agrega los personajes que siempre vienen incluidos en este paquete." : "Agrega personajes opcionales. El cliente podrá sumar uno, varios o ninguno."}
        </div>
      ) : null}

      {choosePrimary && <p><strong>{t("1 personaje incluido a elección")}</strong>{" " + t("· Precio si es adicional (")}<ServiceCurrency/>)</p>}
      {rows.map((row, idx) => (
        <div className="svc-character-row" key={row.id || idx}>
          <input
            aria-label={`Nombre del personaje ${idx + 1}`}
            value={row.name || ""}
            onChange={(e) => updateRow(idx, { name: e.target.value })}
            placeholder={t("Nombre del personaje")}
          />
          {!included ? <input
            aria-label={`Recargo del personaje ${idx + 1}`}
            type="number"
            min="0"
            step="0.01"
            value={row.price ?? 0}
            onChange={(e) => updateRow(idx, { price: e.target.value })}
            placeholder="$ extra"
          /> : <span>{t("Incluido")}</span>}
          <button type="button" aria-label={`Quitar personaje ${row.name || idx + 1}`} onClick={() => removeRow(idx)}>✕</button>
        </div>
      ))}

      <p className="svc-help-text">
        {included ? "Estos personajes se muestran como incluidos y no se cobran por separado." : "El recargo se suma una sola vez al precio de la experiencia e incluye al animador. Vincula cada personaje a su disfraz en Disponibilidad y equipo."}
      </p>
    </div>
  );
}

function getServicePresentationLabel(service) {
  const key = service?.serviceIconKey || service?.iconKey || "auto";
  const found = SERVICE_ICON_OPTIONS.find((opt) => String(opt.key) === String(key));
  if (found && found.label && found.key !== "auto") return found.label;
  const name = String(service?.name || service?.title || service?.label || "Servicio").trim();
  const first = name ? name[0].toUpperCase() : "S";
  return first;
}

function inferServiceIconKey(service) {
  const text = normalizeIconText([
    service?.serviceIconKey,
    service?.iconKey,
    service?.name,
    service?.title,
    service?.label,
    service?.category,
    service?.type,
  ].filter(Boolean).join(" "));

  if (!text || text === "auto") return "auto";

  const direct = SERVICE_ICON_OPTIONS.find((opt) => String(opt.key) === text);
  if (direct) return direct.key;

  const rules = [
    ["barba bigote beard mustache", "beard_trim"],
    ["afeitado shave navaja razor", "shave"],
    ["fade degradado", "fade_cut"],
    ["lineup perfilado contorno", "lineup"],
    ["hombre masculino barberia barber", "men_haircut"],
    ["mujer femenino dama", "women_haircut"],
    ["nino niño nina niña infantil kids", "kids_haircut"],
    ["blower secado dryer brushing", "blow_dry"],
    ["lavado shampoo wash", "hair_wash"],
    ["tinte color roots raiz retoque", "hair_color"],
    ["mechas highlights rayitos", "highlights"],
    ["balayage", "balayage"],
    ["keratina keratin botox capilar alisado straightening", "keratin"],
    ["extension extensiones", "hair_extensions"],
    ["corte cabello pelo haircut", "haircut"],
    ["pedicure pies foot", "pedicure"],
    ["manicure unas uñas nails", "manicure"],
    ["gel acrylic acrilica acrilicas rubber builder", "gel_nails"],
    ["nail art diseno diseño esmalte polish francesa", "nail_art"],
    ["facial limpieza acne peeling mask mascarilla", "facial"],
    ["masaje massage drenaje relajante profundo", "massage"],
    ["piedras stones", "hot_stones"],
    ["depilacion depilación wax laser", "waxing"],
    ["cejas brows microblading", "eyebrows"],
    ["pestanas pestañas lashes lifting", "lashes"],
    ["maquillaje makeup novia social", "makeup"],
    ["yoga", "yoga"],
    ["pilates", "pilates"],
    ["gym fitness entrenamiento", "personal_training"],
    ["nutricion nutrición", "nutrition"],
    ["cumpleanos cumpleaños fiesta party", "birthday_party"],
    ["personaje personajes show animacion animación", "character_show"],
    ["globos balloons", "balloons"],
    ["pintacaritas face paint", "face_paint"],
    ["magia magic", "magic_show"],
    ["foto fotografia photo camera", "photo_session"],
    ["decoracion decoración decor", "decor"],
    ["dj musica música", "dj_music"],
    ["catering comida", "catering"],
    ["limpieza cleaning", "cleaning"],
    ["domicilio home", "home_service"],
    ["consulta asesoría asesoria diagnostic diagnostico", "consultation"],
    ["paquete package", "package"],
  ];

  for (const [words, key] of rules) {
    if (words.split(" ").some((word) => word && text.includes(word))) return key;
  }

  return "auto";
}

function getServiceIconOption(keyOrService) {
  const isObj = typeof keyOrService === "object";
  const explicitKey = isObj
    ? (keyOrService?.serviceIconKey || keyOrService?.iconKey || "auto")
    : keyOrService;
  let safeKey = String(explicitKey || "auto");

  if (isObj && (!safeKey || safeKey === "auto")) {
    safeKey = inferServiceIconKey(keyOrService);
  }

  return SERVICE_ICON_OPTIONS.find((opt) => String(opt.key) === safeKey) || SERVICE_ICON_OPTIONS[0] || null;
}

const SERVICE_PRESENTATION_ICON_NAMES = {
  haircut: "Tijeras",
  men_haircut: "Tijeras",
  women_haircut: "Tijeras",
  kids_haircut: "Corte infantil",
  fade_cut: "Degradado",
  beard_trim: "Barba",
  beard_design: "Barba",
  barber_combo: "Corte + barba",
  shave: "Afeitado",
  manicure: "Manicure",
  gel_nails: "Uñas",
  nail_art: "Nail art",
  pedicure: "Pedicure",
  blow_dry: "Secado",
  hair_wash: "Lavado",
  hair_color: "Color",
  highlights: "Mechas",
  balayage: "Balayage",
  keratin: "Keratina",
  hair_extensions: "Extensiones",
  hair_treatment: "Tratamiento capilar",
  facial: "Facial",
  facial_cleaning: "Limpieza facial",
  massage: "Masaje",
  hot_stones: "Piedras calientes",
  waxing: "Depilación",
  eyebrows: "Cejas",
  lashes: "Pestañas",
  makeup: "Maquillaje",
  spa: "Spa",
  yoga: "Yoga",
  pilates: "Pilates",
  fitness: "Fitness",
  personal_training: "Entrenamiento",
  nutrition: "Nutrición",
  birthday_party: "Cumpleaños",
  character_show: "Personaje",
  magic_show: "Magia",
  face_paint: "Pintacaritas",
  balloons: "Globos",
  photo_session: "Fotografía",
  decor: "Decoración",
  dj_music: "Música",
  catering: "Catering",
  home_service: "Domicilio",
  cleaning: "Limpieza",
  pet_grooming: "Mascotas",
  car_wash: "Auto",
  consultation: "Consulta",
  package: "Paquete",
};

function getServicePresentationSummary(service, option = null) {
  const opt = option || getServiceIconOption(service);
  const key = String(opt?.key || inferServiceIconKey(service) || "auto");
  const label = SERVICE_PRESENTATION_ICON_NAMES[key] || opt?.label || "Automático";
  return `Ícono · ${label}`;
}

const SERVICE_ICON_PACKS = {
  lucide: Lucide,
  fa: FaIcons,
  gi: GiIcons,
  md: MdIcons,
};

function resolvePremiumServiceIcon(option = {}) {
  const candidates = Array.isArray(option.iconCandidates) && option.iconCandidates.length
    ? option.iconCandidates
    : [{ pack: option.iconPack || "lucide", name: option.iconName }];

  for (const candidate of candidates) {
    if (!candidate || !candidate.name) continue;
    const pack = SERVICE_ICON_PACKS[candidate.pack || "lucide"] || Lucide;
    const Icon = pack?.[candidate.name];
    if (Icon) return Icon;
  }

  if (option.iconName && Lucide?.[option.iconName]) return Lucide[option.iconName];
  return Lucide?.Sparkles || Lucide?.Circle || null;
}

function ServiceIconVisual({ service, option = null, className = "", compact = false }) {
  const opt = option || getServiceIconOption(service);
  const label = opt?.label || getServicePresentationLabel(service);
  const fallback = opt?.glyph || getServiceIconGlyph(service);
  const SafeIcon = resolvePremiumServiceIcon(opt);

  return (
    <span className={`svc-service-icon-visual premium ${compact ? "compact" : ""} ${className || ""}`} title={label} aria-label={label}>
      {SafeIcon ? (
        <SafeIcon className="svc-premium-icon-svg" aria-hidden="true" strokeWidth={2.1} />
      ) : (
        <i>{fallback}</i>
      )}
    </span>
  );
}

function normalizeIconText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}


function normalizePriceType(service) {
  const raw = String(service?.priceType || service?.pricingType || "").toLowerCase().trim();
  if (["quote", "quotation", "cotizacion", "cotización"].includes(raw)) return "quote";
  if (["from", "starting", "desde"].includes(raw)) return "from";
  return "fixed";
}

function formatMoneyAmount(value, region = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return regionMoney(region).format(n);
}

function formatServicePrice(service, region = {}) {
  const priceType = normalizePriceType(service);
  if (priceType === "quote") return service?.priceLabel || service?.publicPriceLabel || "Solicitar cotización";
  const raw = service?.price;
  if (raw === null || raw === undefined || raw === "" || Number(raw) <= 0) return "Precio por definir";
  const amount = formatMoneyAmount(raw, region);
  if (!amount) return "Precio por definir";
  if (priceType === "from") return `Desde ${amount}`;
  return amount;
}

const SERVICE_DESCRIPTION_MAX_LENGTH = 160;

function normalizeServiceDescription(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, SERVICE_DESCRIPTION_MAX_LENGTH);
}

function isServiceMissingPrice(service) {
  if (Array.isArray(service?.visitTypeOptions) && service.visitTypeOptions.length) {
    const enabled = service.visitTypeOptions.filter(option => option.enabled !== false);
    return !enabled.length || enabled.some(option => {
      if (['quote','cotizacion','cotización'].includes(option.priceType || option.pricingType)) return false;
      const prices = option.durationPrices?.length ? option.durationPrices.map(row => row.price) : [option.price];
      if (!option.durationPrices?.length && Number(option.price) === 0 && option.price != null && option.price !== '' && option.freePriceConfirmed === true) return false;
      return prices.some(price => price == null || price === '' || !Number.isFinite(Number(price)) || Number(price) <= 0);
    });
  }
  if (normalizePriceType(service) === "quote") return false;
  return service?.price === null || service?.price === undefined || service?.price === "" || Number(service?.price) <= 0;
}

function isServiceRepeatable(service) {
  return Boolean(service?.allowConcurrentSameService || service?.sameServiceCanOverlap || service?.repeatable || service?.concurrentSameService);
}

function getServiceDurationLabel(service, isEventsBusiness = false) {
  const mins = Number(service?.duration || service?.durationMinutes || service?.minutes || 0);
  if (!Number.isFinite(mins) || mins <= 0) return "Sin duración";
  if (isEventsBusiness && mins >= 60) {
    const hours = mins / 60;
    return Number.isInteger(hours) ? `${hours}h base` : `${hours.toFixed(1)}h base`;
  }
  return `${mins} min`;
}


function normalizeServiceIconToken(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getServiceIconToken(service) {
  const rawKey = normalizeServiceIconToken(service?.serviceIconKey || service?.iconKey || service?.icon || service?.serviceIcon || "");
  const rawLabel = normalizeServiceIconToken(getServicePresentationLabel(service));
  const rawName = normalizeServiceIconToken(service?.name || service?.title || service?.label || "");
  const source = `${rawKey} ${rawLabel} ${rawName}`;

  if (rawKey && rawKey !== "auto") return rawKey;
  if (source.includes("barber") || source.includes("corte") || source.includes("hair") || source.includes("cabello")) return "scissors";
  if (source.includes("blower") || source.includes("blow") || source.includes("secado") || source.includes("dryer")) return "blower";
  if (source.includes("pedicure") || source.includes("pies") || source.includes("foot")) return "pedicure";
  if (source.includes("manicure") || source.includes("unas") || source.includes("una") || source.includes("nail")) return "manicure";
  if (source.includes("shampoo") || source.includes("lavado") || source.includes("wash")) return "shampoo";
  if (source.includes("keratina") || source.includes("keratin") || source.includes("tratamiento")) return "keratin";
  if (source.includes("makeup") || source.includes("maquillaje") || source.includes("palette")) return "makeup";
  if (source.includes("spa") || source.includes("masaje") || source.includes("massage") || source.includes("facial")) return "spa";
  if (source.includes("fitness") || source.includes("gym") || source.includes("entren")) return "fitness";
  if (source.includes("foto") || source.includes("photo") || source.includes("camara") || source.includes("camera")) return "photo";
  if (source.includes("show") || source.includes("evento") || source.includes("party") || source.includes("anim")) return "event";
  if (source.includes("paquete") || source.includes("package")) return "package";
  return "default";
}

function getServiceIconGlyph(service) {
  const token = getServiceIconToken(service);
  const glyphs = {
    scissors: "✂",
    blower: "≋",
    manicure: "✦",
    pedicure: "◔",
    shampoo: "◍",
    keratin: "✧",
    makeup: "◐",
    spa: "◎",
    fitness: "⚡",
    photo: "▣",
    event: "✺",
    package: "□",
    default: "◇",
  };
  return glyphs[token] || glyphs.default;
}

function getServicePresentationMode(service) {
  const raw = String(service?.servicePresentationMode || service?.presentationMode || "").toLowerCase();
  if (raw === "image" || raw === "photo") return "image";
  if (raw === "icon" || raw === "simple") return "icon";
  return service?.imageUrl ? "image" : "icon";
}

function EventSummaryLine({ service, isEventsBusiness }) {
  if (!isEventsBusiness) return null;
  const chars = Array.isArray(service?.characters) ? service.characters.length : 0;
  const extras = Array.isArray(service?.extras || service?.optionalExtras) ? (service.extras || service.optionalExtras).length : 0;
  const bits = [];
  if (chars && service.charactersMode !== "none") bits.push(`${chars} personajes ${service.charactersMode === "included" ? "incluidos" : "opcionales"}`);
  if (extras) bits.push(`${extras} extra${extras === 1 ? "" : "s"}`);
  if (!bits.length) bits.push("Configurable para eventos");
  return <span className="svc-preview-event-line">{bits.join(" · ")}</span>;
}

function ServicePricingEditor({ value, onChange, isEventsBusiness = false }) {
  const region=useBusinessRegion();

  const current = value || {};
  const priceType = normalizePriceType(current);
  const priceValue = current.price ?? "";
  const patch = (nextPatch) => onChange({ ...(current || {}), ...nextPatch });

  if (!isEventsBusiness) {
    return (
      <div className="svc-price-duration-card svc-price-card">
        <span>{t("Tipo de precio")}</span>
        <div className="svc-price-mode-mini svc-standard-price-mode">
          <button type="button" className={priceType === "fixed" ? "active" : ""} onClick={() => patch({ priceType: "fixed", pricingType: "fixed" })}>{t("Precio fijo")}</button>
          <button type="button" className={priceType === "from" ? "active" : ""} onClick={() => patch({ priceType: "from", pricingType: "from" })}>{t("Desde")}</button>
        </div>
        <span>{t("Precio base")}</span>
        <div className="svc-money-input"><i><ServiceCurrency/></i><input type="number" value={priceValue} onChange={(e) => patch({ price: e.target.value })} placeholder="30" /></div>
        <small className="svc-price-public-preview">{t("Se mostrará:")}<strong>{formatServicePrice(current, region)}</strong></small>
      </div>
    );
  }

  return (
    <div className="svc-price-block svc-price-block-minimal">
      <div className="svc-price-type-row compact">
        {[
          { id: "fixed", label: "Precio", helper: "Muestra un monto." },
          { id: "quote", label: "Cotización", helper: "Sin monto público." },
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            className={priceType === item.id ? "active" : ""}
            onClick={() => patch({ priceType: item.id, pricingType: item.id, priceLabel: item.id === "quote" ? "Solicitar cotización" : "", publicPriceLabel: item.id === "quote" ? "Solicitar cotización" : "" })}
          >
            <strong>{item.label}</strong>
            <span>{item.helper}</span>
          </button>
        ))}
      </div>
      {priceType === "quote" ? (
        <div className="svc-quote-preview minimal"><strong>{t("Se mostrará:")}</strong><span>{current.priceLabel || t("Solicitar cotización")}</span></div>
      ) : (
        <div className="svc-money-input event"><i><ServiceCurrency/></i><input type="number" value={priceValue} onChange={(e) => patch({ price: e.target.value, priceType: "fixed", pricingType: "fixed" })} placeholder="120" /></div>
      )}
    </div>
  );
}


function BookingPaymentPolicyEditor({ value, onChange, requestActivation }) {
  const region=useBusinessRegion();

  if ((region.countryCode || 'PA') !== 'PA' || region.onlinePaymentsEnabled === false) return <p className="svc-payment-offline">{t("Por ahora, coordina el pago directamente con tu cliente. Los cobros online no están habilitados en este país.")}</p>;
  const current = value || {};
  const eligibility = bookingPaymentPolicyEligibility(current);
  const editorPolicy = getBookingPaymentPolicyEditorValue(current);
  const displayType = eligibility.eligible ? editorPolicy.type : "none";
  const totalCents = eligibility.priceCents || 0;
  const previewService = {
    ...current,
    bookingPaymentPolicy: {
      ...editorPolicy,
      type: displayType,
      enabled: displayType !== "none",
    },
  };
  const dueNowCents = bookingPaymentDueNowCents(previewService);
  const balanceCents = bookingPaymentBalanceCents(previewService);
  const money = (cents) => `$${(Math.max(0, Number(cents || 0)) / 100).toFixed(2)}`;

  const patchPolicy = (patch) => {
    const next = {
      ...editorPolicy,
      ...patch,
      version: BOOKING_PAYMENT_POLICY_VERSION,
    };
    next.enabled = next.type !== "none";
    onChange({
      ...current,
      bookingPaymentPolicy: next,
    });
  };

  const selectType = async (type, verified = false) => {
    if (type !== "none" && !eligibility.eligible) return;
    if (type !== "none" && !verified && requestActivation) { await requestActivation(() => selectType(type, true)); return; }

    if (type === "percentage") {
      patchPolicy({
        type,
        percentage: Number(editorPolicy.percentage || 50),
        amount: null,
      });
      return;
    }

    if (type === "fixed") {
      const suggested = totalCents > 0 ? Math.min(totalCents / 200, totalCents / 100) : 0;
      patchPolicy({
        type,
        percentage: null,
        amount: Number(editorPolicy.amount || suggested || 0),
      });
      return;
    }

    patchPolicy({
      type,
      percentage: null,
      amount: null,
    });
  };

  const summary =
    displayType === "none"
      ? "No se solicita abono. La confirmación depende de las reglas de la reserva."
      : `${money(dueNowCents)} al reservar · ${money(balanceCents)} quedará pendiente.`;

  return (
    <section className="svc-editor-section compact svc-booking-payment-policy">
      <div className="svc-booking-payment-head">
        <div>
          <strong>{t("Pago para reservar")}</strong>
          <span>{t("Define si el cliente debe pagar antes de confirmar la cita.")}</span>
        </div>
        <span className={`svc-booking-payment-status ${displayType !== "none" ? "active" : ""}`}>
          {displayType === "none" ? "Sin cobro previo" : "Cobro requerido"}
        </span>
      </div>

      {!eligibility.eligible ? (
        <div className="svc-booking-payment-note">
          <strong>{t("No disponible con este precio.")}</strong>
          <span>
            {eligibility.reason === "fixed-price-required"
              ? "Para exigir un pago al reservar, usa Precio fijo en este servicio."
              : "Define un precio mayor a $0 para activar el cobro previo."}
          </span>
        </div>
      ) : null}

      <div className="svc-booking-payment-options">
        {[
          { id: "none", title: "Sin abono", text: "No solicita un pago anticipado." },
          { id: "percentage", title: "Porcentaje", text: "Ej. 50% del servicio." },
          { id: "fixed", title: "Monto fijo", text: "Ej. $5 para reservar." },
          { id: "full", title: "Pago completo", text: "Cobra el 100% antes de confirmar." },
        ].map((item) => {
          const disabled = item.id !== "none" && !eligibility.eligible;
          return (
            <button
              key={item.id}
              type="button"
              className={`svc-booking-payment-option ${displayType === item.id ? "active" : ""}`}
              onClick={() => selectType(item.id)}
              disabled={disabled}
            >
              <strong>{item.title}</strong>
              <span>{item.text}</span>
            </button>
          );
        })}
      </div>

      {eligibility.eligible && displayType === "percentage" ? (
        <div className="svc-booking-payment-control">
          <label>
            <span>{t("Porcentaje a cobrar")}</span>
            <div className="svc-booking-payment-input suffix">
              <input
                type="number"
                min="1"
                max="100"
                step="1"
                value={editorPolicy.percentage ?? 50}
                onChange={(e) => {
                  const next = Math.min(100, Math.max(1, Number(e.target.value || 1)));
                  patchPolicy({ type: "percentage", percentage: next, amount: null });
                }}
              />
              <em>%</em>
            </div>
          </label>
        </div>
      ) : null}

      {eligibility.eligible && displayType === "fixed" ? (
        <div className="svc-booking-payment-control">
          <label>
            <span>{t("Monto a cobrar")}</span>
            <div className="svc-booking-payment-input prefix">
              <em>$</em>
              <input
                type="number"
                min="0.01"
                max={(totalCents / 100).toFixed(2)}
                step="0.01"
                value={editorPolicy.amount ?? 0}
                onChange={(e) => {
                  const price = totalCents / 100;
                  const next = Math.min(price, Math.max(0, Number(e.target.value || 0)));
                  patchPolicy({ type: "fixed", amount: next, percentage: null });
                }}
              />
            </div>
          </label>
          <small>{t("Máximo") + " "}{money(totalCents)}.</small>
        </div>
      ) : null}

      <div className={`svc-booking-payment-preview ${displayType !== "none" ? "active" : ""}`}>
        <strong>{displayType === "none" ? "Sin abono" : "Vista del cobro"}</strong>
        <span>{summary}</span>
      </div>
    </section>
  );
}

function ServicePresentationEditor({
  value,
  onChange,
  onUploadImage = null,
  canUploadImage = false,
  isEventsBusiness = false,
  nameLabel = "Nombre del servicio",
  namePlaceholder = "",
}) {
  const region=useBusinessRegion();

  const current = value || {};
  const mode = getServicePresentationMode(current);
  const imageSelected = mode === "image";
  const hasImage = Boolean(current.imageUrl);
  const selectedIconKey = current.serviceIconKey || current.iconKey || "auto";
  const selectedIconOption = getServiceIconOption(current);
  const [iconSearch, setIconSearch] = useState("");
  const [iconCategory, setIconCategory] = useState("popular");
  const [presentationOpen, setPresentationOpen] = useState(false);
  const durationLabel = getServiceDurationLabel(current, isEventsBusiness);
  const priceLabel = isEventsBusiness ? formatEventPackagePrice(current, region) : formatServicePrice(current, region);
  const patch = (nextPatch) => onChange({ ...(current || {}), ...nextPatch });

  const iconCategories = useMemo(() => {
    const seen = new Map();
    SERVICE_ICON_OPTIONS.forEach((opt) => {
      const id = opt.category || "general";
      if (!seen.has(id)) seen.set(id, opt.categoryLabel || id);
    });
    return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
  }, []);

  const filteredIconOptions = useMemo(() => {
    const q = normalizeIconText(iconSearch);
    return SERVICE_ICON_OPTIONS.filter((opt) => {
      if (iconCategory !== "all" && (opt.category || "general") !== iconCategory) return false;
      if (!q) return true;
      const haystack = normalizeIconText([opt.label, opt.key, opt.categoryLabel, ...(opt.tags || [])].join(" "));
      return haystack.includes(q);
    });
  }, [iconSearch, iconCategory]);

  const chooseIcon = (key) => {
    patch({ serviceIconKey: key, iconKey: key, servicePresentationMode: "icon", presentationMode: "icon" });
  };

  const handleImageUpload = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    patch({ servicePresentationMode: "image", presentationMode: "image" });
    if (canUploadImage && onUploadImage) onUploadImage();
  };

  return (
    <section className={`svc-editor-section compact svc-option1-editor ${isEventsBusiness ? "svc-event-service-editor" : "svc-standard-service-editor"}`}>
      <div className="svc-option1-main">
        <div className="svc-option1-preview">
          <span>{t("Así lo verán tus clientes")}</span>
          <div className={`svc-option1-preview-media ${imageSelected && hasImage ? "has-image" : `has-icon icon-${getServiceIconToken(current)}`}`}>
            {imageSelected && hasImage ? <img src={current.imageUrl} alt={current.name || "Imagen del servicio"} /> : <ServiceIconVisual service={current} option={selectedIconOption} />}
          </div>
          <strong>{current.name || (isEventsBusiness ? "Nombre del paquete" : "Nombre del servicio")}</strong>
          {String(current.description || "").trim() ? (
            <small className="svc-option1-preview-description">{String(current.description || "").trim()}</small>
          ) : null}
          <p>{durationLabel} · {priceLabel}</p>
          <EventSummaryLine service={current} isEventsBusiness={isEventsBusiness} />
        </div>

        <div className="svc-option1-fields">
          <Field label={nameLabel}>
            <input
              value={current.name || ""}
              onChange={(e) => patch({ name: e.target.value })}
              placeholder={namePlaceholder}
            />
          </Field>

          <label className="svc-service-description-field">
            <span>
              <strong>{t("Descripción")}</strong>
              <em>{t("Opcional")}</em>
            </span>
            <textarea
              rows={2}
              maxLength={SERVICE_DESCRIPTION_MAX_LENGTH}
              value={String(current.description || "").slice(0, SERVICE_DESCRIPTION_MAX_LENGTH)}
              onChange={(e) => patch({ description: e.target.value.slice(0, SERVICE_DESCRIPTION_MAX_LENGTH) })}
              placeholder={isEventsBusiness ? "Ej: Show de 60 minutos con animación y fotos." : "Ej: Incluye lavado, corte personalizado y acabado."}
            />
            <small>
              <span>{t("Cuenta brevemente qué incluye el servicio.")}</span>
              <b>{String(current.description || "").length}/{SERVICE_DESCRIPTION_MAX_LENGTH}</b>
            </small>
          </label>

          <div className="svc-option1-field-grid">
            <div className="svc-option1-unit-field">
              <span>{isEventsBusiness ? "Duración base" : t("Duración")}</span>
              <div className="svc-option1-input-unit">
                <input
                  type="number"
                  value={Number(current.duration || 30)}
                  onChange={(e) => patch({ duration: Number(e.target.value || 0) })}
                />
                <em>min</em>
              </div>
            </div>

            {!isEventsBusiness ? (
              <div className="svc-option1-unit-field svc-option1-price-field">
                <span>{t("Precio")}</span>
                <div className="svc-price-compact-row">
                  <div className="svc-price-mode-mini svc-standard-price-mode" aria-label={t("Tipo de precio")}>
                    <button
                      type="button"
                      className={normalizePriceType(current) === "fixed" ? "active" : ""}
                      onClick={() => patch({ priceType: "fixed", pricingType: "fixed" })}
                    >{t("Fijo")}</button>
                    <button
                      type="button"
                      className={normalizePriceType(current) === "from" ? "active" : ""}
                      onClick={() => patch({ priceType: "from", pricingType: "from" })}
                    >{t("Desde")}</button>
                  </div>
                  <div className="svc-option1-input-unit money">
                    <em>$</em>
                    <input
                      type="number"
                      min="0"
                      value={current.price ?? ""}
                      onChange={(e) => patch({ price: e.target.value })}
                      placeholder="30"
                    />
                  </div>
                </div>
                <small className="svc-price-public-preview">{t("Se mostrará como")}<strong>{formatServicePrice(current, region)}</strong>
                </small>
              </div>
            ) : null}
          </div>

          {!isEventsBusiness && <SessionServiceFields value={current} onChange={patch}/>}
          {isEventsBusiness ? (
            <EventExperiencePricingEditor
              value={current.visitTypeOptions}
              basePrice={current.price}
              onChange={(options) => {
                const primary = options.find((x) => x.enabled) || options[0];
                patch({
                  visitTypeOptions: options,
                  experienceType: primary?.id || current.experienceType || "show",
                  eventExperienceType: primary?.id || current.experienceType || "show",
                  priceType: primary?.priceType === "quote" ? "quote" : "fixed",
                  pricingType: primary?.priceType === "quote" ? "quote" : "fixed",
                  price: primary?.priceType === "quote" ? "" : (primary?.price ?? current.price ?? ""),
                  priceLabel: primary?.priceType === "quote" ? "Solicitar cotización" : "",
                  publicPriceLabel: primary?.priceType === "quote" ? "Solicitar cotización" : "",
                });
              }}
            />
          ) : null}
        </div>
      </div>

      <div className="svc-option1-presentation svc-presentation-minimal-trigger">
        {isEventsBusiness && current.imageUrl && <img className="event-image-thumbnail" src={current.imageUrl} alt="Imagen del paquete" />}
        <div className="svc-presentation-minimal-copy">
          <strong>{t("Presentación")}</strong>
          <span>{imageSelected ? "Imagen personalizada" : getServicePresentationSummary(current, selectedIconOption)}</span>
        </div>

        <button type="button" className="svc-presentation-open-btn" onClick={() => setPresentationOpen(true)}>{t("Cambiar")}</button>

        <Sheet
          open={presentationOpen}
          title={t("Presentación del servicio")}
          onClose={() => setPresentationOpen(false)}
          footer={
            <div className="svc-actions svc-presentation-sheet-actions">
              <button type="button" onClick={() => setPresentationOpen(false)}>{t("Cancelar")}</button>
              <button type="button" className="primary" onClick={() => setPresentationOpen(false)}>{t("Listo")}</button>
            </div>
          }
        >
          <div className="svc-presentation-sheet">
            <div className="svc-presentation-sheet-hero">
              <div className={`svc-presentation-sheet-preview ${imageSelected && hasImage ? "has-image" : "has-icon"}`}>
                {imageSelected && hasImage ? <img src={current.imageUrl} alt={current.name || "Imagen del servicio"} /> : <ServiceIconVisual service={current} option={selectedIconOption} />}
              </div>

              <div>
                <span>{t("Así se verá")}</span>
                <strong>{current.name || (isEventsBusiness ? "Nombre del paquete" : "Nombre del servicio")}</strong>
                <p>{imageSelected ? "La imagen se usará como visual principal del servicio." : `Ícono seleccionado: ${selectedIconOption?.label || "Automático"}.`}</p>
              </div>
            </div>

            <div className="svc-presentation-mode-cards" role="tablist" aria-label={t("Tipo de presentación")}>
              <button
                type="button"
                className={!imageSelected ? "active" : ""}
                onClick={() => patch({ servicePresentationMode: "icon", presentationMode: "icon" })}
              >
                <ServiceIconVisual option={selectedIconOption} compact />
                <div>
                  <strong>{t("Ícono profesional")}</strong>
                  <span>{t("Ideal para servicios rápidos, catálogo limpio y vista consistente.")}</span>
                </div>
              </button>

              <button
                type="button"
                className={imageSelected ? "active" : ""}
                onClick={() => patch({ servicePresentationMode: "image", presentationMode: "image" })}
              >
                <div className="svc-presentation-image-glyph">IMG</div>
                <div>
                  <strong>{t("Imagen propia")}</strong>
                  <span>{t("Útil si quieres mostrar un resultado, paquete o experiencia visual.")}</span>
                </div>
              </button>
            </div>

            {!imageSelected ? (
              <div className="svc-icon-picker svc-icon-picker-lucide svc-icon-picker-modal" aria-label={t("Elegir ícono del servicio")}>
                <div className="svc-icon-picker-top">
                  <label>
                    <span>{t("Buscar ícono")}</span>
                    <input
                      value={iconSearch}
                      onChange={(e) => setIconSearch(e.target.value)}
                      placeholder={t("Corte, barba, manicure, evento...")}
                    />
                  </label>
                </div>

                <div className="svc-icon-category-tabs" role="tablist" aria-label={t("Categorías de íconos")}>
                  <button type="button" className={iconCategory === "popular" ? "active" : ""} onClick={() => setIconCategory("popular")}>{t("Populares")}</button>
                  <button type="button" className={iconCategory === "all" ? "active" : ""} onClick={() => setIconCategory("all")}>{t("Todos")}</button>
                  {iconCategories.filter((cat) => cat.id !== "popular").map((cat) => (
                    <button key={cat.id} type="button" className={iconCategory === cat.id ? "active" : ""} onClick={() => setIconCategory(cat.id)}>
                      {cat.label}
                    </button>
                  ))}
                </div>

                <div className="svc-icon-grid svc-icon-grid-modal" role="list">
                  {filteredIconOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      className={`svc-icon-choice ${String(selectedIconKey) === String(opt.key) ? "active" : ""}`}
                      onClick={() => chooseIcon(opt.key)}
                      title={opt.label}
                    >
                      <ServiceIconVisual option={opt} compact />
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="svc-image-mode-panel">
                <div>
                  <strong>{hasImage ? "Imagen cargada" : "Sin imagen todavía"}</strong>
                  <span>{hasImage ? "Puedes cambiarla por una foto nueva." : "Sube una imagen para este servicio después de guardar o desde esta opción."}</span>
                </div>
                <button
                  type="button"
                  className={`svc-option1-upload ${canUploadImage ? "" : "disabled"}`}
                  onClick={handleImageUpload}
                >
                  {hasImage ? t("Cambiar imagen") : canUploadImage ? "Subir imagen" : "Guarda para subir imagen"}
                </button>
              </div>
            )}
          </div>
        </Sheet>
      </div>
    </section>
  );
}

function ServiceCard({ service, useImages, onClick, onToggleDisabled, isEventsBusiness = false }) {
  const region=useBusinessRegion();

  const extra = Number(service.bufferBeforeMinutes || 0) + Number(service.bufferAfterMinutes || 0);
  const isVisible = !service.disabled;
  const hasImage = Boolean(service.imageUrl);
  const repeatable = isServiceRepeatable(service);
  const priceLabel = isEventsBusiness ? formatEventPackagePrice(service, region) : formatServicePrice(service, region);
  const durationLabel = getServiceDurationLabel(service, isEventsBusiness);
  const presentationLabel = getServiceIconGlyph(service);
  const charactersCount = Array.isArray(service.characters) ? service.characters.length : 0;
  const visitCount = Array.isArray(service.visitTypeOptions) ? service.visitTypeOptions.filter((x) => x?.enabled !== false).length : 0;

  return (
    <article className={`svc-card-item svc-catalog-card ${!isVisible ? "svc-card-hidden" : ""}`} onClick={onClick}>
      <div className={`svc-catalog-media ${useImages ? "visual" : "simple"}`}>
        {(getServicePresentationMode(service) === "image" || useImages) && hasImage ? (
          <img src={service.imageUrl} alt={service.name || "Servicio"} />
        ) : (
          <ServiceIconVisual service={service} compact />
        )}
      </div>

      <div className="svc-info svc-catalog-info">
        <div className="svc-badges svc-catalog-badges">
          {service.featured ? <span>{t("Destacado")}</span> : null}
          {!isVisible ? <span className="off">{t("Oculto")}</span> : null}
          {repeatable ? <span>{t("Repetible")}</span> : <span>{t("Único")}</span>}
        </div>

        <h4>{service.name || (isEventsBusiness ? "Paquete" : "Servicio")}</h4>
        <p>{durationLabel} · {priceLabel}{extra ? ` · +${extra} min buffer` : ""}</p>

        <div className="svc-catalog-meta-row">
          {isEventsBusiness && charactersCount ? <span>{charactersCount}{" " + t("personajes") + " "}</span> : null}
          {isEventsBusiness && visitCount ? <span>{visitCount}{" " + t("experiencias")}</span> : null}
          {!isEventsBusiness ? <span>{t("Reservable online")}</span> : null}
        </div>
      </div>

      <button
        type="button"
        className={`svc-card-visibility ${isVisible ? "on" : "off"}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggleDisabled(service);
        }}
        aria-pressed={isVisible}
        title={isVisible ? "Ocultar" : "Activar"}
      >
        <i />
        <span>{isVisible ? (isEventsBusiness ? "Visible" : t("Activo")) : t("Oculto")}</span>
      </button>
    </article>
  );
}

function defaultVisitTypeOptions(basePrice = 0) {
  const price = Number(basePrice || 0);
  return [
    { id: "show", label: "Show completo", enabled: true, price },
    { id: "visit", label: "Solo visita", enabled: true, price: price ? Math.round(price * 0.65) : 0 },
    { id: "photos", label: "Fotos y saludo", enabled: false, price: price ? Math.round(price * 0.5) : 0 },
  ];
}

function normalizeVisitTypeOptions(options, basePrice = 0) {
  const defaults = defaultVisitTypeOptions(basePrice);
  const list = Array.isArray(options) && options.length ? options : defaults;

  return defaults.map((def) => {
    const found = list.find((x) => String(x.id) === String(def.id)) || {};
    const rawPrice = found.price ?? def.price ?? 0;
    const price = rawPrice === "" || rawPrice == null ? 0 : Number(rawPrice || 0);

    return {
      id: def.id,
      label: found.label || def.label,
      enabled: typeof found.enabled === "boolean" ? found.enabled : !!def.enabled,
      price: Number.isFinite(price) ? price : 0,
    };
  });
}


function normalizeDurationPricingTiers(tiers, minHours = 1, maxHours = 3, basePrice = 0) {
  const min = Math.max(0.5, Number(minHours || 1));
  const max = Math.max(min, Number(maxHours || min));
  const source = Array.isArray(tiers) && tiers.length ? tiers : [];
  const cleaned = source
    .map((tier) => {
      const hoursRaw = tier?.hours ?? tier?.durationHours ?? tier?.value;
      const priceRaw = tier?.price ?? tier?.amount ?? tier?.totalPrice;
      const hours = Number(hoursRaw || 0);
      const price = priceRaw === "" || priceRaw == null ? 0 : Number(priceRaw || 0);
      if (!Number.isFinite(hours) || hours <= 0) return null;
      return {
        id: tier?.id || `duration-${String(hours).replace(".", "_")}`,
        hours,
        durationMinutes: Math.round(hours * 60),
        price: Number.isFinite(price) ? price : 0,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.hours - b.hours);

  if (cleaned.length) return cleaned;

  const out = [];
  const base = Number(basePrice || 0);
  for (let h = min; h <= max + 0.001; h += 0.5) {
    out.push({
      id: `duration-${String(h).replace(".", "_")}`,
      hours: Number(h.toFixed(1)),
      durationMinutes: Math.round(h * 60),
      price: base ? Math.round(base * h) : 0,
    });
  }
  return out;
}

function formatHoursLabel(hours) {
  const n = Number(hours || 0);
  if (n === 1) return "1 hora";
  if (Number.isInteger(n)) return `${n} horas`;
  return `${String(n).replace(".", ",")} horas`;
}

function DurationPricingEditor({ pricingMode, tiers, minHours, maxHours, basePrice, onModeChange, onTiersChange }) {
  const mode = pricingMode === "duration_tiers" ? "duration_tiers" : "proportional";
  const rows = normalizeDurationPricingTiers(tiers, minHours, maxHours, basePrice);

  const updateRow = (idx, patch) => {
    const next = rows.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    onTiersChange(normalizeDurationPricingTiers(next, minHours, maxHours, basePrice));
  };

  const addRow = () => {
    const last = rows.length ? Number(rows[rows.length - 1].hours || 0) : Number(minHours || 1);
    const nextHours = Math.max(0.5, Number((last + 0.5).toFixed(1)));
    onTiersChange([...rows, { id: `duration-${Date.now()}`, hours: nextHours, durationMinutes: Math.round(nextHours * 60), price: 0 }]);
  };

  const removeRow = (idx) => {
    onTiersChange(rows.filter((_, i) => i !== idx));
  };

  return (
    <div className="svc-duration-pricing-editor">
      <div className="svc-character-editor-head">
        <div>
          <strong>{t("Configuración de precios")}</strong>
          <span>{t("Define cómo se calcula el precio según la duración.")}</span>
        </div>
      </div>

      <div className="svc-pricing-mode-grid">
        <button
          type="button"
          className={`svc-pricing-mode-card ${mode === "proportional" ? "active" : ""}`}
          onClick={() => onModeChange("proportional")}
        >
          <strong>{t("Precio proporcional")}</strong>
          <span>{t("Usa el precio base como referencia y lo ajusta según la duración.")}</span>
        </button>
        <button
          type="button"
          className={`svc-pricing-mode-card ${mode === "duration_tiers" ? "active" : ""}`}
          onClick={() => onModeChange("duration_tiers")}
        >
          <strong>{t("Escala personalizada")}</strong>
          <span>{t("Define precios específicos por duración. Cada duración tiene su precio final.")}</span>
        </button>
      </div>

      {mode === "duration_tiers" ? (
        <div className="svc-tier-table">
          <div className="svc-tier-table-head">
            <span>{t("Duración")}</span>
            <span>{t("Precio")}</span>
            <span />
          </div>

          {rows.map((row, idx) => (
            <div className="svc-tier-row" key={row.id || idx}>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={row.hours ?? 1}
                onChange={(e) => updateRow(idx, { hours: Number(e.target.value || 1), durationMinutes: Math.round(Number(e.target.value || 1) * 60) })}
              />
              <input
                type="number"
                value={row.price ?? 0}
                onChange={(e) => updateRow(idx, { price: e.target.value })}
                placeholder="$"
              />
              <button type="button" onClick={() => removeRow(idx)}>✕</button>
            </div>
          ))}

          <button type="button" className="svc-tier-add" onClick={addRow}>{t("Agregar duración")}</button>
          <p className="svc-help-text">{t("Ejemplo: 1 hora $120, 2 horas $200, 3 horas $270. Se muestra como una escala de precios, no como descuento.")}</p>
        </div>
      ) : (
        <div className="svc-pricing-preview">
          <strong>{t("Vista previa")}</strong>
          <span>{t("Precio base: $")}{Number(basePrice || 0)}</span>
          <small>{t("Se calcula: precio base × minutos elegidos ÷ duración general en minutos.")}</small>
        </div>
      )}
    </div>
  );
}


function normalizeExperiencePricingOptions(options, basePrice = null) {
  const defaults = defaultVisitTypeOptions(basePrice);
  const list = Array.isArray(options) ? options : [];
  return defaults.map((def) => {
    const found = list.find((x) => String(x.id) === String(def.id)) || {};
    const rawType = String(found.priceType || found.pricingType || "").toLowerCase();
    const priceType = rawType === "quote" || rawType === "cotizacion" || rawType === "cotización" ? "quote" : "fixed";
    const rawPrice = Object.prototype.hasOwnProperty.call(found, "price") ? found.price : (def.id === "show" ? basePrice : null);
    const price = rawPrice === "" || rawPrice == null ? null : Number(rawPrice);
    return {
      id: def.id,
      label: found.label || def.label,
      enabled: typeof found.enabled === "boolean" ? found.enabled : (Array.isArray(options) && options.length ? !!list.find(x => x.id === def.id) : def.id === "show"),
      priceType,
      pricingType: priceType,
      price: Number.isFinite(price) ? price : null,
      freePriceConfirmed: found.freePriceConfirmed === true && Number(price) === 0 && price !== null,
      priceLabel: priceType === "quote" ? (found.priceLabel || "Solicitar cotización") : "",
      durationPrices: Array.isArray(found.durationPrices) ? found.durationPrices.map(row => ({hours:row.hours,price:row.price})) : [],
    };
  });
}

function formatExperienceOptionPrice(option, region = {}) {
  const type = String(option?.priceType || option?.pricingType || "fixed").toLowerCase();
  if (type === "quote" || type === "cotizacion" || type === "cotización") return option?.priceLabel || "Cotización";
  if (option?.durationPrices?.length) return option.durationPrices.map(row => row.hours + ' h: ' + (row.price == null || row.price === '' ? 'Precio pendiente' : formatMoneyAmount(row.price, region))).join(' · ');
  if (option?.price == null || option.price === '') return 'Precio pendiente';
  if (Number(option.price) === 0) return option.freePriceConfirmed ? 'Sin costo' : '$0 · revisar tarifa';
  const amount = formatMoneyAmount(option?.price, region);
  return amount || "Sin precio";
}

function getPrimaryExperienceOption(service) {
  const options = normalizeExperiencePricingOptions(service?.visitTypeOptions, service?.price);
  const preferred = service?.experienceType || service?.eventExperienceType;
  return options.find((x) => x.enabled && String(x.id) === String(preferred)) || options.find((x) => x.enabled) || options[0];
}

function formatEventPackagePrice(service, region = {}) {
  const option = getPrimaryExperienceOption(service);
  if (!option) return formatServicePrice(service, region);
  const enabled = normalizeExperiencePricingOptions(service?.visitTypeOptions, service?.price).filter((x) => x.enabled);
  return enabled.length ? enabled.map(item => item.label + ': ' + formatExperienceOptionPrice(item, region)).join(' · ') : 'Sin experiencias activas';
}

function EventExperiencePricingEditor({ value = [], basePrice = 0, onChange = () => {} }) {
  const [showInactive,setShowInactive]=useState(false);
  const rows = normalizeExperiencePricingOptions(value, basePrice);
  const update = (id, patch) => {
    const next = rows.map((item) => {
      if (item.id !== id) return item;
      const priceType = patch.priceType || patch.pricingType || item.priceType || "fixed";
      return {
        ...item,
        ...patch,
        priceType,
        pricingType: priceType,
        priceLabel: priceType === "quote" ? (patch.priceLabel || item.priceLabel || "Solicitar cotización") : "",
      };
    });
    onChange(normalizeExperiencePricingOptions(next, basePrice));
  };

  return (
    <div className="svc-experience-pricing-editor">
      <div className="svc-event-mini-head slim">
        <div>
          <strong>{t("Experiencias del paquete")}</strong>
          <span>{t("Cada experiencia puede tener precio o cotización.")}</span>
        </div>
      </div>
      <div className="svc-experience-price-list">
        {rows.filter(item=>item.enabled || showInactive).map((item) => {
          const isQuote = String(item.priceType || "fixed") === "quote";
          return (
            <div className={`svc-experience-price-row ${item.enabled ? "enabled" : "disabled"}`} key={item.id}>
              <button type="button" aria-label={`${item.enabled ? "Quitar" : "Agregar"} ${item.label}`} className={`svc-mini-toggle ${item.enabled ? "active" : ""}`} onClick={() => update(item.id, { enabled: !item.enabled })}>
                {item.enabled ? t("Quitar") : "Agregar"}
              </button>
              <strong>{item.label}</strong>
              <div className="svc-price-mode-mini">
                <button type="button" className={!isQuote ? "active" : ""} onClick={() => update(item.id, { priceType: "fixed", pricingType: "fixed" })}>{t("Precio")}</button>
                <button type="button" className={isQuote ? "active" : ""} onClick={() => update(item.id, { priceType: "quote", pricingType: "quote", priceLabel: "Solicitar cotización" })}>{t("Cotización")}</button>
              </div>
              {isQuote ? (
                <div className="svc-quote-chip">{t("Solicitar cotización")}</div>
              ) : (
                <div className="svc-money-input mini"><i><ServiceCurrency/></i><input type="number" aria-label={`Precio de ${item.label}`} min="0" step="0.01" value={item.price ?? ""} onChange={(e) => update(item.id, { price: e.target.value })} placeholder={t("Por definir")} /></div>
              )}
              {!isQuote && item.enabled && <details open className="event-experience-duration" style={{gridColumn:'1 / -1',width:'100%'}}>
                <summary>{t("Tabla por duración") + " "}{item.durationPrices.length ? `(${item.durationPrices.length})` : '· personalizar'}</summary>
                <p>{t("Define un precio final para cada duración de esta experiencia. Sin filas, se usa la regla de duración del paquete.")}</p>
                {item.durationPrices.map((tier,index)=><div key={index} style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:8}}>
                  <label>{t("Horas") + " "}<input aria-label={`${item.label}: horas ${index+1}`} type="number" min="0.5" max="24" step="0.5" value={tier.hours} onChange={e=>update(item.id,{durationPrices:item.durationPrices.map((row,i)=>i===index?{...row,hours:e.target.value}:row)})}/></label>
                  <label>{t("Precio total") + " "}<input aria-label={`${item.label}: precio ${index+1}`} type="number" min="0" step="0.01" value={tier.price} onChange={e=>update(item.id,{durationPrices:item.durationPrices.map((row,i)=>i===index?{...row,price:e.target.value}:row)})}/></label>
                  <button type="button" aria-label={`Eliminar duración ${index+1} de ${item.label}`} onClick={()=>update(item.id,{durationPrices:item.durationPrices.filter((_,i)=>i!==index)})}>{t("Eliminar")}</button>
                </div>)}
                <button type="button" onClick={()=>update(item.id,{durationPrices:[...item.durationPrices,{hours:'',price:''}]})}>{t("Agregar duración")}</button>
              </details>}
              {!isQuote && item.enabled && item.price !== null && Number(item.price) === 0 && !item.durationPrices.length && <label style={{gridColumn:'1 / -1',fontSize:13}}><input type="checkbox" checked={item.freePriceConfirmed === true} onChange={e=>update(item.id,{freePriceConfirmed:e.target.checked})}/>{" " + t("Confirmo que esta experiencia se ofrece sin costo.")}</label>}
            </div>
          );
        })}
      </div>
      {rows.some(item=>!item.enabled)&&<button type="button" className="event-add-experience" onClick={()=>setShowInactive(!showInactive)}>{showInactive?"Ocultar experiencias inactivas":"Agregar experiencia"}</button>}
    </div>
  );
}

function VisitTypeOptionsEditor({ value, basePrice, onChange }) {
  const options = normalizeVisitTypeOptions(value, basePrice);

  const update = (id, patch) => {
    const next = options.map((item) => item.id === id ? { ...item, ...patch } : item);
    onChange(normalizeVisitTypeOptions(next, basePrice));
  };

  return (
    <div className="svc-visit-editor">
      <div className="svc-character-editor-head">
        <strong>{t("Experiencias base")}</strong>
        <span>{t("Visita, show completo, fotos, etc.")}</span>
      </div>

      {options.map((item) => (
        <div className="svc-visit-row" key={item.id}>
          <button
            type="button"
            className={`svc-mini-toggle ${item.enabled ? "active" : ""}`}
            onClick={() => update(item.id, { enabled: !item.enabled })}
          >
            {item.enabled ? t("Activo") : "Off"}
          </button>

          <div className="svc-visit-label">{item.label}</div>

          <input
            type="number"
            value={item.price ?? 0}
            onChange={(e) => update(item.id, { price: e.target.value })}
            placeholder="$"
          />
        </div>
      ))}

      <p className="svc-help-text">{t("Ejemplo: Show completo $100 y Solo visita $60. Luego el cliente suma los personajes que quiera.")}</p>
    </div>
  );
}


const EVENT_EXPERIENCE_TYPES = [
  { id: "show", label: "Show completo", helper: "Paquete principal con animación." },
  { id: "visit", label: "Solo visita", helper: "Aparición corta o saludo." },
  { id: "meet_greet", label: "Meet & greet", helper: "Fotos y momento especial." },
  { id: "photos", label: "Fotos y saludo", helper: "Experiencia corta y sencilla." },
  { id: "other", label: "Otro", helper: "Experiencia personalizada." },
];

const DEFAULT_EVENT_EXTRAS = [
  { id: "balloons", name: "Globoflexia", price: 15 },
  { id: "facepaint", name: "Pintacaritas", price: 20 },
];

function normalizeExtrasRows(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row, idx) => {
      const name = String(row?.name || row?.label || "").trim();
      if (!name) return null;
      const priceRaw = row?.price ?? row?.amount ?? 0;
      const price = priceRaw === "" || priceRaw == null ? 0 : Number(priceRaw || 0);
      return {
        id: row?.id || `extra-${idx}-${name.toLowerCase().replace(/[^a-z0-9]+/gi, "-")}`,
        name,
        price: Number.isFinite(price) ? price : 0,
        enabled: row?.enabled !== false,
        type: "extra",
      };
    })
    .filter(Boolean);
}

function experienceLabel(value) {
  return (EVENT_EXPERIENCE_TYPES.find((x) => x.id === value) || EVENT_EXPERIENCE_TYPES[0]).label;
}

function EventTypeSelector({ value = "show", onChange = () => {} }) {
  const selected = value || "show";
  return (
    <div className="svc-event-type-grid">
      {EVENT_EXPERIENCE_TYPES.map((type) => (
        <button key={type.id} type="button" className={selected === type.id ? "active" : ""} onClick={() => onChange(type.id)}>
          <strong>{type.label}</strong>
          <span>{type.helper}</span>
        </button>
      ))}
    </div>
  );
}

function EventExtrasEditor({ value = [], onChange = () => {} }) {
  const rows = Array.isArray(value) ? value : [];
  const updateRow = (idx, patch) => onChange(rows.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  const addRow = (template = null) => onChange([...rows, template ? { ...template } : { id: `extra-${Date.now()}`, name: "", price: 0 }]);
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));
  const availableDefaults = DEFAULT_EVENT_EXTRAS.filter((item) => !rows.some((row) => String(row.name).toLowerCase() === String(item.name).toLowerCase()));

  return (
    <div className="svc-event-extras-editor">
      <div className="svc-event-mini-head">
        <div>
          <strong>{t("Extras opcionales")}</strong>
          <span>{t("Agrega cosas que el cliente podría sumar al paquete.")}</span>
        </div>
        <button type="button" onClick={() => addRow()}>{t("Agregar extra")}</button>
      </div>
      {rows.length ? (
        <div className="svc-event-extra-rows">
          {rows.map((row, idx) => (
            <div className="svc-event-extra-row" key={row.id || idx}>
              <input value={row.name || ""} onChange={(e) => updateRow(idx, { name: e.target.value })} placeholder={t("Nombre del extra")} />
              <input aria-label={t("Precio del extra")} type="number" min="0" step="0.01" value={row.price ?? 0} onChange={(e) => updateRow(idx, { price: e.target.value })} placeholder="$" />
              <button type="button" onClick={() => removeRow(idx)}>✕</button>
            </div>
          ))}
        </div>
      ) : <div className="svc-event-empty-note">{t("Los extras son opcionales. Puedes dejar esta parte vacía.")}</div>}
      {availableDefaults.length ? (
        <div className="svc-event-quick-add">
          {availableDefaults.slice(0, 3).map((item) => <button key={item.id} type="button" onClick={() => addRow(item)}>+ {item.name}</button>)}
        </div>
      ) : null}
    </div>
  );
}

function EventPackageBlock({ id, title, meta, helper, children, openBlock, setOpenBlock }) {
  const isOpen = openBlock === id;
  return <div className={`svc-event-lite-block ${isOpen ? "open" : ""}`}>
    <button type="button" className="svc-event-lite-summary" aria-expanded={isOpen} onClick={() => setOpenBlock(isOpen ? null : id)}>
      <div><strong>{title}</strong><span>{helper}</span></div><em>{meta}</em><i>{isOpen ? "Ocultar" : t("Editar")}</i>
    </button>
    {isOpen ? <div className="svc-event-lite-body">{children}</div> : null}
  </div>;
}


function EventPackageEditor({value,onChange,resources,inventoryItems,requestActivation,onUploadImage,canUploadImage=false,busy=false}) {
 const [section,setSection]=useState('presentation');

 const tabs=[['presentation','Presentación'],['pricing','Precios'],['duration','Duraciones'],['characters','Personajes'],['extras','Extras'],['availability','Disponibilidad'],['booking','Reserva y pago']];
 const patch=changes=>onChange({...value,...changes});
 const selected=tabs.find(tab=>tab[0]===section);
 return <div className="event-editor-workspace event-editor-quiet event-editor-v2">
  <nav aria-label={t("Secciones del paquete")} className="event-editor-nav">{tabs.map(([id,label,hint],index)=><button type="button" key={id} aria-current={section===id?'step':undefined} onClick={()=>setSection(id)}><strong>{label}</strong></button>)}</nav>
  <div className="event-editor-content"><header><div><h3>{selected?.[1]}</h3><p>{{presentation:"Así verán este paquete tus clientes.",pricing:"Define cuánto cuesta cada experiencia o si requiere cotización.",duration:"Elige cuánto puede durar el evento y cómo cambia su precio.",characters:"Define qué personajes incluye el paquete y cuáles tienen recargo.",extras:"Complementos opcionales que el cliente puede agregar.",availability:"Evita reservar el mismo disfraz o animador en eventos simultáneos.",booking:"Define cuándo se confirma la reserva y cuánto paga el cliente."}[section]}</p></div></header>
   {section==='presentation'&&<div className="event-presentation-only"><ServicePresentationEditor value={value} onChange={onChange} isEventsBusiness canUploadImage={canUploadImage} onUploadImage={onUploadImage} nameLabel="Nombre del paquete" namePlaceholder="Ej.: Mundo de Mario Bros"/></div>}
   {section==='pricing'&&<><EventExperiencePricingEditor value={value.visitTypeOptions} basePrice={value.price} onChange={options=>{const primary=options.find(x=>x.enabled);patch({visitTypeOptions:options,...(primary?{experienceType:primary.id,eventExperienceType:primary.id,price:primary.price,priceType:primary.priceType,pricingType:primary.priceType}:{})});}}/></>}
   {section==='duration'&&<section className="event-duration-settings"><p className="event-editor-note">{t("Estas son duraciones del evento, no horas de inicio. Las horas disponibles dependen de Horarios y reservas, el equipo y las reservas existentes.")}</p><h4>{t("Duración y límites")}</h4><p>{t("El cliente elige en intervalos de 30 minutos entre el mínimo y el máximo. Para una sola duración, usa el mismo valor en ambos.")}</p><Field label={t("Duración general (minutos)")}><input type="number" min="1" value={value.duration} onChange={e=>patch({duration:e.target.value})}/></Field><div className="svc-two"><Field label={t("Mínimo de horas")}><input type="number" min="0.5" step="0.5" value={value.minHours} onChange={e=>patch({minHours:e.target.value})}/></Field><Field label={t("Máximo de horas")}><input type="number" min="0.5" step="0.5" value={value.maxHours} onChange={e=>patch({maxHours:e.target.value})}/></Field></div><DurationPricingEditor pricingMode={value.pricingMode} tiers={value.durationPricingTiers} minHours={value.minHours} maxHours={value.maxHours} basePrice={value.price} onModeChange={pricingMode=>patch({pricingMode})} onTiersChange={durationPricingTiers=>patch({durationPricingTiers})}/><p className="event-editor-note">Si una experiencia tiene su propia tabla en Precios, esa tabla tiene prioridad. Sus duraciones deben quedar dentro de estos límites. La escala general se comparte entre las experiencias que no tienen tabla propia.</p></section>}
   {section==='extras'&&<EventExtrasEditor value={value.extrasRows} onChange={extrasRows=>patch({extrasRows})}/>}
   {section==='availability'&&<EventResourceRequirements value={value} onChange={onChange} resources={resources} expanded/>}
   {section==='characters'&&<EventPackageEssentials value={value} onChange={onChange} resources={resources} section={section}/>}
   {section==='booking'&&<section className="event-reservation-settings">
    <h3>{t("¿Cuándo confirmas la reserva?")}</h3>
    <Field label={t("Confirmación")}><select value={value.eventApprovalMode || 'inherit'} onChange={e=>patch({eventApprovalMode:e.target.value})}>
     <option value="inherit">{t("Usar la configuración del negocio")}</option><option value="manual">{t("Después de revisar cada solicitud")}</option><option value="auto">{t("Automáticamente al cumplir el pago requerido")}</option>
    </select></Field>
    <p>{t("Las experiencias por cotización siempre requieren una propuesta y la aceptación del cliente antes del pago.")}</p>
    <BookingPaymentPolicyEditor value={value} onChange={onChange} requestActivation={requestActivation}/>
    <p className="event-reservation-explanation">{value.eventApprovalMode==='auto' ? 'Con disponibilidad, la reserva se confirma al completar el abono requerido. Si no pides abono, se confirma al reservar.' : value.eventApprovalMode==='manual' ? 'Primero revisas la solicitud. Después de aprobarla, el cliente completa el abono requerido para confirmar.' : 'Se aplica la confirmación configurada para el negocio.'}{" " + t("Abono:") + " "}{formatBookingPaymentPolicySummary(value)}.</p>
    <h3>{t("Tiempo entre eventos")}</h3><EventLogisticsFields value={value} onChange={patch} expanded/>
    <details className="event-editor-advanced"><summary>{t("Saldo, capacidad y costos internos")}</summary>
     <Field label={t("¿Cómo recibes el saldo?")}><select value={value.eventBalanceOnlineEnabled===false?'business':'online'} onChange={e=>patch({eventBalanceOnlineEnabled:e.target.value==='online'})}><option value="online">{t("Wompi o pago directo al negocio")}</option><option value="business">{t("Directamente en el negocio")}</option></select><small>{t("El cliente autoriza cada pago; no hay cargos automáticos.")}</small></Field>
     <EventConcurrencyChoice repeatable={!!value.allowConcurrentSameService} maxConcurrent={value.maxConcurrentSameService||1} onChange={allowConcurrentSameService=>patch({allowConcurrentSameService})} onMaxConcurrentChange={maxConcurrentSameService=>patch({maxConcurrentSameService})}/><InventoryUsageEditor value={value.inventoryUsage} service={value} inventoryItems={inventoryItems} onChange={inventoryUsage=>patch({inventoryUsage})} disabled={busy}/>
    </details>
   </section>}
   {section==='presentation'&&<section className="event-visibility"><Field label={t("Visibilidad en el perfil")}><select value={value.disabled ? "hidden" : "visible"} onChange={e=>patch({disabled:e.target.value==="hidden"})}><option value="visible">{t("Visible · los clientes pueden encontrarlo")}</option><option value="hidden">{t("Oculto · solo para gestión interna")}</option></select><small>{t("Ocultarlo no cancela las reservas existentes.")}</small></Field></section>}
  </div>
 </div>;
}

function EventCatalogRow({service,index,reordering,canMoveUp,canMoveDown,onMove,onEdit,onVisibility}) {
  const region=useBusinessRegion();

 const [menu,setMenu]=useState(false);
 const missing=isServiceMissingPrice(service);
 const tariffs=normalizeExperiencePricingOptions(service.visitTypeOptions,service.price).filter(x=>x.enabled);
 return <article className="event-catalog-row" role="listitem">
  <button type="button" className="event-catalog-identity" onClick={onEdit} aria-label={'Editar '+service.name}>
   {service.imageUrl?<img src={service.imageUrl} alt=""/>:<span className="event-catalog-placeholder" aria-hidden="true">{String(service.name||'P').slice(0,1)}</span>}
   <span><strong>{service.name||'Paquete'}</strong><span className="event-catalog-tariffs">{missing?'Tarifas por completar':tariffs.map(x=>x.label+': '+formatExperienceOptionPrice(x, region)).join(' · ')}</span><span className="event-catalog-details">{service.charactersMode==='choose_primary' ? t("1 personaje incluido a elección") : t("Personajes")}: {normalizeCharacterRows(service.characters).map(row=>row.name).filter(Boolean).join(', ') || 'Sin opciones'}</span><span className="event-catalog-details">{t("Extras:") + " "}{normalizeExtrasRows(service.extras||service.optionalExtras).filter(row=>row.enabled).map(row=>row.name+' (+'+formatMoneyAmount(row.price, region)+')').join(', ') || 'Sin extras'} · {formatBookingPaymentPolicySummary(service)}</span></span>
  </button>
  <span className="event-catalog-state">{service.disabled?t("Oculto"):'Visible'}{missing&&<small>{t("Por revisar")}</small>}</span>
  {reordering&&<div className="event-catalog-order"><button type="button" disabled={!canMoveUp} onClick={()=>onMove(-1)} aria-label={'Subir '+service.name}>↑</button><span>{index+1}</span><button type="button" disabled={!canMoveDown} onClick={()=>onMove(1)} aria-label={'Bajar '+service.name}>↓</button></div>}
  <div className="event-catalog-actions"><button type="button" onClick={onEdit}>{t("Editar")}</button><div className="event-catalog-more" onBlur={e=>{if(!e.currentTarget.contains(e.relatedTarget))setMenu(false);}} onKeyDown={e=>{if(e.key==='Escape'){setMenu(false);e.currentTarget.querySelector('button')?.focus();}}}><button type="button" aria-label={'Opciones de '+service.name} aria-expanded={menu} onClick={()=>setMenu(!menu)}>⋯</button>{menu&&<div className="event-catalog-popover"><button type="button" onClick={()=>{setMenu(false);onVisibility();}}>{service.disabled?'Mostrar en el perfil':'Ocultar del perfil'}</button></div>}</div></div>
 </article>;
}

function EventTariffList({service}) {
  const region=useBusinessRegion();

 const options=normalizeExperiencePricingOptions(service.visitTypeOptions,service.price).filter(option=>option.enabled);
 return <div className="event-tariff-list">{options.length?options.map(option=><div key={option.id}><span>{option.label}</span><strong>{formatExperienceOptionPrice(option, region)}</strong></div>):<span>{t("Sin experiencias activas")}</span>}</div>;
}

function EventPackageEssentials({ value, onChange, resources=[], section=null }) {
  const characters = Array.isArray(value?.characterRows) ? value.characterRows : [];
  const extras = Array.isArray(value?.extrasRows) ? value.extrasRows : [];
  const charactersMode = value?.charactersMode || "customer_selects";
  const [openBlock, setOpenBlock] = useState(section === 'characters' ? 'characters' : section === 'booking' ? 'conditions' : null);
  const patch = (nextPatch) => onChange({ ...(value || {}), ...nextPatch });

  const charsLabel = charactersMode === "none"
    ? "Sin adicionales"
    : charactersMode === "included"
      ? `${characters.length} ${characters.length === 1 ? 'incluido' : 'incluidos'} en la lista`
      : `${characters.length} ${characters.length === 1 ? 'opcional' : 'opcionales'}`;
  const extrasLabel = extras.length ? `${extras.length} extra${extras.length === 1 ? "" : "s"}` : "Sin extras";


  if(section==='characters') return <section className="event-characters-simple">
    <Field label={t("¿Cómo se eligen los personajes?")}><select value={charactersMode} onChange={e=>patch({charactersMode:e.target.value})}>
      <option value="choose_primary">{t("El cliente elige un personaje incluido")}</option>
      <option value="customer_selects">{t("Personajes fijos y adicionales")}</option>
      <option value="included">{t("Todos los personajes de la lista incluidos")}</option>
      <option value="none">{t("Sin personajes para elegir")}</option>
    </select></Field>
    {charactersMode==='choose_primary' ? <p>El precio de la experiencia incluye un personaje y su animador. El recargo de cada fila se cobra solo si ese personaje acompaña al principal.</p> : <Field label={t("Personajes fijos incluidos")}><input value={value.includedCharactersText || ''} onChange={e=>patch({includedCharactersText:e.target.value})} placeholder={t("Ej.: Mario")}/><small>{t("Estos personajes siempre asisten. Separa los nombres con comas.")}</small></Field>}
    {charactersMode !== 'none' && <CharacterRowsEditor value={characters} choosePrimary={charactersMode==='choose_primary'} included={charactersMode==='included'} onChange={characterRows=>patch({characterRows})}/>}
  </section>;
  return (
    <section className="svc-editor-section compact svc-event-essentials svc-event-essentials-minimal svc-event-lite">
      <div className="svc-event-lite-head">
        <strong>{t("Configura cómo se reserva")}</strong>
        <span>{t("Define qué incluye, qué puede agregar el cliente y cómo atenderás la reserva.")}</span>
      </div>

      {(!section || section === 'booking') && (<EventPackageBlock openBlock={openBlock} setOpenBlock={setOpenBlock} id="conditions"
        title={t("Confirmación y saldo")} meta={value.eventApprovalMode === 'manual' ? 'Revisión manual' : value.eventApprovalMode === 'auto' ? 'Automática' : 'Según el negocio'}
        helper="Cuándo se confirma la reserva y cómo recibirás el saldo pendiente.">
      <Field label={t("Confirmación del paquete")}><select value={value.eventApprovalMode || 'inherit'} onChange={e=>patch({eventApprovalMode:e.target.value})}>
        <option value="inherit">{t("Usar la configuración del negocio")}</option><option value="manual">{t("Revisar y aprobar cada solicitud")}</option><option value="auto">{t("Confirmar automáticamente si hay disponibilidad")}</option>
      </select><small>{t("Los paquetes por cotización siempre requieren revisión del negocio.")}</small></Field>
      <Field label={t("Pago del saldo")}><select value={value.eventBalanceOnlineEnabled===false?'business':'online'} onChange={e=>patch({eventBalanceOnlineEnabled:e.target.value==='online'})}>
        <option value="online">{t("Ofrecer Wompi y pago directo al negocio")}</option><option value="business">{t("Recibir el saldo directamente en el negocio")}</option>
      </select><small>Aplica a nuevas reservas. El cliente autoriza cada pago; no hay cobros automáticos. Wompi debe estar habilitado para tu negocio.</small></Field>
      </EventPackageBlock>)}
      {(!section || section === 'characters') && (<EventPackageBlock openBlock={openBlock} setOpenBlock={setOpenBlock}
        id="characters"
        title={t("Personajes")}
        meta={charsLabel}
        helper="Separa los personajes incluidos de los adicionales con recargo."
      >
        <div className="svc-character-mode-row compact">
          {[{ id: "customer_selects", label: "Ofrecer adicionales" }, { id: "included", label: "Lista incluida" }, { id: "none", label: "Sin adicionales" }].map((item) => (
            <button key={item.id} type="button" aria-pressed={charactersMode === item.id} className={charactersMode === item.id ? "active" : ""} onClick={() => patch({ charactersMode: item.id })}>{item.label}</button>
          ))}
        </div>
        {charactersMode !== "none" ? <CharacterRowsEditor value={characters} included={charactersMode === 'included'} onChange={(rows) => patch({ characterRows: rows })} /> : <div className="svc-event-empty-note">El cliente no podrá agregar personajes. Las filas guardadas se conservan si cambias de modalidad.</div>}
        <Field label="Personajes incluidos en el precio (separados por coma)"><input value={value.includedCharactersText || ""} onChange={e => patch({includedCharactersText:e.target.value})} placeholder={t("Ej.: Mario")} /><small>Siempre aparecen como incluidos. Para ofrecer Mario con Peach opcional, escribe Mario aquí y agrega Peach como personaje adicional.</small></Field>
      </EventPackageBlock>)}

      {(!section || section === 'characters') && (<EventPackageBlock openBlock={openBlock} setOpenBlock={setOpenBlock}
        id={t("extras")}
        title={t("Extras opcionales")}
        meta={extrasLabel}
        helper="Adicionales como globoflexia o pintacaritas; no cambian la duración reservada."
      >
        <EventExtrasEditor value={extras} onChange={(rows) => patch({ extrasRows: rows })} />
      </EventPackageBlock>)}
      {(!section || section === 'characters') && (<EventPackageBlock openBlock={openBlock} setOpenBlock={setOpenBlock} id="availability"
        title={t("Disponibilidad y equipo")} meta={`${Array.isArray(value.eventResourceRequirements) ? value.eventResourceRequirements.length : 0} vínculos`}
        helper="Disfraces y animadores necesarios para poder atender este paquete.">
        <p className="svc-help-text">Vincula también los personajes incluidos. Sus nombres por sí solos no reservan un disfraz ni un animador.</p>
        <EventResourceRequirements value={value} onChange={onChange} resources={resources} expanded />
      </EventPackageBlock>)}
      {(!section || section === 'booking') && (<EventPackageBlock openBlock={openBlock} setOpenBlock={setOpenBlock} id="logistics"
        title={t("Preparación y traslado")} meta="Tiempos entre reservas"
        helper="Reserva margen antes y después del evento para evitar cruces de horario.">
        <EventLogisticsFields value={value} onChange={patch} expanded />
      </EventPackageBlock>)}
    </section>
  );
}


function getServiceOrderValue(service, fallback = 0) {
  const candidates = [
    service?.serviceOrder,
    service?.publicOrder,
    service?.displayOrder,
    service?.order,
    service?.sortOrder,
    service?.position,
    service?.index,
  ];

  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }

  return fallback;
}

function sortServicesForPublicOrder(list = []) {
  return (Array.isArray(list) ? list : [])
    .map((service, idx) => ({ service, idx }))
    .sort((a, b) => {
      const ao = getServiceOrderValue(a.service, a.idx);
      const bo = getServiceOrderValue(b.service, b.idx);
      if (ao !== bo) return ao - bo;

      const aCreated = Number(a.service?.createdAt?.seconds || a.service?.createdAt || 0);
      const bCreated = Number(b.service?.createdAt?.seconds || b.service?.createdAt || 0);
      if (aCreated !== bCreated) return aCreated - bCreated;

      return a.idx - b.idx;
    })
    .map((item, publicIndex) => ({
      ...item.service,
      serviceOrder: getServiceOrderValue(item.service, publicIndex),
      publicOrder: getServiceOrderValue(item.service, publicIndex),
      displayOrder: getServiceOrderValue(item.service, publicIndex),
    }));
}

function assignServiceOrderSequence(list = []) {
  return (Array.isArray(list) ? list : []).map((service, idx) => ({
    ...service,
    serviceOrder: idx,
    publicOrder: idx,
    displayOrder: idx,
    order: idx,
    sortOrder: idx,
  }));
}

function normalizeServicesWithOrder(list = []) {
  return assignServiceOrderSequence(sortServicesForPublicOrder(list));
}


function serviceInventoryUsageRows(value) {
  return (Array.isArray(value) ? value : [])
    .map((row, idx) => {
      const itemId = String(row?.itemId || row?.inventoryItemId || row?.id || "").trim();
      const quantityUsed = Number(row?.quantityUsed ?? row?.quantity ?? row?.qty ?? 0);
      if (!itemId || !Number.isFinite(quantityUsed) || quantityUsed <= 0) return null;
      return {
        id: row?.id || `usage-${itemId}-${idx}`,
        itemId,
        quantityUsed,
      };
    })
    .filter(Boolean);
}

function inventoryLabel(item) {
  return String(item?.name || item?.title || item?.label || item?.id || "Producto").trim();
}

function serviceInventoryStockTone(item = {}, quantityUsed = 0) {
  const stock = Number(item?.currentStock ?? item?.stock ?? item?.quantity ?? 0);
  const minStock = Number(item?.minStock ?? item?.minimumStock ?? item?.reorderPoint ?? 0);
  const qty = Number(quantityUsed || 0);
  if (!item || !item.id) return "missing";
  if (Number.isFinite(stock) && Number.isFinite(qty) && stock < qty) return "critical";
  if (Number.isFinite(stock) && Number.isFinite(minStock) && minStock > 0 && stock - qty <= minStock) return "warning";
  return "ok";
}

function serviceInventoryStockLabel(tone) {
  if (tone === "missing") return "No encontrado";
  if (tone === "critical") return "Stock insuficiente";
  if (tone === "warning") return "Cerca del mínimo";
  return "Stock OK";
}

function serviceInventoryUsageCost(rows = [], inventoryItems = []) {
  const map = new Map((Array.isArray(inventoryItems) ? inventoryItems : []).map((item) => [String(item.id), item]));
  return serviceInventoryUsageRows(rows).reduce((sum, row) => {
    const item = map.get(String(row.itemId));
    const unitCost = Number(item?.unitCost ?? item?.cost ?? 0);
    return sum + (Number.isFinite(unitCost) ? unitCost * Number(row.quantityUsed || 0) : 0);
  }, 0);
}

function servicePriceNumber(service = {}) {
  const priceType = normalizePriceType(service);
  if (priceType === "quote") return 0;
  const n = Number(service?.price ?? service?.basePrice ?? 0);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function serviceMaterialSummary(service = {}, inventoryItems = []) {
  const rows = serviceInventoryUsageRows(service?.inventoryUsage || service?.inventoryConsumption || []);
  const materialCost = serviceInventoryUsageCost(rows, inventoryItems);
  const price = servicePriceNumber(service);
  const margin = Math.max(0, price - materialCost);
  const marginPct = price > 0 ? Math.round((margin / price) * 100) : null;
  return {
    rows,
    count: rows.length,
    materialCost,
    price,
    margin,
    marginPct,
    hasMaterials: rows.length > 0,
  };
}

function InventoryUsageEditor({ value = [], inventoryItems = [], service = {}, onChange = () => {}, disabled = false }) {
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const rows = serviceInventoryUsageRows(value);
  const activeItems = (Array.isArray(inventoryItems) ? inventoryItems : []).filter((item) => item && item.active !== false);
  const itemMap = new Map(activeItems.map((item) => [String(item.id), item]));
  const summary = serviceMaterialSummary({ ...service, inventoryUsage: rows }, activeItems);
  const totalCost = summary.materialCost;

  const updateRow = (idx, patch) => {
    const next = rows.map((row, index) => index === idx ? { ...row, ...patch } : row);
    onChange(serviceInventoryUsageRows(next));
  };

  const addRow = () => {
    const first = activeItems.find((item) => item?.id && !rows.some((row) => String(row.itemId) === String(item.id))) || activeItems.find((item) => item?.id);
    if (!first) return;
    onChange([...rows, { id: `usage-${Date.now()}`, itemId: String(first.id), quantityUsed: 1 }]);
    setMaterialsOpen(true);
  };

  const removeRow = (idx) => onChange(rows.filter((_, index) => index !== idx));

  const riskLabel = rows.some((row) => serviceInventoryStockTone(itemMap.get(String(row.itemId)), row.quantityUsed) === "critical")
    ? "Crítico"
    : rows.some((row) => serviceInventoryStockTone(itemMap.get(String(row.itemId)), row.quantityUsed) === "warning")
      ? "Revisar"
      : rows.length
        ? "OK"
        : "—";

  const compactFooter = rows.length
    ? `${rows.length} producto${rows.length === 1 ? "" : "s"} · $${totalCost.toFixed(2)} costo${summary.marginPct != null ? ` · ${summary.marginPct}% margen` : ""}`
    : activeItems.length
      ? "Conecta productos del inventario solo si este servicio consume materiales."
      : "Primero agrega productos en Inventario para poder conectarlos.";

  return (
    <section className={`svc-materials-compact-card ${rows.length ? "has-materials" : "empty"}`}>
      <div className="svc-materials-compact-copy">
        <strong>{t("Materiales")}</strong>
        <p>{rows.length ? compactFooter : "Sin materiales conectados."}</p>
      </div>

      {rows.length ? (
        <div className="svc-materials-compact-metrics" aria-label={t("Resumen de materiales")}>
          <div><span>{t("Costo")}</span><strong>${totalCost.toFixed(2)}</strong></div>
          <div><span>{t("Margen")}</span><strong>{summary.marginPct == null ? "—" : `${summary.marginPct}%`}</strong></div>
          <div><span>{t("Stock")}</span><strong>{riskLabel}</strong></div>
        </div>
      ) : null}

      <button type="button" className="svc-materials-config-btn" onClick={() => setMaterialsOpen(true)} disabled={disabled || (!activeItems.length && !rows.length)}>
        {rows.length ? t("Editar") : "Configurar"}
      </button>

      <Sheet
        open={materialsOpen}
        title={t("Materiales del servicio")}
        onClose={() => setMaterialsOpen(false)}
        footer={
          <div className="svc-actions svc-materials-sheet-actions">
            <button onClick={() => setMaterialsOpen(false)}>{t("Listo")}</button>
            <button className="primary" onClick={addRow} disabled={disabled || !activeItems.length}>{t("Agregar producto")}</button>
          </div>
        }
      >
        <div className="svc-materials-sheet-body">
          <div className="svc-materials-sheet-intro">
            <div>
              <span>{t("Inventario consumido")}</span>
              <strong>{service?.name || "Este servicio"}</strong>
              <p>{t("Define qué productos se descuentan cuando una reserva se completa. Los cambios se guardan cuando guardes el servicio.")}</p>
            </div>
            <em>{rows.length ? `${rows.length} producto${rows.length === 1 ? "" : "s"}` : "Sin materiales"}</em>
          </div>

          <div className="svc-materials-sheet-summary">
            <div><span>{t("Costo materiales")}</span><strong>${totalCost.toFixed(2)}</strong></div>
            <div><span>{t("Precio servicio")}</span><strong>{summary.price > 0 ? `$${summary.price.toFixed(2)}` : "—"}</strong></div>
            <div><span>{t("Margen estimado")}</span><strong>{summary.price > 0 ? `$${summary.margin.toFixed(2)}` : "—"}</strong></div>
            <div><span>{t("Margen %")}</span><strong>{summary.marginPct == null ? "—" : `${summary.marginPct}%`}</strong></div>
            <div><span>{t("Riesgo stock")}</span><strong>{riskLabel}</strong></div>
          </div>

          {!activeItems.length ? (
            <div className="svc-inventory-empty">{t("Primero agrega productos en Reportes → Inventario. Luego vuelve aquí para conectarlos a este servicio.")}</div>
          ) : null}

          <div className="svc-materials-editor-list">
            {rows.length === 0 && activeItems.length ? (
              <div className="svc-materials-empty-state">
                <strong>{t("No hay productos conectados")}</strong>
                <span>{t("Agrega el primer producto consumido por este servicio.")}</span>
                <button type="button" onClick={addRow}>{t("Agregar producto")}</button>
              </div>
            ) : null}

            {rows.map((row, idx) => {
              const item = itemMap.get(String(row.itemId));
              const unitCost = Number(item?.unitCost ?? item?.cost ?? 0) || 0;
              const stock = Number(item?.currentStock ?? item?.stock ?? item?.quantity ?? 0);
              const unit = item?.unit || "un.";
              const tone = serviceInventoryStockTone(item, row.quantityUsed);
              return (
                <div className="svc-materials-editor-row" key={row.id || `${row.itemId}-${idx}`}>
                  <label className="product">
                    <span>{t("Producto")}</span>
                    <select disabled={disabled || !activeItems.length} value={row.itemId} onChange={(e) => updateRow(idx, { itemId: e.target.value })}>
                      {activeItems.map((itemOption) => (
                        <option key={itemOption.id} value={itemOption.id}>{inventoryLabel(itemOption)}{" " + t("· stock") + " "}{itemOption.currentStock ?? itemOption.stock ?? 0} {itemOption.unit || "un."}</option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span>{t("Cantidad")}</span>
                    <input disabled={disabled} type="number" min="0" step="0.01" value={row.quantityUsed} onChange={(e) => updateRow(idx, { quantityUsed: e.target.value })} />
                  </label>

                  <div className="svc-materials-line-cost">
                    <span>{t("Costo línea")}</span>
                    <strong>${(unitCost * Number(row.quantityUsed || 0)).toFixed(2)}</strong>
                    <small>{unitCost ? `$${unitCost.toFixed(2)} / ${unit}` : "sin costo"}</small>
                  </div>

                  <div className={`svc-materials-stock-pill ${tone}`}>
                    <span>{serviceInventoryStockLabel(tone)}</span>
                    <strong>{Number.isFinite(stock) ? stock : "—"} {unit}</strong>
                  </div>

                  <button type="button" className="svc-materials-remove" disabled={disabled} onClick={() => removeRow(idx)}>{t("Quitar")}</button>
                </div>
              );
            })}
          </div>
        </div>
      </Sheet>
    </section>
  );
}

function ServicesSectionContent({ active=true, onConfigurePayments, businessId=null, services=[], onUploadServiceImage=()=>{}, onDeleteService=()=>{}, onAddService=async()=>{}, onSaveService=null, onServicesChange=null, disableAdd=false, singleService=false, singleServiceDuration=60, onToggleSingleService=()=>{}, onChangeSingleServiceDuration=()=>{}, isEventsBusiness=false, externalSaveSignal=0 }) {
  const region=useBusinessRegion();

  const activation = useLoopPaymentActivation(businessId, {onConfigure:onConfigurePayments, active});
  const [localServices,setLocalServices]=useState(normalizeServicesWithOrder(services));
  const [addOpen,setAddOpen]=useState(false);
  const [editOpen,setEditOpen]=useState(false);
  const [editing,setEditing]=useState(null);
  const [saving,setSaving]=useState(false);
  const [savingAdd,setSavingAdd]=useState(false);
  usePendingEditor({pending:active && (addOpen || editOpen),busy:saving || savingAdd,title:"Servicios",discard:()=>{setAddOpen(false);setEditOpen(false);setEditing(null);}});
  const [useImages,setUseImages]=useState(false);
  const [loadingUseImages,setLoadingUseImages]=useState(false);
  const [searchTerm,setSearchTerm]=useState("");
  const [serviceFilter,setServiceFilter]=useState("all");
  const [reordering,setReordering]=useState(false);
  const [catalogTools,setCatalogTools]=useState(false);
  const [showResources,setShowResources]=useState(false);
  const [showAddAdvanced,setShowAddAdvanced]=useState(false);
  const [showEditAdvanced,setShowEditAdvanced]=useState(false);
  const [newSvc,setNewSvc]=useState({name:"", description:"", duration:60, price:"", featured:false, disabled:false, minHours:0.5, maxHours:2, pricingMode:"proportional", durationPricingTiers:[], characterRows:[],visitTypeOptions:normalizeExperiencePricingOptions([], null),allowConcurrentSameService:false,maxConcurrentSameService:1, serviceIconKey:"auto", servicePresentationMode:"icon", presentationMode:"icon", experienceType:"show", charactersMode:"customer_selects", extrasRows:[], inventoryUsage:[], priceType:isEventsBusiness?"quote":"fixed", pricingType:isEventsBusiness?"quote":"fixed", priceLabel:"Solicitar cotización",bookingPaymentPolicy:{version:BOOKING_PAYMENT_POLICY_VERSION,type:"none",enabled:false,percentage:null,amount:null}});
  const [inventoryItems,setInventoryItems]=useState([]);
  const [eventResources,setEventResources]=useState([]);

  useEffect(()=>setLocalServices(normalizeServicesWithOrder(services)),[services]);
  useEffect(()=>{ let mounted=true; async function load(){ if(!businessId){setUseImages(false);return;} setLoadingUseImages(true); try{ const bdoc=await getDoc(doc(defaultDb,"businesses",businessId)); if(mounted && bdoc.exists()){ const data=bdoc.data()||{}; setUseImages(!!data.servicesWithImages); } }catch(e){console.warn(e)} finally{ if(mounted) setLoadingUseImages(false); }} load(); return()=>{mounted=false};},[businessId]);
  useEffect(()=>{ let mounted=true; async function loadInventory(){ if(!businessId){ setInventoryItems([]); return; } try{ const snap=await getDocs(collection(defaultDb,`businesses/${businessId}/inventoryItems`)); if(!mounted) return; setInventoryItems(snap.docs.map((d)=>({id:d.id,...(d.data()||{})})).filter((item)=>item.active!==false)); }catch(e){ console.warn("[ServicesSection] No se pudo cargar inventario",e); if(mounted) setInventoryItems([]); } } loadInventory(); return()=>{mounted=false};},[businessId]);
  const stats=useMemo(()=>({total:localServices.length, featured:localServices.filter(s=>s.featured).length}),[localServices]);
  const copy = isEventsBusiness
    ? {
        kicker: "Eventos",
        title: "Paquetes / temáticas",
        subtitle: `${stats.total} temáticas · ${stats.featured} destacadas`,
        add: "Agregar temática",
        singleTitle: "Un solo paquete / servicio",
        singleText: "Actívalo si el negocio solo ofrece una opción principal para reservar.",


        namePlaceholder: "Ej: Mario Bros, Frozen, Superhéroes, Princesas",
        addTitle: "Agregar paquete / temática",
        editTitle: "Editar paquete",
        empty: "Todavía no hay paquetes o temáticas.",
      }
    : {
        kicker: "Servicios",
        title: "Menú de reservas",
        subtitle: `${stats.total} servicios · ${stats.featured} destacados`,
        add: "Agregar",
        singleTitle: "Un solo paquete / servicio",
        singleText: "Actívalo si el negocio solo ofrece una opción principal para reservar.",
        photosTitle: "Fotos por servicio",
        photosText: "Activa imágenes para mostrar cada servicio de forma visual.",
        namePlaceholder: "Ej: Corte de cabello",
        addTitle: "Agregar servicio",
        editTitle: "Editar servicio",
        empty: "Todavía no hay servicios.",
      };
  const toggleUseImages=async(v)=>{ if(!businessId){setUseImages(v);return;} await loopBusinessUpdateDoc(defaultDb,`businesses/${businessId}`,{servicesWithImages:!!v,updatedAt:loopBusinessNow()}); setUseImages(!!v); };
  const parseCharacters=(rows=[])=>normalizeCharacterRows(rows);
  const charactersToRows=(arr)=>normalizeCharacterRows(arr);
  const uploadImage=(serviceId)=>{ setAddOpen(false); setEditOpen(false); setTimeout(()=>{ const input=document.createElement('input'); input.type='file'; input.accept=IMAGE_UPLOAD_ACCEPT; input.onchange=async(ev)=>{ const file=ev.target.files&&ev.target.files[0]; if(file){
      const prepared=await prepareImageFileForUpload(file,{purpose:'imagen de servicio',namePrefix:'loop-service',maxMb:18,setStatus:null});
      if(!prepared.ok){ alert(prepared.message||'No pudimos cargar esa imagen. Usa JPG, PNG, WEBP o HEIC/HEIF.'); return; }
      const imageModePatch={servicePresentationMode:"image",presentationMode:"image",updatedAt:loopBusinessNow()};
      try{
        if(businessId&&serviceId&&!String(serviceId).startsWith('tmp-')) await loopBusinessUpdateDoc(defaultDb,`businesses/${businessId}/services/${serviceId}`,imageModePatch);
        setLocalServices(prev=>{
          const next=prev.map(s=>String(s.id)===String(serviceId)?{...s,...imageModePatch}:s);
          onServicesChange&&onServicesChange(next);
          return next;
        });
      }catch(e){console.warn('[ServicesSection] No se pudo guardar modo imagen antes de subir foto',e);}
      onUploadServiceImage(prepared.file,serviceId);
    } }; input.click(); }, 80); };
  const add=async()=>{
    if(savingAdd) return;
    const validation=validateServiceBasics({...newSvc,serviceKind:isEventsBusiness ? "event_package" : newSvc.serviceKind});if(validation)return alert(validation);
    if(serializeBookingPaymentPolicy(newSvc).enabled && !(await activation.request())) return;

    setSavingAdd(true);

    try {
      const extras={
        featured:!!newSvc.featured,
        eventResourceRequirements:newSvc.eventResourceRequirements || [],
        disabled:!!newSvc.disabled,
        description:normalizeServiceDescription(newSvc.description),
        priceType:normalizePriceType(newSvc),
        pricingType:normalizePriceType(newSvc),
        priceLabel:normalizePriceType(newSvc)==="quote"?"Solicitar cotización":"",
        publicPriceLabel:normalizePriceType(newSvc)==="quote"?"Solicitar cotización":"",
        minHours:Number(newSvc.minHours||0.5),
        maxHours:Number(newSvc.maxHours||2),
        pricingMode:newSvc.pricingMode==="duration_tiers"?"duration_tiers":"proportional",
        durationPricingTiers:normalizeDurationPricingTiers(newSvc.durationPricingTiers,newSvc.minHours,newSvc.maxHours,newSvc.price),
        characters:parseCharacters(newSvc.characterRows),
        visitTypeOptions:normalizeExperiencePricingOptions(newSvc.visitTypeOptions,newSvc.price),
        allowConcurrentSameService:!!newSvc.allowConcurrentSameService,
        sameServiceCanOverlap:!!newSvc.allowConcurrentSameService,
        uniqueResource:!newSvc.allowConcurrentSameService,
        maxConcurrentSameService:Number(newSvc.maxConcurrentSameService||1),
        ...eventScheduleSettings(newSvc),
        serviceIconKey:newSvc.serviceIconKey || "auto",
        iconKey:newSvc.serviceIconKey || "auto",
        servicePresentationMode:newSvc.servicePresentationMode || newSvc.presentationMode || "icon",
        presentationMode:newSvc.servicePresentationMode || newSvc.presentationMode || "icon",
        serviceKind:isEventsBusiness?"event_package":"standard_service",
        experienceType:newSvc.experienceType || "show",
        eventExperienceType:newSvc.experienceType || "show",
        charactersMode:newSvc.charactersMode || "customer_selects",
        includedCharacters:String(newSvc.includedCharactersText || "").split(",").map(x=>x.trim()).filter(Boolean),
        extras:normalizeExtrasRows(newSvc.extrasRows),
        optionalExtras:normalizeExtrasRows(newSvc.extrasRows),
        inventoryUsage:serviceInventoryUsageRows(newSvc.inventoryUsage),
        inventoryConsumption:serviceInventoryUsageRows(newSvc.inventoryUsage),
        materialItemsCount:serviceInventoryUsageRows(newSvc.inventoryUsage).length,
        materialCostAmount:serviceInventoryUsageCost(newSvc.inventoryUsage, inventoryItems),
        sessionPackage:newSvc.sessionPackage || {enabled:false}, bookingPaymentPolicy:serializeBookingPaymentPolicy(newSvc)
      };

      const created=await onAddService(
        newSvc.name.trim(),
        Number(newSvc.duration||30),
        normalizePriceType(newSvc)==="quote"?null:(newSvc.price===""?0:Number(newSvc.price)),
        extras
      );

      const svc=created&&created.id
        ? { ...created, serviceOrder: localServices.length, publicOrder: localServices.length, displayOrder: localServices.length }
        : {id:`tmp-${Date.now()}`,...newSvc,...extras,duration:Number(newSvc.duration||30), serviceOrder: localServices.length, publicOrder: localServices.length, displayOrder: localServices.length};

      setLocalServices(prev=>{
        const exists = prev.some((s) => String(s.id) === String(svc.id));
        const next = normalizeServicesWithOrder(exists ? prev.map((s) => String(s.id) === String(svc.id) ? {...s, ...svc} : s) : [...prev,svc]);
        onServicesChange&&onServicesChange(next);
        return next;
      });

      setNewSvc({
        name:"",
        description:"",
        duration:60,
        price:"",
        featured:false,
        disabled:false,
        minHours:0.5,
        maxHours:2,
        pricingMode:"proportional",
        durationPricingTiers:[],
        characterRows:[],
        visitTypeOptions:normalizeExperiencePricingOptions([], null),
        allowConcurrentSameService:false,
        maxConcurrentSameService:1,
        serviceIconKey:"auto",
        servicePresentationMode:"icon",
        presentationMode:"icon",
        experienceType:"show",
        charactersMode:"customer_selects",
        extrasRows:[],
        inventoryUsage:[],
        priceType:isEventsBusiness?"quote":"fixed",
        pricingType:isEventsBusiness?"quote":"fixed",
        priceLabel:"Solicitar cotización",
        bookingPaymentPolicy:{version:BOOKING_PAYMENT_POLICY_VERSION,type:"none",enabled:false,percentage:null,amount:null}
      });

      setShowAddAdvanced(false);
      setAddOpen(false);
    } catch(e) {
      console.error('[ServicesSection] Error agregando servicio:', e);
      alert(`No se pudo agregar el servicio. ${e?.message ? e.message : 'Revisa permisos o conexión.'}`);
    } finally {
      setSavingAdd(false);
    }
  };
  const openEdit=(s)=>{ setEditing({...s, includedCharactersText:(s.includedCharacters || []).join(", "), price:s.price??"", description:String(s.description||s.subtitle||s.details||s.tagline||"").slice(0,SERVICE_DESCRIPTION_MAX_LENGTH), featured:!!s.featured, disabled:!!s.disabled, bufferEnabled:!!(Number(s.bufferBeforeMinutes||0)||Number(s.bufferAfterMinutes||0)), bufferBeforeMinutes:s.bufferBeforeMinutes ?? null, bufferAfterMinutes:s.bufferAfterMinutes ?? null, minHours:Number(s.minHours||1), maxHours:Number(s.maxHours||3), pricingMode:s.pricingMode==="duration_tiers"?"duration_tiers":"proportional", durationPricingTiers:normalizeDurationPricingTiers(s.durationPricingTiers,s.minHours,s.maxHours,s.price), characterRows:charactersToRows(s.characters), visitTypeOptions:normalizeExperiencePricingOptions(s.visitTypeOptions,s.price), allowConcurrentSameService:s.allowConcurrentSameService===true||s.sameServiceCanOverlap===true||s.repeatable===true||s.concurrentSameService===true, serviceIconKey:s.serviceIconKey||s.iconKey||"auto", servicePresentationMode:s.servicePresentationMode||s.presentationMode||(s.imageUrl?"image":"icon"), presentationMode:s.servicePresentationMode||s.presentationMode||(s.imageUrl?"image":"icon"), experienceType:s.experienceType||s.eventExperienceType||(Array.isArray(s.visitTypeOptions)&&s.visitTypeOptions.find((x)=>x?.enabled!==false)?.id)||"show", charactersMode:s.charactersMode||"customer_selects", extrasRows:normalizeExtrasRows(s.extras||s.optionalExtras||[]), inventoryUsage:serviceInventoryUsageRows(s.inventoryUsage||s.inventoryConsumption||[]), bookingPaymentPolicy:getBookingPaymentPolicyEditorValue(s), priceType:normalizePriceType(s), pricingType:normalizePriceType(s), priceLabel:s.priceLabel||s.publicPriceLabel||"Solicitar cotización" }); setShowEditAdvanced(false); setEditOpen(true); };
  const save=async()=>{ if(!editing || activation.checking || saving) return; const validation=validateServiceBasics({...editing,serviceKind:isEventsBusiness ? "event_package" : editing.serviceKind});if(validation)return alert(validation); if(serializeBookingPaymentPolicy(editing).enabled && !(await activation.request())) return; setSaving(true); const currentPriceType=normalizePriceType(editing); const payload={sessionPackage:editing.sessionPackage || {enabled:false},name:String(editing.name||"").trim(), description:normalizeServiceDescription(editing.description), duration:Number(editing.duration||30), price:currentPriceType==="quote"?null:(editing.price===""?null:Number(editing.price)), priceType:currentPriceType, pricingType:currentPriceType, priceLabel:currentPriceType==="quote"?"Solicitar cotización":"", publicPriceLabel:currentPriceType==="quote"?"Solicitar cotización":"", featured:!!editing.featured, disabled:!!editing.disabled,  minHours:Number(editing.minHours||1), maxHours:Number(editing.maxHours||3), pricingMode:editing.pricingMode==="duration_tiers"?"duration_tiers":"proportional", durationPricingTiers:normalizeDurationPricingTiers(editing.durationPricingTiers,editing.minHours,editing.maxHours,editing.price), characters:parseCharacters(editing.characterRows), visitTypeOptions:normalizeExperiencePricingOptions(editing.visitTypeOptions,editing.price), allowConcurrentSameService:!!editing.allowConcurrentSameService, sameServiceCanOverlap:!!editing.allowConcurrentSameService, uniqueResource:!editing.allowConcurrentSameService,maxConcurrentSameService:Number(editing.maxConcurrentSameService||1), serviceIconKey:editing.serviceIconKey||editing.iconKey||"auto", iconKey:editing.serviceIconKey||editing.iconKey||"auto", servicePresentationMode:editing.servicePresentationMode||editing.presentationMode||"icon", presentationMode:editing.servicePresentationMode||editing.presentationMode||"icon", serviceKind:isEventsBusiness?"event_package":"standard_service", experienceType:editing.experienceType||"show", eventExperienceType:editing.experienceType||"show", charactersMode:editing.charactersMode||"customer_selects", includedCharacters:String(editing.includedCharactersText || "").split(",").map(x=>x.trim()).filter(Boolean),...eventScheduleSettings(editing),extras:normalizeExtrasRows(editing.extrasRows), optionalExtras:normalizeExtrasRows(editing.extrasRows), inventoryUsage:serviceInventoryUsageRows(editing.inventoryUsage), inventoryConsumption:serviceInventoryUsageRows(editing.inventoryUsage), materialItemsCount:serviceInventoryUsageRows(editing.inventoryUsage).length, materialCostAmount:serviceInventoryUsageCost(editing.inventoryUsage, inventoryItems), bookingPaymentPolicy:serializeBookingPaymentPolicy(editing), updatedAt:loopBusinessNow()}; try{ if(businessId&&editing.id&&!String(editing.id).startsWith('tmp-')) { if(typeof onSaveService === "function") await onSaveService(editing.id,payload); else await loopBusinessUpdateDoc(defaultDb,`businesses/${businessId}/services/${editing.id}`,payload); } setLocalServices(prev=>{const next=prev.map(s=>s.id===editing.id?{...s,...payload}:s); onServicesChange&&onServicesChange(next); return next;}); setEditOpen(false); setEditing(null); }catch(e){console.error(e); alert('No se pudo guardar servicio.');} finally{setSaving(false);} };
  const del=async()=>{ if(!editing||!window.confirm('¿Eliminar servicio?')) return; await onDeleteService(editing.id); setLocalServices(prev=>prev.filter(s=>s.id!==editing.id)); setEditOpen(false); setEditing(null); };

  const externalSaveSignalRef=useRef(externalSaveSignal);
  useEffect(()=>{
    if(externalSaveSignalRef.current===externalSaveSignal) return;
    externalSaveSignalRef.current=externalSaveSignal;
    if(!externalSaveSignal) return;
    if(editOpen&&editing){ save(); return; }
    if(addOpen&&String(newSvc.name||"").trim()){ add(); return; }
    try{ console.info("[ServicesSection] external save ignored: no open service editor"); }catch(_){}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[externalSaveSignal]);

  const toggleServiceDisabled=async(service)=>{
    if(!service) return;
    const nextDisabled=!service.disabled;
    if(isEventsBusiness && !nextDisabled && isServiceMissingPrice(service)){
      openEdit(service);
      alert('Revisa las tarifas del paquete antes de mostrarlo. Define un precio, elige Cotización o confirma que se ofrece sin costo.');
      return;
    }
    const payload={disabled:nextDisabled,active:!nextDisabled,visible:!nextDisabled,updatedAt:loopBusinessNow()};
    try{
      if(businessId&&service.id&&!String(service.id).startsWith('tmp-')) {
        await loopBusinessUpdateDoc(defaultDb,`businesses/${businessId}/services/${service.id}`,payload);
      }
      setLocalServices(prev=>{
        const next=prev.map(s=>s.id===service.id?{...s,...payload}:s);
        onServicesChange&&onServicesChange(next);
        return next;
      });
    }catch(e){
      console.error(e);
      alert('No se pudo cambiar la visibilidad del servicio.');
    }
  };



  const orderedServices = useMemo(() => normalizeServicesWithOrder(localServices), [localServices]);
  const canReorderServices = serviceFilter === "all" && !String(searchTerm || "").trim();

  const persistServiceOrder = async (nextList = []) => {
    if (!businessId) return;

    const normalized = assignServiceOrderSequence(nextList);
    try {
      await loopBusinessBulkUpdateCollection(defaultDb, `businesses/${businessId}/services`, (service) => {
        const idx = normalized.findIndex((item) => String(item?.id) === String(service?.id));
        if (idx < 0 || !service?.id || String(service.id).startsWith("tmp-")) return null;
        return {
          serviceOrder: idx,
          publicOrder: idx,
          displayOrder: idx,
          order: idx,
          updatedAt: loopBusinessNow(),
        };
      });
    } catch (err) {
      console.warn("[ServicesSection] Could not persist service order", err);
      await Promise.all(normalized.map((service, idx) => {
        if (!service?.id || String(service.id).startsWith("tmp-")) return Promise.resolve();
        return loopBusinessUpdateDoc(defaultDb, `businesses/${businessId}/services/${service.id}`, {
          serviceOrder: idx,
          publicOrder: idx,
          displayOrder: idx,
          order: idx,
          updatedAt: loopBusinessNow(),
        });
      }));
    }
  };

  const moveService = async (serviceId, direction = 0) => {
    if (!canReorderServices || !serviceId || !direction) return;

    // Important:
    // current is the already sorted public list.
    // After moving, we must NOT call normalizeServicesWithOrder(next),
    // because that would sort again using the old order and undo the move.
    const current = assignServiceOrderSequence(orderedServices);
    const fromIndex = current.findIndex((item) => String(item.id) === String(serviceId));
    if (fromIndex < 0) return;

    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= current.length) return;

    const next = current.slice();
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);

    const normalized = assignServiceOrderSequence(next);
    setLocalServices(normalized);
    onServicesChange&&onServicesChange(normalized);

    try {
      await persistServiceOrder(normalized);
    } catch (err) {
      console.error("[ServicesSection] Error saving service order", err);
      alert("No se pudo guardar el nuevo orden. Intenta nuevamente.");
      setLocalServices(current);
      onServicesChange&&onServicesChange(current);
    }
  };

  const visibleServices = localServices.filter((svc) => !svc.disabled).length;
  const hiddenServices = Math.max(0, localServices.length - visibleServices);
  const missingPriceServices = localServices.filter((svc) => isServiceMissingPrice(svc)).length;
  const missingDurationServices = localServices.filter((svc) => !Number(svc.duration || svc.durationMinutes || 0)).length;

  const serviceNoun = isEventsBusiness ? "paquete" : "servicio";
  const serviceNounPlural = isEventsBusiness ? "paquetes" : "servicios";
  const pageTitle = isEventsBusiness ? "Paquetes y experiencias" : "Menú de reservas";
  const pageSubtitle = isEventsBusiness
    ? "Crea paquetes claros para eventos: experiencia, personajes, extras y precio base."
    : "Administra nombre, duración, precio, visibilidad y materiales desde una vista más limpia.";

  const filteredServices = useMemo(() => {
    const q = String(searchTerm || "").trim().toLowerCase();
    return orderedServices.filter((svc) => {
      const priceMissing = isServiceMissingPrice(svc);
      const durationMissing = !Number(svc.duration || svc.durationMinutes || 0);
      const isHidden = !!svc.disabled;

      if (serviceFilter === "active" && isHidden) return false;
      if (serviceFilter === "hidden" && !isHidden) return false;
      if (serviceFilter === "missingPrice" && !priceMissing) return false;
      if (serviceFilter === "missingDuration" && !durationMissing) return false;

      if (!q) return true;
      const haystack = [svc.name, svc.title, svc.label, svc.description, svc.serviceIconKey, svc.iconKey]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [orderedServices, searchTerm, serviceFilter]);

  const openAddModal = () => {
    setShowAddAdvanced(false);
    setAddOpen(true);
  };

  const renderServiceRow = (svc, index) => {
    if(isEventsBusiness)return <EventCatalogRow key={svc.id} service={svc} index={index} reordering={reordering} canMoveUp={canReorderServices && index>0} canMoveDown={canReorderServices && index<orderedServices.length-1} onMove={direction=>moveService(svc.id,direction)} onEdit={()=>openEdit(svc)} onVisibility={()=>toggleServiceDisabled(svc)}/>;
    const isVisible = !svc.disabled;
    const repeatable = isServiceRepeatable(svc);
    const priceLabel = isEventsBusiness ? formatEventPackagePrice(svc, region) : formatServicePrice(svc, region);
    const adminPriceLabel = !isEventsBusiness && isServiceMissingPrice(svc) ? "Por definir" : priceLabel;
    const durationLabel = getServiceDurationLabel(svc, isEventsBusiness);
    const presentationLabel = getServiceIconGlyph(svc);
    const materialSummary = serviceMaterialSummary(svc, inventoryItems);
    const serviceDescription = normalizeServiceDescription(svc.description || svc.subtitle || svc.details || svc.tagline || "");
    const hasWarnings = isServiceMissingPrice(svc) || durationLabel === "Sin duración";

    return (
      <article
        key={svc.id}
        className={`svc-op-row ${!isVisible ? "is-hidden" : ""}`}
        onClick={() => openEdit(svc)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openEdit(svc);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Editar ${svc.name || (isEventsBusiness ? "paquete" : "servicio")}`}
        title={t("Abrir editor del servicio")}
      >
        <div className="svc-op-icon" aria-hidden="true">
          {(getServicePresentationMode(svc) === "image" || useImages) && svc.imageUrl ? <img src={svc.imageUrl} alt="" /> : <ServiceIconVisual service={svc} compact />}
        </div>

        <div className="svc-op-main">
          <strong>{svc.name || (isEventsBusiness ? "Paquete" : "Servicio")}</strong>
          {svc.sessionPackage?.enabled && <span className="session-status">{t("Paquete ·") + " "}{svc.sessionPackage.totalSessions}{" " + t("sesiones")}</span>}
          {serviceDescription ? <small className="svc-op-description">{serviceDescription}</small> : null}
          {isEventsBusiness ? (
            <span className="svc-op-meta">{normalizeExperiencePricingOptions(svc.visitTypeOptions, svc.price).filter((x) => x.enabled).length}{" " + t("experiencias ·") + " "}{Array.isArray(svc.characters) ? svc.characters.length : 0}{" " + t("personajes") + " "}{svc.charactersMode === "included" ? "incluidos" : "opcionales"} · {Array.isArray(svc.extras || svc.optionalExtras) ? (svc.extras || svc.optionalExtras).length : 0}{" " + t("extras")}</span>
          ) : (
            <span className="svc-op-meta">{isVisible ? (svc.sessionPackage?.enabled ? "Sesiones agendadas por el negocio" : t("Reservable online")) : "No aparece en el perfil público"}{repeatable ? " · Repetible" : " · Único"} · {formatBookingPaymentPolicySummary(svc)}</span>
          )}
        </div>

        <div className="svc-op-value"><span>{svc.sessionPackage?.enabled ? "Por sesión" : t("Duración")}</span><strong>{durationLabel}</strong></div>
        <div className={`svc-op-value svc-op-price ${isServiceMissingPrice(svc) ? "is-missing" : ""}`}><span>{svc.sessionPackage?.enabled ? "Paquete completo" : t("Precio")}</span>{isEventsBusiness ? <EventTariffList service={svc}/> : <strong>{adminPriceLabel}</strong>}</div>
        <div className={`svc-op-value svc-op-materials ${materialSummary.hasMaterials ? "has-materials" : "no-materials"}`}>
          <span>{isEventsBusiness ? "Consumibles" : t("Materiales")}</span>
          <strong>
            {materialSummary.hasMaterials
              ? `${materialSummary.count} ${materialSummary.count === 1 ? "material" : "materiales"}`
              : isEventsBusiness ? "No configurados" : "Sin conectar"}
          </strong>
          {materialSummary.hasMaterials ? (
            <em>
              {materialSummary.marginPct != null
                ? `Costo $${materialSummary.materialCost.toFixed(2)} · Margen ${materialSummary.marginPct}%`
                : `Costo $${materialSummary.materialCost.toFixed(2)}`}
            </em>
          ) : null}
        </div>

        <div className="svc-op-actions" onClick={(e) => e.stopPropagation()}><button type="button" onClick={() => openEdit(svc)}>{t("Editar")}</button>
          <div className={`svc-op-status ${isVisible ? "on" : "off"}`}>
            <i />
            {isVisible ? t("Activo") : t("Oculto")}
          </div>

          {hasWarnings ? <div className="svc-op-warning">{t("Revisar configuración")}</div> : null}

          <div className="svc-order-controls" aria-label={`Orden público: posición ${index + 1}`}>
            <span>{t("Orden") + " "}{index + 1}</span>
            <div>
              <button
                type="button"
                disabled={!canReorderServices || index === 0}
                onClick={() => moveService(svc.id, -1)}
                aria-label={t("Subir servicio")}
                title={canReorderServices ? "Subir en el perfil público" : "Limpia filtros para ordenar"}
              >
                ↑
              </button>
              <button
                type="button"
                disabled={!canReorderServices || index === orderedServices.length - 1}
                onClick={() => moveService(svc.id, 1)}
                aria-label={t("Bajar servicio")}
                title={canReorderServices ? "Bajar en el perfil público" : "Limpia filtros para ordenar"}
              >
                ↓
              </button>
            </div>
          </div>

          <button
            type="button"
            className="svc-op-visibility"
            onClick={(e) => {
              e.stopPropagation();
              toggleServiceDisabled(svc);
            }}
          >
            {isVisible ? "Ocultar" : "Activar"}
          </button>
        </div>
      </article>
    );
  };

  return (
    <div className={`svc-page svc-minimal-page ${isEventsBusiness ? "svc-events-mode event-catalog-redesign" : "svc-services-mode"} ${reordering ? "is-reordering" : ""}`}>
      <style>{styles}</style>
      {!addOpen && !editOpen && activation.dialog}

      <section className="svc-minimal-shell">
        {isEventsBusiness&&businessId&&<div hidden={!showResources} className="event-catalog-resources"><EventResourcesEditor key={businessId} businessId={businessId} onLoaded={setEventResources}/></div>}
        <div className="svc-minimal-header">
          <div>
            <span>{isEventsBusiness ? "Catálogo de experiencias" : t("Servicios")}</span>
            <h3>{isEventsBusiness ? `Paquetes · ${localServices.length}` : pageTitle}</h3>
            <p>{pageSubtitle}</p>
          </div>
          <button type="button" className="svc-minimal-primary" onClick={openAddModal} disabled={savingAdd || disableAdd}>
            {savingAdd ? "Agregando..." : `Agregar ${serviceNoun}`}
          </button>
        </div>


        {isEventsBusiness&&<div className="event-catalog-toolbar">
         <input aria-label={t("Buscar paquete")} value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} placeholder={t("Buscar paquete…")}/>
         <select aria-label={t("Filtrar paquetes")} value={serviceFilter} onChange={e=>setServiceFilter(e.target.value)}><option value="all">{t("Todos ·") + " "}{localServices.length}</option><option value="active">{t("Visibles ·") + " "}{visibleServices}</option><option value="hidden">{t("Ocultos ·") + " "}{hiddenServices}</option><option value="missingPrice">{t("Por revisar ·") + " "}{missingPriceServices}</option></select>
         <div className="event-catalog-tools" onBlur={e=>{if(!e.currentTarget.contains(e.relatedTarget))setCatalogTools(false);}} onKeyDown={e=>{if(e.key==='Escape'){setCatalogTools(false);e.currentTarget.querySelector('button')?.focus();}}}><button type="button" aria-expanded={catalogTools} onClick={()=>setCatalogTools(!catalogTools)}>{t("Más opciones")}</button>{catalogTools&&<div className="event-catalog-popover"><button type="button" onClick={()=>{setSearchTerm('');setServiceFilter('all');setReordering(!reordering);setCatalogTools(false);}}>{reordering?t("Terminar de ordenar"):'Reordenar paquetes'}</button><button type="button" disabled={loadingUseImages} onClick={()=>toggleUseImages(!useImages)}>{t("Perfil público:") + " "}{useImages?'tarjetas':'lista'}</button><button type="button" onClick={()=>{setShowResources(!showResources);setCatalogTools(false);}}>{showResources?'Cerrar disfraces':'Disfraces y disponibilidad'}</button></div>}</div>
         {reordering&&<button type="button" className="event-order-done" onClick={()=>setReordering(false)}>{t("Terminar de ordenar")}</button>}
        </div>}

        <div className="svc-minimal-toolbar">
          <label className="svc-minimal-search">
            <span>{t("Buscar")}</span>
            <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={`Buscar ${serviceNoun}...`} />
          </label>

          <div className="svc-minimal-filters" role="tablist" aria-label={t("Filtro de servicios")}>
            <button type="button" className={serviceFilter === "all" ? "active" : ""} onClick={() => setServiceFilter("all")}>{t("Todos")}</button>
            <button type="button" className={serviceFilter === "active" ? "active" : ""} onClick={() => setServiceFilter("active")}>{t("Activos")}</button>
            <button type="button" className={serviceFilter === "hidden" ? "active" : ""} onClick={() => setServiceFilter("hidden")}>{t("Ocultos")}</button>
            <button type="button" className={serviceFilter === "missingPrice" ? "active" : ""} onClick={() => setServiceFilter("missingPrice")}>{isEventsBusiness ? t("Por revisar") : "Sin precio"}</button>
          </div>

          <button
            type="button"
            className={`svc-minimal-mode ${useImages ? "visual" : "simple"}`}
            onClick={() => toggleUseImages(!useImages)}
            disabled={loadingUseImages}
            title={t("Cambiar presentación pública")}
          >
            {useImages ? "Tarjetas" : "Lista"}
          </button>
        </div>

        <div className="svc-order-note">
          <button type="button" aria-pressed={reordering} onClick={()=>setReordering(!reordering)}>{reordering ? t("Terminar de ordenar") : "Reordenar paquetes"}</button>
          <span>{t("El orden se refleja en tu perfil público.")}</span>
          {!canReorderServices ? <em>{t("Para ordenar, usa el filtro “Todos” y limpia la búsqueda.")}</em> : null}
        </div>

        {isEventsBusiness && missingPriceServices > 0 && <button type="button" className="event-catalog-attention" onClick={()=>setServiceFilter("missingPrice")}><strong>{missingPriceServices}{" " + t("paquetes requieren revisar tarifas")}</strong><span>{t("Revisa precios pendientes o en cero antes de ofrecerlos. Ver paquetes →")}</span></button>}
        <div className="svc-minimal-summary" aria-label={t("Resumen de servicios")}>
          <span><strong>{localServices.length}</strong> {serviceNounPlural}</span>
          <span><strong>{visibleServices}</strong>{" " + t("activos")}</span>
          {hiddenServices ? <span><strong>{hiddenServices}</strong>{" " + t("ocultos")}</span> : null}
          {missingPriceServices ? <span className="warn"><strong>{missingPriceServices}</strong>{" " + t("sin precio")}</span> : null}
          {missingDurationServices ? <span className="warn"><strong>{missingDurationServices}</strong>{" " + t("sin duración")}</span> : null}
          <span className="materials"><strong>{localServices.filter((svc) => serviceMaterialSummary(svc, inventoryItems).hasMaterials).length}</strong>{" " + t("con materiales")}</span>
        </div>

        <div className="svc-op-table" role="list">
          {filteredServices.length === 0 ? (
            <div className="svc-op-empty">
              <strong>{localServices.length ? "No hay resultados con este filtro." : copy.empty}</strong>
              <span>{localServices.length ? "Prueba otro filtro o busca por otro nombre." : `Agrega tu primer ${serviceNoun} para comenzar.`}</span>
              {!localServices.length ? <button type="button" onClick={openAddModal}>{`Agregar ${serviceNoun}`}</button> : null}
            </div>
          ) : (
            filteredServices.map(renderServiceRow)
          )}
        </div>
      </section>

      <Sheet
        open={active && addOpen}
        title={copy.addTitle}
        onClose={() => setAddOpen(false)}
        footer={
          <div className="svc-actions">
            <button onClick={() => setAddOpen(false)}>{t("Cancelar")}</button>
            <button className="primary" onClick={add} disabled={savingAdd}>{savingAdd ? "Agregando..." : "Agregar"}</button>
          </div>
        }
      >
        {isEventsBusiness ? <>{activation.dialog}<EventPackageEditor value={newSvc} onChange={setNewSvc} resources={eventResources} inventoryItems={inventoryItems} requestActivation={activation.request} busy={savingAdd} /></> : (<div className="svc-form svc-minimal-editor">{activation.dialog}
          <ServicePresentationEditor
            value={newSvc}
            onChange={(next) => setNewSvc(next)}
            canUploadImage={false}
            isEventsBusiness={isEventsBusiness}
            nameLabel={isEventsBusiness ? "Nombre del paquete" : "Nombre del servicio"}
            namePlaceholder={copy.namePlaceholder}
          />

          {!isEventsBusiness ? (
            <BookingPaymentPolicyEditor
              value={newSvc}
              requestActivation={activation.request}
              onChange={(next) => setNewSvc(next)}
            />
          ) : null}

          {isEventsBusiness ? (
            <EventPackageEssentials value={newSvc} onChange={(next) => setNewSvc(next)} resources={eventResources} />
          ) : null}

          <InventoryUsageEditor value={newSvc.inventoryUsage} service={newSvc} inventoryItems={inventoryItems} onChange={(rows) => setNewSvc((v) => ({ ...v, inventoryUsage: rows }))} disabled={savingAdd} />

          <button type="button" className="svc-show-advanced" onClick={() => setShowAddAdvanced((v) => !v)}>
            {showAddAdvanced ? "Ocultar opciones avanzadas" : "Mostrar opciones avanzadas"}
          </button>

          {showAddAdvanced ? (
            <section className="svc-editor-section compact advanced">
              {isEventsBusiness ? (
                <div className="svc-event-config minimal">
                  <div className="svc-two">
                    <Field label={t("Horas mín.")}>
                      <input type="number" min="0.5" step="0.5" value={newSvc.minHours} onChange={(e) => setNewSvc((v) => ({ ...v, minHours: Number(e.target.value || 1) }))} />
                    </Field>
                    <Field label={t("Horas máx.")}>
                      <input type="number" min="0.5" step="0.5" value={newSvc.maxHours} onChange={(e) => setNewSvc((v) => ({ ...v, maxHours: Number(e.target.value || 3) }))} />
                    </Field>
                  </div>
                  <DurationPricingEditor pricingMode={newSvc.pricingMode} tiers={newSvc.durationPricingTiers} minHours={newSvc.minHours} maxHours={newSvc.maxHours} basePrice={newSvc.price} onModeChange={(mode) => setNewSvc((v) => ({ ...v, pricingMode: mode }))} onTiersChange={(tiers) => setNewSvc((v) => ({ ...v, durationPricingTiers: tiers }))} />
                  <EventConcurrencyChoice repeatable={!!newSvc.allowConcurrentSameService} maxConcurrent={newSvc.maxConcurrentSameService || 1} onChange={(value) => setNewSvc((v) => ({ ...v, allowConcurrentSameService: value }))} onMaxConcurrentChange={(value) => setNewSvc((v) => ({ ...v, maxConcurrentSameService: value }))} />
                </div>
              ) : null}
            </section>
          ) : null}
        </div>)}
      </Sheet>

      <Sheet
        open={active && editOpen && !!editing}
        title={copy.editTitle}
        onClose={() => setEditOpen(false)}
        dangerAction={isEventsBusiness ? <details className="event-delete-menu"><summary>{t("Más opciones")}</summary><button type="button" className="svc-delete-link" onClick={del}>{t("Eliminar paquete")}</button></details> : <button type="button" className="svc-delete-link" onClick={del}>{t("Eliminar servicio")}</button>}
        footer={
          <div className="svc-actions">
            <button onClick={() => setEditOpen(false)}>{t("Cancelar")}</button>
            <button className="primary" onClick={save} disabled={saving}>{saving ? t("Guardando...") : t("Guardar")}</button>
          </div>
        }
      >
        {editing ? (
          (isEventsBusiness ? <>{activation.dialog}<EventPackageEditor value={editing} onChange={setEditing} resources={eventResources} inventoryItems={inventoryItems} requestActivation={activation.request} busy={saving} canUploadImage={!!editing.id && !String(editing.id).startsWith("tmp-")} onUploadImage={()=>uploadImage(editing.id)}/></> : (<div className="svc-form svc-minimal-editor">{activation.dialog}
            <ServicePresentationEditor
              value={editing}
              onChange={(next) => setEditing(next)}
              canUploadImage={!!editing.id && !String(editing.id).startsWith("tmp-")}
              onUploadImage={() => uploadImage(editing.id)}
              isEventsBusiness={isEventsBusiness}
              nameLabel={isEventsBusiness ? "Nombre del paquete" : "Nombre del servicio"}
              namePlaceholder={copy.namePlaceholder}
            />

            {!isEventsBusiness ? (
              <BookingPaymentPolicyEditor
                value={editing}
                requestActivation={activation.request}
                onChange={(next) => setEditing(next)}
              />
            ) : null}

            {isEventsBusiness ? (
              <EventPackageEssentials value={editing} onChange={(next) => setEditing(next)} resources={eventResources} />
            ) : null}

            <InventoryUsageEditor value={editing.inventoryUsage} service={editing} inventoryItems={inventoryItems} onChange={(rows) => setEditing((v) => ({ ...v, inventoryUsage: rows }))} disabled={saving} />

            <button type="button" className="svc-show-advanced" onClick={() => setShowEditAdvanced((v) => !v)}>
              {showEditAdvanced ? "Ocultar opciones avanzadas" : "Mostrar opciones avanzadas"}
            </button>

            {showEditAdvanced ? (
              <section className="svc-editor-section compact advanced">
                {isEventsBusiness ? (
                  <div className="svc-event-config minimal">
                    <div className="svc-two">
                      <Field label={t("Horas mín.")}>
                        <input type="number" min="0.5" step="0.5" value={Number(editing.minHours || 1)} onChange={(e) => setEditing((v) => ({ ...v, minHours: Number(e.target.value || 1) }))} />
                      </Field>
                      <Field label={t("Horas máx.")}>
                        <input type="number" min="0.5" step="0.5" value={Number(editing.maxHours || 3)} onChange={(e) => setEditing((v) => ({ ...v, maxHours: Number(e.target.value || 3) }))} />
                      </Field>
                    </div>

                    <DurationPricingEditor pricingMode={editing.pricingMode} tiers={editing.durationPricingTiers} minHours={editing.minHours} maxHours={editing.maxHours} basePrice={editing.price} onModeChange={(mode) => setEditing((v) => ({ ...v, pricingMode: mode }))} onTiersChange={(tiers) => setEditing((v) => ({ ...v, durationPricingTiers: tiers }))} />
                    <EventConcurrencyChoice repeatable={!!editing.allowConcurrentSameService} maxConcurrent={editing.maxConcurrentSameService || 1} onChange={(value) => setEditing((v) => ({ ...v, allowConcurrentSameService: value }))} onMaxConcurrentChange={(value) => setEditing((v) => ({ ...v, maxConcurrentSameService: value }))} />
                  </div>
                ) : null}
              </section>
            ) : null}
          </div>))
        ) : null}
      </Sheet>
    </div>
  );
}
const styles=`

.svc-concurrency-block{border:1px solid #edf2f7;background:#f8fafc;border-radius:24px;padding:16px;display:grid;gap:13px}
.svc-concurrency-head strong{display:block;color:#0f172a;font-size:15px;font-weight:950;letter-spacing:-.02em}
.svc-concurrency-head span{display:block;color:#64748b;font-size:12px;line-height:1.4;font-weight:750;margin-top:4px}
.svc-concurrency-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
.svc-concurrency-card{border:1px solid #e2e8f0;background:#fff;border-radius:22px;padding:15px;text-align:left;display:flex;gap:12px;align-items:flex-start;cursor:pointer;transition:.16s ease}
.svc-concurrency-card:hover{transform:translateY(-1px);border-color:#bfd5df}
.svc-concurrency-card.active{border-color:rgba(0,103,95,.34);background:rgba(0,103,95,.06);box-shadow:0 12px 26px rgba(0,103,95,.08)}
.svc-concurrency-icon{font-size:22px;line-height:1}
.svc-concurrency-card strong{display:block;color:#0f172a;font-size:14px;font-weight:950}
.svc-concurrency-card span{display:block;color:#64748b;font-size:12px;line-height:1.35;font-weight:730;margin-top:4px}
@media(max-width:760px){.svc-concurrency-grid{grid-template-columns:1fr}}

.svc-settings-flat{grid-template-columns:repeat(3,minmax(0,1fr))!important;align-items:stretch}.svc-toggle{min-height:94px!important;text-align:left!important;padding:18px!important}.svc-toggle strong{font-size:16px!important}.svc-toggle span{line-height:1.35!important}.svc-field input,.svc-field select,.svc-field textarea{border-radius:16px!important;min-height:46px!important}.svc-sheet{width:min(980px,calc(100vw - 28px))!important}.svc-sheet-body{padding:22px!important}.svc-card-item{border-radius:24px!important}.svc-empty{border-radius:24px!important;background:#f8fafc!important;border:1px dashed #cbd5e1!important;padding:28px!important;font-weight:900!important;color:#64748b!important}@media(max-width:900px){.svc-settings-flat{grid-template-columns:1fr!important}.svc-sheet{width:100vw!important;max-width:100vw!important;border-radius:24px 24px 0 0!important}}
.svc-page{display:grid;gap:14px}.svc-hero-mini,.svc-settings-flat,.svc-list-flat{border-radius:30px;border:1px solid rgba(226,232,240,.96);background:rgba(255,255,255,.96);box-shadow:0 18px 48px rgba(15,23,42,.07);padding:18px}.svc-hero-mini{display:flex;justify-content:space-between;gap:14px;align-items:center}.svc-hero-mini span{color:#00675f;font-size:11px;font-weight:950;letter-spacing:.12em;text-transform:uppercase}.svc-hero-mini h3{margin:7px 0 0;font-size:30px;line-height:.95;letter-spacing:-.06em;font-weight:950}.svc-hero-mini p{margin:8px 0 0;color:#64748b;font-size:13px;font-weight:750}.svc-hero-mini button,.svc-actions .primary{min-height:44px;border:0;border-radius:16px;background:linear-gradient(135deg,#00675f,#269690);color:#fff;font-weight:950;padding:0 16px;box-shadow:0 12px 26px rgba(0,103,95,.20);cursor:pointer}.svc-hero-mini button:disabled{opacity:.55;cursor:not-allowed}.svc-hero-mini button:not(:disabled){opacity:1!important;cursor:pointer!important}.svc-settings-flat{display:grid;grid-template-columns:minmax(0,1fr);gap:12px}.svc-toggle{border-radius:22px;border:1px solid #e2e8f0;background:#fff;padding:14px;text-align:left;cursor:pointer}.svc-toggle.active{border-color:rgba(0,103,95,.28);background:rgba(0,103,95,.06)}.svc-toggle strong{display:block;font-weight:950}.svc-toggle span{display:block;margin-top:4px;color:#64748b;font-size:12px;font-weight:750}.svc-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(285px,1fr));gap:12px}.svc-card-item{border:1px solid #e2e8f0;background:#fff;border-radius:24px;padding:12px;display:grid;grid-template-columns:104px 1fr;gap:12px;cursor:pointer;transition:160ms ease}.svc-card-item:hover{transform:translateY(-2px);box-shadow:0 18px 42px rgba(15,23,42,.09);border-color:rgba(0,103,95,.20)}.svc-photo{height:92px;border-radius:18px;background:#f1f5f9;overflow:hidden;position:relative;display:grid;place-items:center;color:#94a3b8;font-size:11px;font-weight:850}.svc-photo img{width:100%;height:100%;object-fit:cover}.svc-photo button{position:absolute;left:7px;right:7px;bottom:7px;min-height:28px;border:0;border-radius:999px;background:rgba(15,23,42,.58);color:#fff;font-size:11px;font-weight:950}.svc-info h4{margin:6px 0 5px;font-size:17px;line-height:1.08;letter-spacing:-.035em;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.svc-info p{margin:0;color:#64748b;font-size:12px;font-weight:800}.svc-badges{display:flex;gap:6px;min-height:22px}.svc-badges span{border-radius:999px;background:rgba(0,103,95,.08);color:#00675f;padding:5px 8px;font-size:10px;font-weight:950}.svc-badges .off{background:#fef2f2;color:#dc2626}.svc-empty{color:#64748b;font-size:13px;font-weight:750}.svc-field span{display:block;margin-bottom:7px;color:#334155;font-size:12px;font-weight:950}.svc-field input{width:100%;min-height:50px;border-radius:18px;border:1px solid #e2e8f0;padding:0 14px;box-sizing:border-box;font-weight:750}.svc-two{display:grid;grid-template-columns:1fr 1fr;gap:12px}.svc-form{display:grid;gap:12px}.svc-modal{position:fixed;inset:0;z-index:130;background:rgba(15,23,42,.56);padding:16px;overflow:auto}.svc-sheet{width:min(100%,620px);margin:24px auto;background:#fff;border-radius:30px;box-shadow:0 30px 90px rgba(15,23,42,.30);overflow:hidden}.svc-sheet-head{padding:18px;border-bottom:1px solid #f1f5f9;display:flex;justify-content:space-between;align-items:center}.svc-sheet-head h3{margin:0;font-size:22px;font-weight:950;letter-spacing:-.04em}.svc-sheet-head button{width:38px;height:38px;border:0;border-radius:999px;background:#f1f5f9;font-weight:950}.svc-sheet-body{padding:18px}.svc-sheet-foot{padding:14px 18px;border-top:1px solid #f1f5f9}.svc-actions{display:grid;grid-template-columns:1fr 1fr;gap:10px}.svc-actions button,.danger{min-height:44px;border-radius:16px;border:1px solid #e2e8f0;background:#fff;font-weight:950}.danger{border-color:#fecaca;background:#fef2f2;color:#dc2626}.svc-big-photo{height:180px;border-radius:24px;border:1px dashed rgba(0,103,95,.35);background:rgba(0,103,95,.055);overflow:hidden;color:#00675f;font-weight:950}.svc-big-photo img{width:100%;height:100%;object-fit:contain;background:#fff}@media(max-width:780px){.svc-hero-mini,.svc-settings-flat,.svc-list-flat{border-radius:24px;padding:15px}.svc-hero-mini{align-items:flex-start}.svc-hero-mini h3{font-size:25px}.svc-settings-flat,.svc-two{grid-template-columns:1fr}.svc-grid{grid-template-columns:1fr}.svc-card-item{grid-template-columns:92px 1fr}.svc-photo{height:86px}.svc-sheet{margin:10px auto;border-radius:28px}}

.svc-event-config{border:1px solid rgba(0,103,95,.16);background:rgba(0,103,95,.045);border-radius:24px;padding:12px;display:grid;gap:12px}

.svc-help-text{margin:0;color:#64748b;font-size:12px;font-weight:750;line-height:1.35}

.svc-modal{display:flex!important;align-items:flex-start!important;justify-content:center!important;padding:72px 18px 28px!important}
.svc-sheet{width:min(100%,920px)!important;margin:0 auto!important;max-height:calc(100dvh - 100px)!important;display:flex!important;flex-direction:column!important}
.svc-sheet-head{position:sticky!important;top:0!important;z-index:4!important;background:#fff!important}
.svc-sheet-body{overflow:auto!important;min-height:0!important}
.svc-sheet-foot{position:sticky!important;bottom:0!important;background:#fff!important;z-index:4!important}
.svc-event-config{border:1px solid rgba(0,103,95,.16);background:rgba(0,103,95,.045);border-radius:24px;padding:12px;display:grid;gap:12px}
.svc-character-editor{border:1px solid rgba(226,232,240,.95);background:#fff;border-radius:22px;padding:12px;display:grid;gap:10px}
.svc-character-editor-head{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:10px}
.svc-character-editor-head strong{font-size:13px;font-weight:950;color:#0f172a}
.svc-character-editor-head button{min-height:34px;border:0;border-radius:999px;background:#00675f;color:#fff;font-size:12px;font-weight:950;padding:0 12px}
.svc-character-row{display:grid;grid-template-columns:minmax(0,1fr) 130px 38px;gap:8px;align-items:center}
.svc-character-row input{min-width:0;width:100%;box-sizing:border-box;min-height:44px;border-radius:15px;border:1px solid #e2e8f0;padding:0 12px;font-weight:800}
.svc-character-row button{height:38px;border:0;border-radius:999px;background:#fef2f2;color:#dc2626;font-weight:950}
.svc-character-empty{border:1px dashed #cbd5e1;border-radius:16px;padding:12px;color:#64748b;font-size:12px;font-weight:750;background:#f8fafc}
.svc-help-text{margin:0;color:#64748b;font-size:12px;font-weight:750;line-height:1.35}
@media(max-width:720px){.svc-modal{padding:56px 10px 18px!important}.svc-sheet{max-height:calc(100dvh - 74px)!important}.svc-character-row{grid-template-columns:1fr}.svc-character-row button{width:100%}}

.svc-visit-editor{border:1px solid rgba(226,232,240,.95);background:#fff;border-radius:22px;padding:12px;display:grid;gap:10px}
.svc-character-editor-head span{color:#64748b;font-size:11px;font-weight:850}
.svc-visit-row{display:grid;grid-template-columns:78px minmax(0,1fr) 120px;gap:8px;align-items:center}
.svc-mini-toggle{min-height:40px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;color:#64748b;font-size:12px;font-weight:950}
.svc-mini-toggle.active{background:#ecfdf5;border-color:#99f6e4;color:#00675f}
.svc-visit-label{font-size:13px;font-weight:950;color:#0f172a}
.svc-visit-row input{min-height:42px;border-radius:15px;border:1px solid #e2e8f0;padding:0 12px;font-weight:850}
@media(max-width:720px){.svc-visit-row{grid-template-columns:1fr}.svc-visit-row input{width:100%}}

/* PREMIUM SERVICE SETTINGS REFINEMENT */
.svc-switch-setting{
  border:1px solid #e2e8f0;
  background:#fff;
  border-radius:24px;
  padding:16px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:16px;
}
.svc-switch-setting.active{
  border-color:rgba(0,103,95,.28);
  background:rgba(0,103,95,.055);
}
.svc-switch-setting strong{
  display:block;
  color:#0f172a;
  font-size:15px;
  font-weight:950;
}
.svc-switch-setting span{
  display:block;
  color:#64748b;
  font-size:12px;
  line-height:1.35;
  font-weight:750;
  margin-top:4px;
}
.svc-switch{
  width:56px;
  height:32px;
  border:0;
  border-radius:999px;
  background:#cbd5e1;
  padding:3px;
  cursor:pointer;
  flex:0 0 auto;
  transition:.16s ease;
}
.svc-switch i{
  display:block;
  width:26px;
  height:26px;
  border-radius:999px;
  background:#fff;
  box-shadow:0 3px 10px rgba(15,23,42,.16);
  transform:translateX(0);
  transition:.16s ease;
}
.svc-switch.on{
  background:#00675f;
}
.svc-switch.on i{
  transform:translateX(24px);
}
.svc-concurrency-repeatable{
  cursor:pointer;
}
.svc-concurrency-content{
  width:100%;
  min-width:0;
}
.svc-repeat-capacity{
  margin-top:12px;
  padding:12px;
  border-radius:18px;
  background:#fff;
  border:1px solid rgba(0,103,95,.16);
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
}
.svc-repeat-capacity label{
  color:#334155;
  font-size:12px;
  font-weight:950;
}
.svc-repeat-stepper{
  display:grid;
  grid-template-columns:34px 42px 34px;
  align-items:center;
  border:1px solid #e2e8f0;
  border-radius:999px;
  overflow:hidden;
  background:#f8fafc;
}
.svc-repeat-stepper button{
  width:34px;
  height:34px;
  border:0;
  background:#fff;
  color:#00675f;
  font-size:17px;
  font-weight:950;
}
.svc-repeat-stepper strong{
  text-align:center;
  font-size:14px!important;
}
.svc-big-photo{
  height:118px!important;
  max-width:360px;
  width:100%;
  justify-self:start;
  border-radius:22px!important;
  background:linear-gradient(135deg,#f8fafc,#ffffff)!important;
  display:grid;
  place-items:center;
  padding:10px;
}
.svc-big-photo img{
  width:100%!important;
  height:100%!important;
  object-fit:contain!important;
  background:#fff!important;
  border-radius:16px;
}
.svc-image-note{
  margin:-4px 0 4px;
  color:#64748b;
  font-size:12px;
  line-height:1.35;
  font-weight:750;
}
@media(max-width:720px){
  .svc-switch-setting{align-items:flex-start}
  .svc-repeat-capacity{display:grid}
  .svc-big-photo{max-width:100%;height:104px!important}
}


/* CONFIG SERVICES CLEANUP */
.svc-settings-flat{display:none!important}
.svc-card-item{position:relative;padding-right:118px!important;min-height:92px}
.svc-card-item.svc-card-hidden{opacity:.74;background:#f8fafc!important}
.svc-card-visibility{
  position:absolute;
  top:16px;
  right:16px;
  min-width:82px;
  height:34px;
  border-radius:999px;
  border:1px solid #e2e8f0;
  background:#fff;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:7px;
  font-size:11px;
  font-weight:950;
  cursor:pointer;
  color:#64748b;
  box-shadow:0 8px 18px rgba(15,23,42,.045);
}
.svc-card-visibility i{
  width:10px;
  height:10px;
  border-radius:999px;
  background:#94a3b8;
}
.svc-card-visibility.on{
  border-color:#99f6e4;
  background:#ecfdf5;
  color:#00675f;
}
.svc-card-visibility.on i{background:#10b981}
.svc-card-visibility.off{
  border-color:#fecaca;
  background:#fff7f7;
  color:#dc2626;
}
.svc-card-visibility.off i{background:#ef4444}
.svc-visibility-note{
  border:1px solid #e2e8f0;
  background:#f8fafc;
  border-radius:18px;
  padding:13px;
  display:grid;
  gap:4px;
}
.svc-visibility-note strong{
  color:#0f172a;
  font-size:13px;
  font-weight:950;
}
.svc-visibility-note span{
  color:#64748b;
  font-size:12px;
  font-weight:750;
  line-height:1.35;
}
@media(max-width:720px){
  .svc-card-item{padding-right:14px!important;padding-bottom:62px!important}
  .svc-card-visibility{top:auto;right:14px;bottom:14px}
}


/* DURATION PRICING TIERS */
.svc-duration-pricing-editor{border:1px solid rgba(226,232,240,.95);background:#fff;border-radius:22px;padding:12px;display:grid;gap:12px}
.svc-pricing-mode-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.svc-pricing-mode-card{border:1px solid #e2e8f0;background:#fff;border-radius:18px;padding:13px;text-align:left;cursor:pointer;transition:.16s ease}
.svc-pricing-mode-card.active{border-color:rgba(0,103,95,.34);background:rgba(0,103,95,.06);box-shadow:0 10px 24px rgba(0,103,95,.08)}
.svc-pricing-mode-card strong{display:block;color:#0f172a;font-size:13px;font-weight:950}
.svc-pricing-mode-card span{display:block;margin-top:4px;color:#64748b;font-size:12px;line-height:1.35;font-weight:750}
.svc-tier-table{display:grid;gap:8px}
.svc-tier-table-head,.svc-tier-row{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr) 38px;gap:8px;align-items:center}
.svc-tier-table-head span{color:#64748b;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.07em}
.svc-tier-row input{min-height:44px;border-radius:15px;border:1px solid #e2e8f0;padding:0 12px;font-weight:850;width:100%;box-sizing:border-box}
.svc-tier-row button{height:38px;border:0;border-radius:999px;background:#fef2f2;color:#dc2626;font-weight:950}
.svc-tier-add{min-height:40px;border:1px dashed rgba(0,103,95,.35);border-radius:16px;background:rgba(0,103,95,.045);color:#00675f;font-weight:950}
.svc-pricing-preview{border:1px solid #e2e8f0;background:#f8fafc;border-radius:18px;padding:13px;display:grid;gap:4px}
.svc-pricing-preview strong{color:#0f172a;font-size:13px;font-weight:950}
.svc-pricing-preview span{color:#00675f;font-size:13px;font-weight:950}
.svc-pricing-preview small{color:#64748b;font-size:12px;font-weight:750;line-height:1.35}
@media(max-width:720px){.svc-pricing-mode-grid{grid-template-columns:1fr}.svc-tier-table-head{display:none}.svc-tier-row{grid-template-columns:1fr}.svc-tier-row button{width:100%}}



/* =========================================================
   PHASE 31C — ServicesSection Premium Revamp
   ========================================================= */
.svc-premium-page{
  --svc-brand:#00675f;
  --svc-brand-2:#00897f;
  --svc-ink:#0f172a;
  --svc-muted:#64748b;
  --svc-line:#e5edf3;
  --svc-soft:#f8fafc;
  gap:18px!important;
}
.svc-premium-hero,
.svc-premium-control-grid,
.svc-premium-list-card{
  border:1px solid rgba(226,232,240,.96)!important;
  background:rgba(255,255,255,.96)!important;
  border-radius:30px!important;
  box-shadow:0 18px 50px rgba(15,23,42,.065)!important;
}
.svc-premium-hero{
  padding:22px!important;
  display:grid!important;
  grid-template-columns:minmax(0,1fr) auto!important;
  align-items:center!important;
  gap:20px!important;
  position:relative;
  overflow:hidden;
}
.svc-premium-hero::before{
  content:"";
  position:absolute;
  inset:-30% auto auto -10%;
  width:360px;
  height:360px;
  border-radius:999px;
  background:radial-gradient(circle,rgba(0,103,95,.13),transparent 64%);
  pointer-events:none;
}
.svc-premium-hero-copy{position:relative;z-index:1}
.svc-premium-hero-copy span,
.svc-premium-list-head span,
.svc-premium-control-card span{
  display:block;
  color:var(--svc-brand);
  font-size:11px;
  font-weight:950;
  letter-spacing:.13em;
  text-transform:uppercase;
}
.svc-premium-hero-copy h3{
  margin:8px 0 0!important;
  color:var(--svc-ink);
  font-size:34px!important;
  line-height:.95!important;
  letter-spacing:-.065em!important;
  font-weight:950!important;
}
.svc-premium-hero-copy p{
  max-width:650px;
  margin:10px 0 0!important;
  color:var(--svc-muted)!important;
  font-size:14px!important;
  line-height:1.45!important;
  font-weight:750!important;
}
.svc-premium-hero-actions{
  position:relative;
  z-index:1;
  display:flex;
  align-items:center;
  justify-content:flex-end;
  gap:10px;
  flex-wrap:wrap;
}
.svc-premium-metric{
  min-width:88px;
  min-height:60px;
  border:1px solid var(--svc-line);
  background:#fff;
  border-radius:20px;
  display:grid;
  place-items:center;
  align-content:center;
  gap:2px;
}
.svc-premium-metric strong{
  color:var(--svc-ink);
  font-size:22px;
  line-height:1;
  font-weight:950;
}
.svc-premium-metric span{
  color:var(--svc-muted);
  font-size:11px;
  font-weight:850;
}
.svc-premium-hero-actions>button,
.svc-premium-list-head>button,
.svc-premium-empty button{
  min-height:44px;
  border:0;
  border-radius:16px;
  background:linear-gradient(135deg,var(--svc-brand),var(--svc-brand-2));
  color:#fff;
  font-weight:950;
  padding:0 18px;
  box-shadow:0 14px 30px rgba(0,103,95,.18);
  cursor:pointer;
}
.svc-premium-hero-actions>button:disabled,
.svc-premium-list-head>button:disabled,
.svc-premium-empty button:disabled{opacity:.55;cursor:not-allowed}
.svc-premium-control-grid{
  padding:14px!important;
  display:grid!important;
  grid-template-columns:repeat(3,minmax(0,1fr))!important;
  gap:12px!important;
}
.svc-premium-control-card{
  border:1px solid var(--svc-line);
  background:linear-gradient(135deg,#fff,#fbfefd);
  border-radius:24px;
  padding:16px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:16px;
}
.svc-premium-control-card strong{
  display:block;
  margin-top:5px;
  color:var(--svc-ink);
  font-size:15px;
  font-weight:950;
}
.svc-premium-control-card p{
  margin:5px 0 0;
  color:var(--svc-muted);
  font-size:12px;
  line-height:1.35;
  font-weight:740;
}
.svc-premium-summary-card{
  background:linear-gradient(135deg,rgba(0,103,95,.07),#ffffff);
}
.svc-premium-list-card{
  padding:18px!important;
}
.svc-premium-list-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:18px;
  margin-bottom:16px;
}
.svc-premium-list-head h4{
  margin:6px 0 0;
  color:var(--svc-ink);
  font-size:24px;
  line-height:1;
  letter-spacing:-.045em;
  font-weight:950;
}
.svc-premium-list-head p{
  margin:7px 0 0;
  color:var(--svc-muted);
  font-size:13px;
  font-weight:780;
}
.svc-premium-grid{
  grid-template-columns:repeat(auto-fill,minmax(310px,1fr))!important;
  gap:14px!important;
}
.svc-premium-empty{
  border:1px dashed #cbd5e1;
  background:linear-gradient(135deg,#f8fafc,#fff);
  border-radius:26px;
  min-height:190px;
  display:grid;
  place-items:center;
  align-content:center;
  gap:8px;
  text-align:center;
  padding:24px;
}
.svc-premium-empty strong{color:var(--svc-ink);font-size:18px;font-weight:950}
.svc-premium-empty span{color:var(--svc-muted);font-size:13px;font-weight:760}
.svc-premium-page .svc-card-item{
  border-radius:26px!important;
  border:1px solid rgba(226,232,240,.96)!important;
  box-shadow:0 12px 30px rgba(15,23,42,.045)!important;
  transition:transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease!important;
}
.svc-premium-page .svc-card-item:hover{
  transform:translateY(-3px)!important;
  border-color:rgba(0,103,95,.24)!important;
  box-shadow:0 22px 50px rgba(15,23,42,.095)!important;
}
.svc-premium-page .svc-info h4{font-size:18px!important;letter-spacing:-.04em!important}
.svc-premium-page .svc-info p{line-height:1.35!important}
.svc-premium-page .svc-badges span{box-shadow:inset 0 0 0 1px rgba(0,103,95,.06)}
.svc-premium-page .svc-sheet-head h3::after{
  content:"Configura la información que verá el cliente al reservar.";
  display:block;
  margin-top:5px;
  color:#64748b;
  font-size:12px;
  line-height:1.35;
  letter-spacing:0;
  font-weight:750;
}
@media(max-width:980px){
  .svc-premium-hero{grid-template-columns:1fr!important;align-items:start!important}
  .svc-premium-hero-actions{justify-content:flex-start!important}
  .svc-premium-control-grid{grid-template-columns:1fr!important}
}
@media(max-width:720px){
  .svc-premium-hero,.svc-premium-control-grid,.svc-premium-list-card{border-radius:24px!important}
  .svc-premium-hero{padding:18px!important}
  .svc-premium-hero-copy h3{font-size:28px!important}
  .svc-premium-list-head{display:grid!important;align-items:start!important}
  .svc-premium-list-head>button{width:100%}
  .svc-premium-grid{grid-template-columns:1fr!important}
}

/* =========================================================
   PHASE 31C.2 — Full Catalog Strategy UX
   ========================================================= */
.svc-catalog-hero,
.svc-catalog-strategy,
.svc-catalog-health-grid,
.svc-catalog-list-card{
  border:1px solid rgba(226,232,240,.96)!important;
  background:rgba(255,255,255,.96)!important;
  border-radius:30px!important;
  box-shadow:0 18px 50px rgba(15,23,42,.065)!important;
}
.svc-catalog-hero{
  padding:24px!important;
  display:grid!important;
  grid-template-columns:minmax(0,1fr) auto!important;
  gap:18px!important;
  align-items:center!important;
  overflow:hidden;
  position:relative;
}
.svc-catalog-hero::before{
  content:"";
  position:absolute;
  width:420px;
  height:420px;
  left:-180px;
  top:-220px;
  border-radius:999px;
  background:radial-gradient(circle,rgba(0,103,95,.15),transparent 66%);
}
.svc-catalog-hero-copy{position:relative;z-index:1}
.svc-catalog-hero-copy span,
.svc-catalog-strategy-head span,
.svc-catalog-list-head span{
  display:block;
  color:#00675f;
  font-size:11px;
  font-weight:950;
  letter-spacing:.13em;
  text-transform:uppercase;
}
.svc-catalog-hero-copy h3{
  margin:8px 0 0!important;
  color:#0f172a;
  font-size:35px!important;
  line-height:.95!important;
  letter-spacing:-.065em!important;
  font-weight:950!important;
}
.svc-catalog-hero-copy p{
  max-width:720px;
  margin:10px 0 0!important;
  color:#64748b!important;
  font-size:14px!important;
  line-height:1.45!important;
  font-weight:750!important;
}
.svc-catalog-hero-actions{position:relative;z-index:1;display:flex;justify-content:flex-end}
.svc-catalog-primary{
  min-height:44px;
  border:0;
  border-radius:16px;
  background:linear-gradient(135deg,#00675f,#00897f);
  color:#fff;
  font-weight:950;
  padding:0 18px;
  box-shadow:0 14px 30px rgba(0,103,95,.18);
  cursor:pointer;
}
.svc-catalog-primary:disabled{opacity:.55;cursor:not-allowed}
.svc-catalog-strategy{padding:16px!important;display:grid!important;gap:14px!important}
.svc-catalog-strategy-head h4{
  margin:6px 0 0;
  color:#0f172a;
  font-size:21px;
  line-height:1;
  letter-spacing:-.045em;
  font-weight:950;
}
.svc-catalog-strategy-head p{
  margin:8px 0 0;
  color:#64748b;
  font-size:13px;
  line-height:1.45;
  font-weight:750;
}
.svc-catalog-mode-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.svc-catalog-mode-card{
  border:1px solid #e2e8f0;
  background:linear-gradient(135deg,#fff,#fbfefd);
  border-radius:24px;
  padding:16px;
  text-align:left;
  cursor:pointer;
  transition:.16s ease;
}
.svc-catalog-mode-card:hover{transform:translateY(-1px);border-color:rgba(0,103,95,.22)}
.svc-catalog-mode-card.active{border-color:rgba(0,103,95,.34);background:rgba(0,103,95,.055);box-shadow:0 12px 28px rgba(0,103,95,.08)}
.svc-catalog-mode-card strong{display:block;color:#0f172a;font-size:15px;font-weight:950}
.svc-catalog-mode-card span{display:block;color:#64748b;font-size:12px;line-height:1.4;font-weight:750;margin-top:5px}
.svc-catalog-mode-card em{display:inline-flex;margin-top:12px;border-radius:999px;padding:7px 10px;background:#f8fafc;color:#00675f;font-size:11px;font-style:normal;font-weight:950}
.svc-catalog-mode-card.active em{background:#00675f;color:#fff}
.svc-catalog-health-grid{padding:12px!important;display:grid!important;grid-template-columns:repeat(6,minmax(0,1fr))!important;gap:10px!important}
.svc-catalog-health-grid article{border:1px solid #e2e8f0;background:#fff;border-radius:20px;min-height:74px;display:grid;place-items:center;align-content:center;gap:4px}
.svc-catalog-health-grid strong{color:#0f172a;font-size:22px;line-height:1;font-weight:950}
.svc-catalog-health-grid span{color:#64748b;font-size:11px;font-weight:850;text-align:center}
.svc-catalog-list-card{padding:18px!important}
.svc-catalog-list-head{display:flex;align-items:center;justify-content:space-between;gap:18px;margin-bottom:16px}
.svc-catalog-list-head h4{margin:6px 0 0;color:#0f172a;font-size:24px;line-height:1;letter-spacing:-.045em;font-weight:950}
.svc-catalog-list-head p{max-width:720px;margin:7px 0 0;color:#64748b;font-size:13px;font-weight:780;line-height:1.42}
.svc-catalog-grid{grid-template-columns:repeat(auto-fill,minmax(330px,1fr))!important;gap:14px!important}
.svc-catalog-card{grid-template-columns:78px minmax(0,1fr)!important;padding:14px 118px 14px 14px!important;align-items:center!important;min-height:112px!important}
.svc-catalog-media{width:64px;height:64px;border-radius:20px;display:grid;place-items:center;overflow:hidden;background:linear-gradient(135deg,#e7fbf7,#ffffff);border:1px solid rgba(0,103,95,.12);color:#00675f;font-size:18px;font-weight:950;letter-spacing:-.04em}
.svc-catalog-media.visual{width:70px;height:70px;background:#f1f5f9;border-color:#e2e8f0}
.svc-catalog-media img{width:100%;height:100%;object-fit:cover;display:block}
.svc-catalog-info h4{margin-top:2px!important}
.svc-catalog-badges{min-height:auto!important;margin-bottom:6px;flex-wrap:wrap}
.svc-catalog-meta-row{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px}
.svc-catalog-meta-row span{display:inline-flex;border-radius:999px;background:#f8fafc;color:#64748b;border:1px solid #e2e8f0;padding:5px 8px;font-size:10.5px;font-weight:850}
.svc-catalog-editor{gap:16px!important}
.svc-editor-section{border:1px solid rgba(226,232,240,.96);background:#fff;border-radius:24px;padding:16px;display:grid;gap:13px}
.svc-editor-advanced{background:linear-gradient(135deg,rgba(0,103,95,.045),#fff)}
.svc-editor-section-head{display:flex;align-items:flex-start;gap:12px}
.svc-editor-section-head>span{width:28px;height:28px;border-radius:999px;background:#e7fbf7;color:#00675f;display:inline-flex;align-items:center;justify-content:center;font-size:12px;font-weight:950;flex:0 0 auto}
.svc-editor-section-head strong{display:block;color:#0f172a;font-size:15px;font-weight:950;letter-spacing:-.02em}
.svc-editor-section-head p{margin:4px 0 0;color:#64748b;font-size:12px;line-height:1.4;font-weight:750}
.svc-catalog-editor .svc-field input,
.svc-catalog-editor .svc-field select{width:100%;min-height:48px;border-radius:16px;border:1px solid #e2e8f0;padding:0 14px;box-sizing:border-box;font-weight:800;background:#fff;color:#0f172a}
.svc-catalog-editor .svc-field select{appearance:auto}
.svc-catalog-editor .svc-big-photo{height:132px!important;max-width:420px!important}
@media(max-width:1100px){.svc-catalog-health-grid{grid-template-columns:repeat(3,minmax(0,1fr))!important}.svc-catalog-hero{grid-template-columns:1fr!important}.svc-catalog-hero-actions{justify-content:flex-start}}
@media(max-width:760px){.svc-catalog-hero,.svc-catalog-strategy,.svc-catalog-list-card{border-radius:24px!important}.svc-catalog-mode-grid{grid-template-columns:1fr}.svc-catalog-health-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important}.svc-catalog-list-head{display:grid}.svc-catalog-list-head .svc-catalog-primary{width:100%}.svc-catalog-grid{grid-template-columns:1fr!important}.svc-catalog-card{grid-template-columns:58px minmax(0,1fr)!important;padding:13px!important;padding-bottom:62px!important}.svc-catalog-media{width:50px;height:50px;border-radius:16px}.svc-catalog-media.visual{width:54px;height:54px}.svc-catalog-hero-copy h3{font-size:28px!important}}


/* =========================================================
   PHASE 31C.3 — Minimal Operating Table
   ========================================================= */
.svc-minimal-page{
  --svc-brand:#00675f;
  --svc-brand-2:#00897f;
  --svc-ink:#0f172a;
  --svc-muted:#64748b;
  --svc-line:#e2e8f0;
  --svc-soft:#f8fafc;
  gap:14px!important;
}
.svc-minimal-shell{
  border:1px solid rgba(226,232,240,.96);
  background:rgba(255,255,255,.97);
  border-radius:28px;
  box-shadow:0 18px 48px rgba(15,23,42,.06);
  padding:18px;
  display:grid;
  gap:14px;
}
.svc-minimal-header{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:18px;
  padding:4px 2px 10px;
  border-bottom:1px solid rgba(226,232,240,.72);
}
.svc-minimal-header span,
.svc-minimal-search span{
  display:block;
  color:var(--svc-brand);
  font-size:11px;
  font-weight:950;
  letter-spacing:.12em;
  text-transform:uppercase;
}
.svc-minimal-header h3{
  margin:6px 0 0;
  color:var(--svc-ink);
  font-size:30px;
  line-height:1;
  letter-spacing:-.055em;
  font-weight:950;
}
.svc-minimal-header p{
  max-width:720px;
  margin:8px 0 0;
  color:var(--svc-muted);
  font-size:13px;
  line-height:1.42;
  font-weight:750;
}
.svc-minimal-primary,
.svc-op-empty button{
  min-height:42px;
  border:0;
  border-radius:14px;
  background:linear-gradient(135deg,var(--svc-brand),var(--svc-brand-2));
  color:#fff;
  font-weight:950;
  padding:0 16px;
  box-shadow:0 12px 26px rgba(0,103,95,.16);
  cursor:pointer;
  white-space:nowrap;
}
.svc-minimal-primary:disabled{opacity:.55;cursor:not-allowed}
.svc-minimal-toolbar{
  display:grid;
  grid-template-columns:minmax(240px,1fr) auto auto;
  gap:10px;
  align-items:end;
}
.svc-minimal-search input{
  width:100%;
  height:44px;
  margin-top:6px;
  border-radius:14px;
  border:1px solid var(--svc-line);
  background:#fff;
  padding:0 14px;
  color:var(--svc-ink);
  font-weight:800;
  outline:none;
  box-sizing:border-box;
}
.svc-minimal-search input:focus{
  border-color:rgba(0,103,95,.35);
  box-shadow:0 0 0 4px rgba(0,103,95,.08);
}
.svc-minimal-filters{
  min-height:44px;
  border:1px solid var(--svc-line);
  background:#fff;
  border-radius:999px;
  padding:4px;
  display:flex;
  align-items:center;
  gap:3px;
  overflow:auto;
}
.svc-minimal-filters button{
  height:34px;
  border:0;
  border-radius:999px;
  background:transparent;
  color:#64748b;
  font-size:12px;
  font-weight:900;
  padding:0 11px;
  cursor:pointer;
  white-space:nowrap;
}
.svc-minimal-filters button.active{
  background:#e7fbf7;
  color:#00675f;
}
.svc-minimal-mode{
  height:44px;
  border:1px solid var(--svc-line);
  border-radius:999px;
  background:#fff;
  color:#334155;
  font-size:12px;
  font-weight:950;
  padding:0 14px;
  cursor:pointer;
  white-space:nowrap;
}
.svc-minimal-mode.visual{
  border-color:rgba(0,103,95,.28);
  background:#e7fbf7;
  color:#00675f;
}
.svc-minimal-summary{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  padding:2px 0 4px;
}
.svc-minimal-summary span{
  display:inline-flex;
  align-items:center;
  gap:5px;
  min-height:28px;
  border:1px solid var(--svc-line);
  border-radius:999px;
  background:#fff;
  color:#64748b;
  font-size:12px;
  font-weight:850;
  padding:0 10px;
}
.svc-minimal-summary strong{color:#0f172a;font-weight:950}
.svc-minimal-summary .warn{background:#fff7ed;border-color:#fed7aa;color:#9a3412}
.svc-op-table{
  border:1px solid rgba(226,232,240,.96);
  border-radius:22px;
  background:#fff;
  overflow:hidden;
}
.svc-op-row{
  min-height:72px;
  display:grid;
  grid-template-columns:44px minmax(190px,1fr) 110px 110px 92px 76px 76px;
  gap:12px;
  align-items:center;
  padding:12px 14px;
  border-bottom:1px solid #f1f5f9;
  cursor:pointer;
  transition:background 140ms ease, transform 140ms ease;
  position:relative;
}
.svc-op-row:last-child{border-bottom:0}
.svc-op-row:hover{background:#fbfefd}
.svc-op-row.is-hidden{background:#f8fafc;opacity:.82}
.svc-op-icon{
  width:42px;
  height:42px;
  border-radius:14px;
  border:1px solid rgba(0,103,95,.13);
  background:linear-gradient(135deg,#e7fbf7,#ffffff);
  color:#00675f;
  display:grid;
  place-items:center;
  overflow:hidden;
  font-size:12px;
  font-weight:950;
  letter-spacing:-.03em;
}
.svc-op-icon img{width:100%;height:100%;object-fit:cover;display:block}
.svc-op-main{min-width:0;display:grid;gap:4px}
.svc-op-main strong{
  color:#0f172a;
  font-size:14px;
  line-height:1.1;
  font-weight:950;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}
.svc-op-main span,
.svc-op-value span{
  color:#64748b;
  font-size:11px;
  line-height:1.2;
  font-weight:750;
}
.svc-op-value{display:grid;gap:3px}
.svc-op-value strong{color:#0f172a;font-size:13px;font-weight:950}
.svc-op-status{
  height:30px;
  border-radius:999px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:7px;
  font-size:11px;
  font-weight:950;
  border:1px solid #e2e8f0;
  background:#fff;
  color:#64748b;
}
.svc-op-status i{width:8px;height:8px;border-radius:999px;background:#94a3b8}
.svc-op-status.on{background:#ecfdf5;border-color:#99f6e4;color:#00675f}
.svc-op-status.on i{background:#10b981}
.svc-op-status.off{background:#fff7f7;border-color:#fecaca;color:#dc2626}
.svc-op-status.off i{background:#ef4444}
.svc-op-warning{
  height:28px;
  border-radius:999px;
  background:#fff7ed;
  border:1px solid #fed7aa;
  color:#9a3412;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  font-size:11px;
  font-weight:950;
}
.svc-op-visibility{
  height:30px;
  border-radius:999px;
  border:1px solid var(--svc-line);
  background:#fff;
  color:#334155;
  font-size:11px;
  font-weight:950;
  cursor:pointer;
}
.svc-op-empty{
  min-height:180px;
  display:grid;
  place-items:center;
  align-content:center;
  gap:8px;
  text-align:center;
  padding:24px;
  background:linear-gradient(135deg,#f8fafc,#fff);
}
.svc-op-empty strong{font-size:17px;font-weight:950;color:#0f172a}
.svc-op-empty span{font-size:13px;font-weight:760;color:#64748b}
.svc-minimal-editor{gap:12px!important}
.svc-editor-section.compact{
  border-radius:22px!important;
  padding:14px!important;
  gap:12px!important;
}
.svc-editor-section.compact.advanced{background:#fbfefd!important}
.svc-editor-section-head.flat>span{display:none!important}
.svc-show-advanced{
  min-height:42px;
  border:1px dashed rgba(0,103,95,.35);
  border-radius:16px;
  background:rgba(0,103,95,.045);
  color:#00675f;
  font-weight:950;
  cursor:pointer;
}
.svc-minimal-switch-row{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:10px;
}
.svc-minimal-switch-row>button{
  min-height:48px;
  border-radius:16px;
  border:1px solid #e2e8f0;
  background:#fff;
  color:#334155;
  font-weight:950;
  cursor:pointer;
}
.svc-minimal-switch-row>button.active{
  border-color:rgba(0,103,95,.30);
  background:#e7fbf7;
  color:#00675f;
}
.compact-note{min-height:48px!important;border-radius:16px!important;padding:10px 12px!important}
.svc-event-config.minimal{border-radius:20px!important;padding:10px!important;background:#f8fafc!important;border-color:#e2e8f0!important}
@media(max-width:1120px){
  .svc-op-row{grid-template-columns:44px minmax(160px,1fr) 92px 92px 86px 70px;}
  .svc-op-warning{display:none}
}
@media(max-width:880px){
  .svc-minimal-header{display:grid}.svc-minimal-primary{width:100%}
  .svc-minimal-toolbar{grid-template-columns:1fr}.svc-minimal-filters{border-radius:16px}.svc-minimal-mode{width:100%}
  .svc-op-row{grid-template-columns:42px minmax(0,1fr) 76px;gap:10px;padding:12px;}
  .svc-op-value:nth-of-type(2), .svc-op-status, .svc-op-warning{display:none}
  .svc-op-visibility{position:absolute;right:12px;bottom:10px}
  .svc-op-row{padding-bottom:50px}
}
@media(max-width:620px){
  .svc-minimal-shell{border-radius:22px;padding:14px}
  .svc-minimal-header h3{font-size:26px}
  .svc-minimal-switch-row{grid-template-columns:1fr}
}



/* =========================================================
   PHASE 31D.6 — Portal modal + quieter service editor
   ========================================================= */
.svc-modal{
  position:fixed!important;
  inset:0!important;
  z-index:9999!important;
  width:100vw!important;
  height:100dvh!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  padding:24px!important;
  background:rgba(15,23,42,.54)!important;
  backdrop-filter:blur(3px)!important;
  overflow:auto!important;
}
.svc-sheet{
  width:min(720px,calc(100vw - 32px))!important;
  max-height:calc(100dvh - 48px)!important;
  margin:0!important;
  border-radius:24px!important;
  background:#fff!important;
  box-shadow:0 34px 110px rgba(15,23,42,.34)!important;
  display:flex!important;
  flex-direction:column!important;
  overflow:hidden!important;
}
.svc-sheet-head{
  position:sticky!important;
  top:0!important;
  z-index:5!important;
  background:#fff!important;
  padding:18px 20px 14px!important;
  border-bottom:1px solid #eef2f6!important;
  display:flex!important;
  align-items:flex-start!important;
  justify-content:space-between!important;
  gap:16px!important;
}
.svc-sheet-head h3{
  margin:0!important;
  font-size:20px!important;
  line-height:1.05!important;
  letter-spacing:-.045em!important;
  color:#0f172a!important;
  font-weight:950!important;
}
.svc-sheet-head h3::after{content:none!important;display:none!important;}
.svc-sheet-head span{
  display:block;
  margin-top:5px;
  color:#64748b;
  font-size:12px;
  line-height:1.35;
  font-weight:730;
}
.svc-sheet-head button{
  width:34px!important;
  height:34px!important;
  flex:0 0 auto!important;
  border:0!important;
  border-radius:999px!important;
  background:#f1f5f9!important;
  color:#334155!important;
  font-weight:950!important;
  cursor:pointer!important;
}
.svc-sheet-body{
  overflow:auto!important;
  min-height:0!important;
  padding:18px 20px!important;
}
.svc-sheet-foot{
  position:sticky!important;
  bottom:0!important;
  z-index:5!important;
  background:rgba(255,255,255,.96)!important;
  border-top:1px solid #eef2f6!important;
  padding:12px 20px!important;
  display:grid!important;
  grid-template-columns:auto minmax(0,1fr)!important;
  gap:14px!important;
  align-items:center!important;
}
.svc-sheet-danger{min-width:0;}
.svc-delete-link{
  border:0!important;
  background:transparent!important;
  color:#dc2626!important;
  font-size:12px!important;
  font-weight:900!important;
  padding:0!important;
  cursor:pointer!important;
  white-space:nowrap!important;
}
.svc-sheet-foot .svc-actions{
  display:grid!important;
  grid-template-columns:1fr 1fr!important;
  gap:10px!important;
  width:100%!important;
}
.svc-minimal-editor .svc-editor-section.compact{
  border-radius:20px!important;
  padding:16px!important;
}
.svc-minimal-switch-row.single{
  grid-template-columns:1fr!important;
}
.svc-minimal-switch-row.single > span{
  color:#64748b;
  font-size:12px;
  line-height:1.35;
  font-weight:730;
}
@media(max-width:760px){
  .svc-modal{align-items:flex-end!important;padding:10px!important;}
  .svc-sheet{width:100%!important;max-height:calc(100dvh - 20px)!important;border-radius:22px 22px 0 0!important;}
  .svc-sheet-foot{grid-template-columns:1fr!important;}
  .svc-sheet-danger{order:2;text-align:center;}
}


/* =========================================================
   PHASE 31D.7 — Event Service Modal UX
   Event-specific clarity without complicating salons.
   ========================================================= */
.svc-event-essentials{border-color:rgba(0,103,95,.20)!important;background:linear-gradient(180deg,#ffffff 0%,#fbfffd 100%)!important}
.svc-event-block,.svc-event-extras-editor{border:1px solid rgba(226,232,240,.92);background:#fff;border-radius:22px;padding:14px;display:grid;gap:12px}
.svc-event-mini-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}
.svc-event-mini-head strong{display:block;color:#0f172a;font-size:13px;font-weight:950;letter-spacing:-.015em}.svc-event-mini-head span{display:block;color:#64748b;font-size:12px;font-weight:750;line-height:1.35;margin-top:3px}
.svc-event-mini-head button{min-height:34px;border:1px solid rgba(0,103,95,.20);background:#f0fdfa;color:#00675f;border-radius:999px;padding:0 12px;font-size:12px;font-weight:950;white-space:nowrap}
.svc-event-type-grid{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:8px}.svc-event-type-grid button{min-height:72px;border:1px solid #e2e8f0;background:#fff;border-radius:18px;padding:11px;text-align:left;cursor:pointer;transition:.16s ease}.svc-event-type-grid button:hover{border-color:rgba(0,103,95,.25);transform:translateY(-1px)}.svc-event-type-grid button.active{border-color:rgba(0,103,95,.42);background:rgba(0,103,95,.065);box-shadow:0 10px 24px rgba(0,103,95,.07)}.svc-event-type-grid strong{display:block;color:#0f172a;font-size:12px;font-weight:950;line-height:1.1}.svc-event-type-grid span{display:block;color:#64748b;font-size:10.5px;line-height:1.25;font-weight:750;margin-top:5px}
.svc-character-mode-row{display:flex;flex-wrap:wrap;gap:8px}.svc-character-mode-row button{height:36px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;color:#475569;padding:0 13px;font-size:12px;font-weight:950}.svc-character-mode-row button.active{background:#ecfdf5;border-color:#99f6e4;color:#00675f}
.svc-event-empty-note{border:1px dashed #cbd5e1;background:#f8fafc;border-radius:16px;padding:12px;color:#64748b;font-size:12px;font-weight:800}.svc-event-extra-rows{display:grid;gap:8px}.svc-event-extra-row{display:grid;grid-template-columns:minmax(0,1fr) 120px 38px;gap:8px;align-items:center}.svc-event-extra-row input{min-height:42px;border-radius:15px;border:1px solid #e2e8f0;padding:0 12px;font-weight:850;width:100%;box-sizing:border-box}.svc-event-extra-row button{height:38px;border:0;border-radius:999px;background:#fef2f2;color:#dc2626;font-weight:950}.svc-event-quick-add{display:flex;flex-wrap:wrap;gap:8px}.svc-event-quick-add button{height:32px;border:1px solid #ccfbf1;background:#f0fdfa;color:#00675f;border-radius:999px;padding:0 11px;font-size:12px;font-weight:950}
.svc-events-mode .svc-minimal-mode::before,.svc-services-mode .svc-minimal-mode::before{content:"Vista: ";color:#64748b}
@media(max-width:920px){.svc-event-type-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:640px){.svc-event-type-grid{grid-template-columns:1fr}.svc-event-extra-row{grid-template-columns:1fr}.svc-event-extra-row button{width:100%}}

/* =========================================================
   PHASE 31D.8 — Service Presentation System
   Ícono por default, imagen opcional, preview única.
   ========================================================= */
.svc-presentation-section{border-color:rgba(0,103,95,.18)!important;background:linear-gradient(180deg,#ffffff,#fbfefd)!important}
.svc-presentation-choice-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.svc-presentation-choice{position:relative;border:1px solid #e2e8f0;background:#fff;border-radius:22px;padding:14px;text-align:left;display:grid;grid-template-columns:22px minmax(0,1fr);gap:10px;align-items:start;cursor:pointer;transition:.16s ease;min-height:164px}
.svc-presentation-choice:hover{border-color:rgba(0,103,95,.24);box-shadow:0 12px 28px rgba(15,23,42,.055)}
.svc-presentation-choice.active{border-color:rgba(0,103,95,.48);background:rgba(0,103,95,.045);box-shadow:0 14px 30px rgba(0,103,95,.07)}
.svc-choice-radio{width:16px;height:16px;border-radius:999px;border:2px solid #cbd5e1;margin-top:2px;display:block;background:#fff}
.svc-presentation-choice.active .svc-choice-radio{border-color:#00675f;background:radial-gradient(circle,#00675f 0 38%,#fff 42%)}
.svc-choice-copy strong{display:block;color:#0f172a;font-size:14px;font-weight:950;letter-spacing:-.02em}
.svc-choice-copy span{display:block;color:#64748b;font-size:12px;line-height:1.35;font-weight:760;margin-top:4px}
.svc-choice-icon-preview{grid-column:2;display:flex;align-items:center;gap:10px;margin-top:4px}
.svc-choice-icon-preview>span,.svc-service-preview-media.has-icon span{width:58px;height:58px;border-radius:999px;background:#e8f5f2;color:#00675f;display:grid;place-items:center;font-size:24px;font-weight:950;border:1px solid #cdebe6}
.svc-presentation-choice select{grid-column:2;min-height:38px;border:1px solid #dbe7ef;background:#fff;border-radius:999px;padding:0 12px;color:#0f172a;font-size:12px;font-weight:900;width:min(100%,230px);cursor:pointer}
.svc-choice-image-preview{grid-column:2;width:100%;height:62px;border-radius:17px;border:1px dashed rgba(0,103,95,.28);background:#f8fafc;display:grid;place-items:center;overflow:hidden;color:#64748b;font-size:12px;font-weight:900;margin-top:2px}
.svc-choice-image-preview img{width:100%;height:100%;object-fit:cover;display:block}
.svc-upload-mini{grid-column:2;display:inline-flex;align-items:center;justify-content:center;min-height:36px;border-radius:999px;border:1px solid rgba(0,103,95,.22);background:#fff;color:#00675f;font-size:12px;font-weight:950;padding:0 12px;width:max-content;margin-top:2px}
.svc-upload-mini.disabled{opacity:.58;color:#64748b;border-color:#e2e8f0;cursor:not-allowed}
.svc-service-preview-block{border:1px solid #e2e8f0;background:#fff;border-radius:22px;padding:13px;display:grid;gap:10px}
.svc-preview-head{display:flex;align-items:flex-end;justify-content:space-between;gap:10px}
.svc-preview-head strong{color:#0f172a;font-size:13px;font-weight:950}
.svc-preview-head span{color:#64748b;font-size:11px;font-weight:780}
.svc-service-preview-card{border:1px solid #edf2f7;background:linear-gradient(180deg,#fff,#f8fafc);border-radius:18px;padding:12px;display:grid;grid-template-columns:68px minmax(0,1fr);gap:13px;align-items:center}
.svc-service-preview-media{width:64px;height:64px;border-radius:18px;overflow:hidden;display:grid;place-items:center;background:#e8f5f2;border:1px solid #cdebe6;color:#00675f}
.svc-service-preview-media.has-image{background:#f1f5f9;border-color:#e2e8f0}
.svc-service-preview-media.has-image img{width:100%;height:100%;object-fit:cover;display:block}
.svc-service-preview-copy strong{display:block;color:#0f172a;font-size:15px;font-weight:950;letter-spacing:-.02em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.svc-service-preview-copy span{display:block;color:#64748b;font-size:12px;font-weight:850;margin-top:4px}
.svc-preview-event-line{color:#00675f!important;font-weight:900!important}
.svc-op-icon span{font-size:18px!important;letter-spacing:0!important}
.svc-catalog-media.simple span{font-size:22px!important;letter-spacing:0!important}
@media(max-width:760px){.svc-presentation-choice-grid{grid-template-columns:1fr}.svc-presentation-choice{min-height:auto}.svc-preview-head{display:grid}.svc-service-preview-card{grid-template-columns:58px minmax(0,1fr)}.svc-service-preview-media{width:56px;height:56px;border-radius:17px}.svc-choice-icon-preview>span,.svc-service-preview-media.has-icon span{width:52px;height:52px;font-size:22px}}


/* PHASE 31D.9 — FLEXIBLE PRICING FOR SERVICES */
.svc-price-block{display:grid;gap:12px;grid-column:1 / -1}
.svc-price-head strong{display:block;color:#0f172a;font-size:13px;font-weight:950;letter-spacing:-.01em}
.svc-price-head span{display:block;margin-top:3px;color:#64748b;font-size:12px;font-weight:750;line-height:1.35}
.svc-price-type-row{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
.svc-price-type-row button{min-height:76px;border:1px solid #e2e8f0;background:#fff;border-radius:18px;padding:12px;text-align:left;cursor:pointer;transition:.16s ease}
.svc-price-type-row button:hover{border-color:rgba(0,103,95,.24);transform:translateY(-1px)}
.svc-price-type-row button.active{border-color:rgba(0,103,95,.38);background:rgba(0,103,95,.065);box-shadow:0 10px 24px rgba(0,103,95,.08)}
.svc-price-type-row button strong{display:block;color:#0f172a;font-size:13px;font-weight:950}
.svc-price-type-row button span{display:block;margin-top:4px;color:#64748b;font-size:11px;font-weight:750;line-height:1.3}
.svc-quote-preview{border:1px solid rgba(0,103,95,.18);background:rgba(0,103,95,.055);border-radius:18px;padding:13px;display:flex;align-items:center;justify-content:space-between;gap:12px;color:#00675f}
.svc-quote-preview strong{color:#334155;font-size:12px;font-weight:950}
.svc-quote-preview span{font-size:14px;font-weight:950}
.svc-op-value strong{white-space:nowrap}
@media(max-width:720px){.svc-price-type-row{grid-template-columns:1fr}.svc-quote-preview{align-items:flex-start;display:grid}}



/* =========================================================
   PHASE 31D.11 — Presentation Image Frame Polish
   La tarjeta de imagen solo muestra la imagen real del servicio.
   ========================================================= */
.svc-presentation-polished{
  border-color:rgba(0,103,95,.14)!important;
  background:#fff!important;
  padding:16px!important;
}
.svc-presentation-polished .compact-title{
  margin-bottom:10px!important;
}
.svc-presentation-choice-grid.refined{
  grid-template-columns:minmax(0,.92fr) minmax(0,1.08fr)!important;
  gap:10px!important;
}
.svc-presentation-choice.compact{
  min-height:132px!important;
  border-radius:20px!important;
  padding:13px!important;
  grid-template-columns:18px minmax(0,1fr)!important;
  gap:9px!important;
}
.svc-presentation-choice.compact .svc-choice-copy strong{
  font-size:13px!important;
}
.svc-presentation-choice.compact .svc-choice-copy span{
  font-size:11px!important;
  margin-top:2px!important;
}
.svc-presentation-choice.compact .svc-choice-radio{
  width:14px!important;
  height:14px!important;
  margin-top:1px!important;
}
.svc-presentation-choice.compact .svc-choice-icon-preview{
  grid-column:2!important;
  margin-top:2px!important;
}
.svc-presentation-choice.compact .svc-choice-icon-preview>span{
  width:46px!important;
  height:46px!important;
  font-size:20px!important;
}
.svc-presentation-choice.compact select{
  grid-column:2!important;
  min-height:34px!important;
  height:34px!important;
  font-size:11px!important;
  max-width:230px!important;
}
.svc-choice-image-preview{display:none!important;}
.svc-choice-image-frame{
  grid-column:2!important;
  width:100%;
  height:76px;
  border-radius:16px;
  border:1px solid rgba(226,232,240,.96);
  background:#f8fafc;
  overflow:hidden;
  display:grid;
  place-items:center;
  margin-top:4px;
}
.svc-choice-image-frame.has-image{
  background:#fff;
  border-style:solid;
}
.svc-choice-image-frame img{
  width:100%;
  height:100%;
  object-fit:cover;
  display:block;
}
.svc-choice-image-frame.empty{
  border-style:dashed;
  background:linear-gradient(180deg,#ffffff,#f8fafc);
  color:#64748b;
}
.svc-choice-image-frame.empty>div{
  display:grid;
  gap:2px;
  text-align:center;
}
.svc-choice-image-frame.empty strong{
  color:#0f172a;
  font-size:12px;
  font-weight:950;
}
.svc-choice-image-frame.empty span{
  color:#64748b;
  font-size:11px;
  font-weight:750;
}
.svc-presentation-choice.compact .svc-upload-mini{
  grid-column:2!important;
  min-height:32px!important;
  height:32px!important;
  font-size:11px!important;
  margin-top:2px!important;
}
.svc-service-preview-block.refined-preview{
  margin-top:2px!important;
  padding:12px!important;
  border-radius:20px!important;
}
.svc-service-preview-block.refined-preview .svc-service-preview-card{
  padding:10px!important;
  grid-template-columns:58px minmax(0,1fr)!important;
}
.svc-service-preview-block.refined-preview .svc-service-preview-media{
  width:54px!important;
  height:54px!important;
  border-radius:16px!important;
}
.svc-service-preview-block.refined-preview .svc-service-preview-media.has-icon span{
  width:48px!important;
  height:48px!important;
  font-size:20px!important;
}
@media(max-width:760px){
  .svc-presentation-choice-grid.refined{grid-template-columns:1fr!important;}
  .svc-choice-image-frame{height:92px;}
}


/* =========================================================
   PHASE 31D.14 — Service Image matches Business Page
   Mismo ratio + mismo object-fit que la tarjeta pública.
   ========================================================= */
.svc-presentation-choice.compact.image-choice{
  min-height:178px!important;
}
.svc-choice-image-frame.bp-service-image-frame{
  grid-column:2!important;
  width:100%!important;
  aspect-ratio:282 / 154!important;
  height:auto!important;
  min-height:0!important;
  padding:0!important;
  border-radius:20px!important;
  border:1px solid rgba(0,103,95,.16)!important;
  background:#f1f5f9!important;
  overflow:hidden!important;
  box-shadow:0 10px 24px rgba(15,23,42,.055)!important;
  margin-top:6px!important;
  display:block!important;
}
.svc-choice-image-frame.bp-service-image-frame.has-image img{
  width:100%!important;
  height:100%!important;
  object-fit:cover!important;
  object-position:center!important;
  border-radius:0!important;
  display:block!important;
  background:#f1f5f9!important;
}
.svc-choice-image-frame.bp-service-image-frame.empty{
  display:grid!important;
  place-items:center!important;
  border-style:dashed!important;
  background:linear-gradient(180deg,#ffffff,#f8fafc)!important;
}
.svc-choice-image-ghost{
  width:100%;
  height:100%;
  min-height:112px;
  display:grid;
  place-items:center;
  align-content:center;
  gap:8px;
  text-align:center;
  padding:14px;
}
.svc-choice-image-ghost span{
  width:54px;
  height:54px;
  border-radius:18px;
  display:grid;
  place-items:center;
  color:#00675f;
  background:#e9f6f3;
  border:1px solid rgba(0,103,95,.12);
  font-size:24px;
  font-weight:950;
}
.svc-choice-image-ghost small{
  color:#64748b;
  font-size:11px;
  line-height:1.35;
  font-weight:750;
  max-width:210px;
}
.svc-presentation-choice.compact.image-choice .svc-upload-mini{
  justify-self:start!important;
  margin-top:8px!important;
  padding:0 14px!important;
  box-shadow:none!important;
}
@media(max-width:760px){
  .svc-presentation-choice.compact.image-choice{min-height:auto!important;}
  .svc-choice-image-frame.bp-service-image-frame{aspect-ratio:282 / 154!important;}
}


/* =========================================================
   PHASE 31D.15 — Slim Presentation Selector
   Ícono/Imagen conviven como selector compacto + una sola preview.
   ========================================================= */
.svc-presentation-slim{
  padding:14px!important;
}
.svc-presentation-slim .compact-title{
  margin-bottom:8px!important;
}
.svc-presentation-slim-row{
  display:grid;
  grid-template-columns:156px minmax(0,1fr);
  gap:12px;
  align-items:stretch;
}
.svc-presentation-segment{
  display:grid;
  align-content:start;
  gap:8px;
  padding:8px;
  border:1px solid rgba(226,232,240,.95);
  border-radius:18px;
  background:#f8fafc;
}
.svc-presentation-segment button{
  min-height:38px;
  border:1px solid transparent;
  border-radius:14px;
  background:transparent;
  color:#64748b;
  font-size:12px;
  font-weight:950;
  cursor:pointer;
  transition:.15s ease;
}
.svc-presentation-segment button.active{
  color:#00675f;
  background:#fff;
  border-color:rgba(0,103,95,.18);
  box-shadow:0 8px 18px rgba(15,23,42,.055);
}
.svc-presentation-live{
  min-height:96px;
  display:grid;
  grid-template-columns:138px minmax(0,1fr) auto;
  gap:12px;
  align-items:center;
  padding:10px;
  border:1px solid rgba(0,103,95,.14);
  border-radius:20px;
  background:linear-gradient(180deg,#ffffff,#fbfefd);
}
.svc-presentation-live-media{
  width:138px;
  aspect-ratio:282/154;
  border-radius:16px;
  overflow:hidden;
  display:grid;
  place-items:center;
  background:#f1f5f9;
  border:1px solid rgba(226,232,240,.92);
}
.svc-presentation-live-media.has-image img{
  width:100%;
  height:100%;
  object-fit:cover;
  object-position:center;
  display:block;
}
.svc-presentation-live-media.has-icon{
  background:#e9f6f3;
  border-color:rgba(0,103,95,.12);
}
.svc-presentation-live-media.has-icon span{
  width:54px;
  height:54px;
  border-radius:18px;
  display:grid;
  place-items:center;
  color:#00675f;
  background:#fff;
  font-size:24px;
  font-weight:950;
  box-shadow:0 8px 18px rgba(15,23,42,.055);
}
.svc-presentation-live-copy{
  min-width:0;
}
.svc-presentation-live-copy strong{
  display:block;
  color:#0f172a;
  font-size:13px;
  font-weight:950;
}
.svc-presentation-live-copy span{
  display:block;
  margin-top:3px;
  color:#64748b;
  font-size:11px;
  line-height:1.35;
  font-weight:750;
}
.svc-presentation-live select{
  min-width:160px;
  max-width:190px;
  height:36px;
  border:1px solid #dbe7ef;
  border-radius:999px;
  background:#fff;
  color:#0f172a;
  padding:0 12px;
  font-size:11px;
  font-weight:900;
}
.svc-upload-mini.slim{
  min-height:36px!important;
  height:36px!important;
  padding:0 14px!important;
  border-radius:999px!important;
  white-space:nowrap;
  margin:0!important;
  box-shadow:none!important;
}
.svc-presentation-slim .svc-presentation-choice-grid,
.svc-presentation-slim .svc-presentation-choice{
  display:none!important;
}
.svc-presentation-slim .refined-preview{
  margin-top:10px!important;
}
@media(max-width:860px){
  .svc-presentation-slim-row{grid-template-columns:1fr}
  .svc-presentation-segment{grid-template-columns:1fr 1fr}
  .svc-presentation-live{grid-template-columns:120px minmax(0,1fr);align-items:center}
  .svc-presentation-live select,
  .svc-upload-mini.slim{grid-column:1/-1;justify-self:start}
  .svc-presentation-live-media{width:120px}
}
@media(max-width:560px){
  .svc-presentation-live{grid-template-columns:1fr}
  .svc-presentation-live-media{width:100%}
}


/* =========================================================
   PHASE 31D.17 — Instant Icon Preview
   El ícono cambia visualmente antes de guardar.
   ========================================================= */
.svc-presentation-live-media.has-icon.icon-scissors span,.svc-service-preview-media.has-icon.icon-scissors span{color:#00675f;background:#e9f6f3;}
.svc-presentation-live-media.has-icon.icon-blower span,.svc-service-preview-media.has-icon.icon-blower span{color:#0f766e;background:#eefcf8;}
.svc-presentation-live-media.has-icon.icon-manicure span,.svc-service-preview-media.has-icon.icon-manicure span{color:#8b5cf6;background:#f5f3ff;}
.svc-presentation-live-media.has-icon.icon-pedicure span,.svc-service-preview-media.has-icon.icon-pedicure span{color:#0ea5e9;background:#eff6ff;}
.svc-presentation-live-media.has-icon.icon-shampoo span,.svc-service-preview-media.has-icon.icon-shampoo span{color:#0284c7;background:#e0f2fe;}
.svc-presentation-live-media.has-icon.icon-keratin span,.svc-service-preview-media.has-icon.icon-keratin span{color:#b45309;background:#fff7ed;}
.svc-presentation-live-media.has-icon.icon-makeup span,.svc-service-preview-media.has-icon.icon-makeup span{color:#be185d;background:#fdf2f8;}
.svc-presentation-live-media.has-icon.icon-spa span,.svc-service-preview-media.has-icon.icon-spa span{color:#047857;background:#ecfdf5;}
.svc-presentation-live-media.has-icon.icon-fitness span,.svc-service-preview-media.has-icon.icon-fitness span{color:#dc2626;background:#fef2f2;}
.svc-presentation-live-media.has-icon.icon-photo span,.svc-service-preview-media.has-icon.icon-photo span{color:#334155;background:#f1f5f9;}
.svc-presentation-live-media.has-icon.icon-event span,.svc-service-preview-media.has-icon.icon-event span{color:#7c3aed;background:#f5f3ff;}
.svc-presentation-live-media.has-icon.icon-package span,.svc-service-preview-media.has-icon.icon-package span{color:#00675f;background:#f0fdfa;}
.svc-presentation-live-copy strong{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}


/* =========================================================
   PHASE 31G — Minimal Service Editor + Event Experience Pricing
   Limpia modal normal y permite precio/cotización por experiencia.
   ========================================================= */
.svc-show-advanced{display:none!important;}
.svc-sheet{width:min(900px,calc(100vw - 32px))!important;border-radius:28px!important;overflow:hidden!important;}
.svc-sheet-head{padding:18px 22px 14px!important;border-bottom:1px solid rgba(226,232,240,.9)!important;}
.svc-sheet-head h3{font-size:22px!important;letter-spacing:-.05em!important;}
.svc-sheet-head span{font-size:12px!important;color:#64748b!important;}
.svc-sheet-body{padding:18px 22px 96px!important;}
.svc-sheet-foot{position:sticky!important;bottom:0!important;background:rgba(255,255,255,.96)!important;backdrop-filter:blur(10px)!important;border-top:1px solid rgba(226,232,240,.92)!important;padding:12px 18px!important;display:grid!important;grid-template-columns:1fr auto!important;gap:12px!important;align-items:center!important;}
.svc-actions{display:flex!important;gap:10px!important;justify-content:flex-end!important;align-items:center!important;}
.svc-actions button{min-width:132px!important;height:42px!important;border-radius:999px!important;font-size:13px!important;font-weight:950!important;}
.svc-actions button:not(.primary){border:1px solid #e2e8f0!important;background:#fff!important;color:#0f172a!important;}
.svc-actions .primary{border:0!important;background:#00675f!important;color:#fff!important;box-shadow:0 12px 26px rgba(0,103,95,.16)!important;}
.svc-sheet-danger{align-self:center!important;}
.svc-delete-link{border:0!important;background:transparent!important;color:#dc2626!important;font-size:12px!important;font-weight:950!important;padding:0!important;text-align:left!important;}
.svc-editor-section.compact{padding:14px!important;border-radius:22px!important;}
.svc-basic-head{display:flex!important;align-items:flex-start!important;justify-content:space-between!important;gap:14px!important;margin-bottom:12px!important;}
.svc-basic-head p{display:none!important;}
.svc-editor-status{height:34px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;color:#64748b;display:inline-flex;align-items:center;gap:7px;padding:0 12px;font-size:12px;font-weight:950;white-space:nowrap;cursor:pointer;}
.svc-editor-status i{width:8px;height:8px;border-radius:999px;background:#94a3b8;}
.svc-editor-status.active{background:#ecfdf5;border-color:#99f6e4;color:#00675f;}
.svc-editor-status.active i{background:#10b981;}
.svc-field{gap:6px!important;}
.svc-field>span{font-size:11px!important;color:#64748b!important;font-weight:950!important;}
.svc-field input,.svc-field select,.svc-field textarea{min-height:42px!important;border-radius:14px!important;font-size:14px!important;font-weight:850!important;}
.svc-two{gap:10px!important;}
.svc-price-duration-card{display:grid;gap:6px;}
.svc-price-duration-card>span{font-size:11px;color:#64748b;font-weight:950;}
.svc-money-input{min-height:42px;border:1px solid #e2e8f0;border-radius:14px;background:#fff;display:grid;grid-template-columns:36px 1fr;align-items:center;overflow:hidden;}
.svc-money-input i{height:100%;display:grid;place-items:center;background:#f8fafc;color:#00675f;font-style:normal;font-size:13px;font-weight:950;border-right:1px solid #edf2f7;}
.svc-money-input input{border:0!important;min-height:40px!important;border-radius:0!important;padding:0 12px!important;outline:none!important;}
.svc-money-input.event{max-width:240px;margin-top:10px;}
.svc-money-input.mini{width:120px;min-height:36px;border-radius:12px;}
.svc-money-input.mini input{min-height:34px!important;font-size:12px!important;}
.svc-price-block-minimal{border:1px solid #e2e8f0!important;border-radius:16px!important;padding:10px!important;background:#fbfefd!important;}
.svc-price-type-row.compact{display:flex!important;gap:8px!important;}
.svc-price-type-row.compact button{min-height:40px!important;border-radius:13px!important;padding:8px 10px!important;}
.svc-price-type-row.compact button span{display:none!important;}
.svc-quote-preview.minimal{margin-top:10px!important;border-radius:13px!important;padding:10px 12px!important;}
.svc-event-essentials-minimal{gap:12px!important;}
.svc-experience-pricing-editor{display:grid;gap:10px;}
.svc-event-mini-head.slim{margin-bottom:0!important;}
.svc-experience-price-list{display:grid;gap:8px;}
.svc-experience-price-row{display:grid;grid-template-columns:70px minmax(120px,1fr) 174px 140px;gap:8px;align-items:center;border:1px solid #e2e8f0;border-radius:16px;background:#fff;padding:8px;}
.svc-experience-price-row.disabled{opacity:.62;background:#f8fafc;}
.svc-experience-price-row>strong{font-size:13px;color:#0f172a;font-weight:950;}
.svc-price-mode-mini{display:grid;grid-template-columns:1fr 1fr;gap:4px;background:#f8fafc;border:1px solid #edf2f7;border-radius:999px;padding:3px;}
.svc-price-mode-mini button{height:28px;border:0;border-radius:999px;background:transparent;color:#64748b;font-size:10px;font-weight:950;cursor:pointer;}
.svc-price-mode-mini button.active{background:#fff;color:#00675f;box-shadow:0 4px 12px rgba(15,23,42,.06);}
.svc-standard-price-mode{grid-template-columns:1fr 1fr;max-width:230px;}
.svc-standard-price-mode button{font-size:10.5px;}
.svc-price-public-preview{display:block;color:#64748b;font-size:10.5px;font-weight:750;line-height:1.35;}
.svc-price-public-preview strong{color:#00675f;font-weight:950;}
.svc-quote-chip{height:36px;border:1px solid #fed7aa;background:#fff7ed;color:#c2410c;border-radius:12px;display:grid;place-items:center;font-size:11px;font-weight:950;}
.svc-character-mode-row.compact{grid-template-columns:repeat(3,minmax(0,1fr))!important;gap:6px!important;}
.compact-event-block{padding:12px!important;border-radius:18px!important;}
@media(max-width:760px){.svc-sheet-foot{grid-template-columns:1fr!important}.svc-actions{display:grid!important;grid-template-columns:1fr 1fr!important}.svc-actions button{min-width:0!important}.svc-experience-price-row{grid-template-columns:1fr!important}.svc-money-input.mini{width:100%;}.svc-price-mode-mini{max-width:none}.svc-basic-head{align-items:center!important}}


/* =========================================================
   PHASE 31G.2 — Event Experience Pricing Up
   Las experiencias del paquete reemplazan el precio general duplicado.
   ========================================================= */
.svc-one{
  display:grid;
  grid-template-columns:minmax(0, 1fr);
  gap:12px;
  max-width:260px;
}
.svc-events-mode .svc-price-block-minimal{
  display:none!important;
}
.svc-events-mode .svc-experience-pricing-editor{
  margin-top:12px!important;
  border:1px solid rgba(226,232,240,.96)!important;
  border-radius:22px!important;
  padding:12px!important;
  background:linear-gradient(180deg,#ffffff,#fbfefd)!important;
}
.svc-events-mode .svc-experience-pricing-editor .svc-event-mini-head.slim{
  margin-bottom:10px!important;
}
.svc-events-mode .svc-experience-pricing-editor .svc-event-mini-head.slim strong{
  font-size:14px!important;
  color:#0f172a!important;
}
.svc-events-mode .svc-experience-pricing-editor .svc-event-mini-head.slim span{
  font-size:11px!important;
  color:#64748b!important;
}
.svc-events-mode .svc-experience-price-list{
  display:grid!important;
  gap:8px!important;
}
.svc-events-mode .svc-experience-price-row{
  grid-template-columns:auto minmax(120px,1fr) auto minmax(130px,auto)!important;
  min-height:46px!important;
  padding:8px!important;
  border-radius:16px!important;
  background:#ffffff!important;
  border:1px solid #edf2f7!important;
}
.svc-events-mode .svc-experience-price-row strong{
  font-size:12px!important;
}
.svc-events-mode .svc-price-mode-mini{
  height:32px!important;
}
.svc-events-mode .svc-price-mode-mini button{
  height:30px!important;
  font-size:10px!important;
  padding:0 10px!important;
}
.svc-events-mode .svc-money-input.mini{
  height:32px!important;
  min-width:118px!important;
}
.svc-events-mode .svc-money-input.mini input{
  height:30px!important;
  min-height:30px!important;
  font-size:12px!important;
}
.svc-events-mode .svc-quote-chip{
  min-height:32px!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  border-radius:999px!important;
  padding:0 11px!important;
  font-size:10px!important;
  font-weight:950!important;
  color:#00675f!important;
  background:#f0fdfa!important;
  border:1px solid #ccfbf1!important;
}
.svc-events-mode .svc-event-essentials-minimal{
  margin-top:0!important;
}
.svc-events-mode .svc-event-essentials-minimal > .svc-editor-section-head{
  display:none!important;
}
@media(max-width:760px){
  .svc-one{max-width:none}
  .svc-events-mode .svc-experience-price-row{
    grid-template-columns:1fr!important;
    align-items:start!important;
  }
  .svc-events-mode .svc-price-mode-mini,
  .svc-events-mode .svc-money-input.mini,
  .svc-events-mode .svc-quote-chip{
    width:100%!important;
  }
}


/* =========================================================
   PHASE 31G.3 — Service Editor Option 1 Layout
   Vista previa arriba + campos + presentación en una sola tarjeta.
   ========================================================= */
.svc-option1-editor{
  border-radius:26px!important;
  padding:14px!important;
  background:#ffffff!important;
  box-shadow:0 12px 34px rgba(15,23,42,.04)!important;
}
.svc-option1-main{
  display:grid;
  grid-template-columns:220px minmax(0,1fr);
  gap:18px;
  align-items:stretch;
}
.svc-option1-preview{
  border:1px solid #e2e8f0;
  border-radius:20px;
  background:linear-gradient(180deg,#ffffff,#fbfefd);
  padding:14px;
  display:grid;
  align-content:center;
  justify-items:center;
  text-align:center;
  min-height:185px;
}
.svc-option1-preview > span{
  justify-self:start;
  color:#0f172a;
  font-size:12px;
  font-weight:950;
  margin-bottom:10px;
}
.svc-option1-preview-media{
  width:72px;
  height:72px;
  border-radius:24px;
  display:grid;
  place-items:center;
  overflow:hidden;
  background:#e9f6f3;
  border:1px solid rgba(0,103,95,.12);
  margin:0 auto 12px;
}
.svc-option1-preview-media.has-image{
  width:100%;
  height:86px;
  border-radius:18px;
  background:#f1f5f9;
}
.svc-option1-preview-media img{
  width:100%;
  height:100%;
  object-fit:cover;
  object-position:center;
  display:block;
}
.svc-option1-preview-media i{
  font-style:normal;
  color:#00675f;
  font-size:30px;
  font-weight:950;
}
.svc-option1-preview strong{
  color:#0f172a;
  font-size:16px;
  font-weight:950;
  letter-spacing:-.025em;
}
.svc-option1-preview p{
  margin:6px 0 0;
  color:#0f172a;
  font-size:13px;
  font-weight:850;
}
.svc-option1-fields{
  display:grid;
  gap:12px;
  align-content:start;
}
.svc-option1-fields .svc-field{
  gap:6px!important;
}
.svc-option1-fields .svc-field input{
  min-height:42px!important;
  border-radius:14px!important;
}
.svc-option1-field-grid{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:12px;
}
.svc-option1-unit-field{
  display:grid;
  gap:6px;
}
.svc-option1-unit-field > span{
  color:#475569;
  font-size:12px;
  font-weight:950;
}
.svc-option1-input-unit{
  min-height:42px;
  display:grid;
  grid-template-columns:1fr 54px;
  align-items:center;
  border:1px solid #dbe7ef;
  border-radius:14px;
  background:#fff;
  overflow:hidden;
}
.svc-option1-input-unit.money{
  grid-template-columns:54px 1fr;
}
.svc-option1-input-unit input{
  min-width:0;
  height:40px;
  border:0;
  background:transparent;
  padding:0 13px;
  color:#0f172a;
  font-size:14px;
  font-weight:850;
  outline:none;
}
.svc-option1-input-unit em{
  height:100%;
  display:grid;
  place-items:center;
  background:#f8fafc;
  color:#00675f;
  font-style:normal;
  font-size:13px;
  font-weight:950;
  border-left:1px solid #edf2f7;
}
.svc-option1-input-unit.money em{
  border-left:0;
  border-right:1px solid #edf2f7;
}
.svc-option1-presentation{
  margin-top:14px;
  padding-top:14px;
  border-top:1px solid #edf2f7;
  display:grid;
  grid-template-columns:1fr minmax(0, 1.55fr);
  gap:16px;
  align-items:center;
}
.svc-option1-presentation > div:first-child strong{
  display:block;
  color:#0f172a;
  font-size:14px;
  font-weight:950;
}
.svc-option1-presentation > div:first-child span{
  display:block;
  margin-top:4px;
  color:#64748b;
  font-size:12px;
  font-weight:750;
}
.svc-option1-presentation-controls{
  display:grid;
  grid-template-columns:220px 66px minmax(160px, 1fr);
  gap:10px;
  align-items:center;
}
.svc-option1-segment{
  height:42px;
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:4px;
  padding:4px;
  border:1px solid #e2e8f0;
  border-radius:16px;
  background:#f8fafc;
}
.svc-option1-segment button{
  border:0;
  border-radius:12px;
  background:transparent;
  color:#64748b;
  font-size:12px;
  font-weight:950;
  cursor:pointer;
}
.svc-option1-segment button.active{
  background:#fff;
  color:#00675f;
  border:1px solid rgba(0,103,95,.15);
  box-shadow:0 8px 18px rgba(15,23,42,.045);
}
.svc-option1-mini-media{
  width:54px;
  height:54px;
  border-radius:16px;
  display:grid;
  place-items:center;
  overflow:hidden;
  background:#e9f6f3;
  border:1px solid rgba(0,103,95,.12);
}
.svc-option1-mini-media span{
  color:#00675f;
  font-size:22px;
  font-weight:950;
}
.svc-option1-mini-media.has-image{
  background:#f1f5f9;
}
.svc-option1-mini-media img{
  width:100%;
  height:100%;
  object-fit:cover;
}
.svc-option1-presentation-controls select,
.svc-option1-upload{
  min-height:42px;
  border:1px solid #dbe7ef;
  border-radius:14px;
  background:#fff;
  color:#0f172a;
  padding:0 13px;
  font-size:12px;
  font-weight:900;
}
.svc-option1-upload{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  color:#00675f;
  cursor:pointer;
}
.svc-option1-upload.disabled{
  opacity:.55;
  cursor:not-allowed;
}
.svc-option1-editor .svc-experience-pricing-editor{
  margin-top:4px!important;
}
.svc-show-advanced{
  display:none!important;
}
.svc-sheet-foot{
  align-items:center!important;
}
.svc-sheet-danger{
  display:flex!important;
  align-items:center!important;
}
@media(max-width:900px){
  .svc-option1-main{
    grid-template-columns:1fr;
  }
  .svc-option1-preview{
    min-height:auto;
  }
  .svc-option1-presentation{
    grid-template-columns:1fr;
  }
  .svc-option1-presentation-controls{
    grid-template-columns:1fr 54px;
  }
  .svc-option1-presentation-controls select,
  .svc-option1-upload{
    grid-column:1 / -1;
  }
}
@media(max-width:560px){
  .svc-option1-field-grid{
    grid-template-columns:1fr;
  }
  .svc-option1-presentation-controls{
    grid-template-columns:1fr;
  }
}


/* =========================================================
   PHASE 31G.4 — Event Package Minimal Editor
   Eventos más limpios: experiencias tipo tabla + complementos colapsables.
   ========================================================= */

.svc-events-mode .svc-sheet{max-width:900px!important}
.svc-events-mode .svc-form{gap:10px!important}
.svc-events-mode .svc-option1-editor{padding:12px!important;border-radius:24px!important}
.svc-events-mode .svc-option1-main{grid-template-columns:190px minmax(0,1fr)!important;gap:14px!important}
.svc-events-mode .svc-option1-preview{min-height:154px!important;padding:12px!important;border-radius:18px!important}
.svc-events-mode .svc-option1-preview > span{font-size:10px!important;letter-spacing:.08em!important;text-transform:uppercase!important;color:#00675f!important}
.svc-events-mode .svc-option1-preview-media{width:100%!important;height:64px!important;border-radius:14px!important;margin-bottom:8px!important}
.svc-events-mode .svc-option1-preview strong{font-size:14px!important}
.svc-events-mode .svc-option1-preview p{margin-top:4px!important;font-size:11px!important;line-height:1.25!important}
.svc-events-mode .svc-preview-event-line{margin-top:4px!important;font-size:10px!important}

.svc-events-mode .svc-experience-pricing-editor{margin-top:8px!important;padding:0!important;border:1px solid #e5edf3!important;border-radius:18px!important;background:#fff!important;overflow:hidden!important}
.svc-events-mode .svc-experience-pricing-editor .svc-event-mini-head.slim{padding:10px 12px!important;margin:0!important;border-bottom:1px solid #edf2f7!important;background:#fbfefd!important}
.svc-events-mode .svc-experience-pricing-editor .svc-event-mini-head.slim strong{font-size:12px!important;letter-spacing:.08em!important;text-transform:uppercase!important;color:#00675f!important}
.svc-events-mode .svc-experience-pricing-editor .svc-event-mini-head.slim span{font-size:11px!important;color:#64748b!important}
.svc-events-mode .svc-experience-price-list{gap:0!important}
.svc-events-mode .svc-experience-price-row{min-height:42px!important;display:grid!important;grid-template-columns:72px minmax(115px,1fr) 152px 140px!important;gap:8px!important;align-items:center!important;padding:8px 10px!important;border:0!important;border-bottom:1px solid #edf2f7!important;border-radius:0!important;background:#fff!important}
.svc-events-mode .svc-experience-price-row:last-child{border-bottom:0!important}
.svc-events-mode .svc-experience-price-row.disabled{opacity:.58!important;background:#fbfbfc!important}
.svc-events-mode .svc-experience-price-row > strong{font-size:12px!important;font-weight:950!important;color:#0f172a!important}
.svc-events-mode .svc-mini-toggle{min-height:28px!important;height:28px!important;border-radius:999px!important;padding:0 10px!important;font-size:10px!important;font-weight:950!important}
.svc-events-mode .svc-price-mode-mini{height:30px!important;border-radius:999px!important;padding:3px!important;background:#f8fafc!important;border:1px solid #e2e8f0!important}
.svc-events-mode .svc-price-mode-mini button{height:22px!important;border-radius:999px!important;padding:0 9px!important;font-size:9px!important}
.svc-events-mode .svc-money-input.mini{min-width:0!important;height:30px!important;border-radius:999px!important}
.svc-events-mode .svc-money-input.mini i{width:34px!important;font-size:11px!important}
.svc-events-mode .svc-money-input.mini input{min-height:28px!important;height:28px!important;font-size:11px!important}
.svc-events-mode .svc-quote-chip{min-height:30px!important;border-radius:999px!important;font-size:9px!important;padding:0 10px!important}

.svc-events-mode .svc-option1-presentation{margin-top:10px!important;padding-top:10px!important;grid-template-columns:170px minmax(0,1fr)!important}
.svc-events-mode .svc-option1-presentation > div:first-child strong{font-size:12px!important}
.svc-events-mode .svc-option1-presentation > div:first-child span{font-size:10px!important}
.svc-events-mode .svc-option1-presentation-controls{grid-template-columns:170px 48px minmax(140px,1fr)!important}
.svc-events-mode .svc-option1-segment,.svc-events-mode .svc-option1-presentation-controls select,.svc-events-mode .svc-option1-upload{min-height:36px!important;height:36px!important;border-radius:13px!important}
.svc-events-mode .svc-option1-mini-media{width:44px!important;height:44px!important;border-radius:14px!important}

.svc-event-lite{padding:12px!important;border-radius:24px!important;background:#fff!important}
.svc-event-lite-head{display:flex;align-items:flex-end;justify-content:space-between;gap:12px;margin-bottom:10px}
.svc-event-lite-head strong{color:#0f172a;font-size:14px;font-weight:950}
.svc-event-lite-head span{color:#64748b;font-size:11px;font-weight:750}
.svc-event-lite-block{border:1px solid #e5edf3;border-radius:18px;overflow:hidden;background:#fff}
.svc-event-lite-block + .svc-event-lite-block{margin-top:8px}
.svc-event-lite-summary{width:100%;min-height:52px;border:0;background:#fff;display:grid;grid-template-columns:minmax(0,1fr) auto auto;gap:10px;align-items:center;text-align:left;padding:10px 12px;cursor:pointer}
.svc-event-lite-summary strong{display:block;color:#0f172a;font-size:13px;font-weight:950}
.svc-event-lite-summary span{display:block;margin-top:4px;color:#64748b;font-size:12px;font-weight:400;line-height:1.5}
.svc-event-lite-summary em{min-height:28px;display:inline-flex;align-items:center;justify-content:center;border-radius:999px;padding:0 10px;background:#f8fafc;color:#475569;border:1px solid #e2e8f0;font-style:normal;font-size:10px;font-weight:950;white-space:nowrap}
.svc-event-lite-summary i{min-height:28px;display:inline-flex;align-items:center;justify-content:center;border-radius:999px;padding:0 10px;background:#f0fdfa;color:#00675f;border:1px solid #ccfbf1;font-style:normal;font-size:10px;font-weight:950}
.svc-event-lite-block.open .svc-event-lite-summary{border-bottom:1px solid #edf2f7;background:#fbfefd}
.svc-event-lite-body{padding:10px}
.svc-event-lite .svc-event-block,.svc-event-lite .compact-event-block,.svc-event-lite .svc-event-extras-editor{border:0!important;padding:0!important;margin:0!important;background:transparent!important;box-shadow:none!important}
.svc-event-lite .svc-event-mini-head{display:none!important}
.svc-event-lite .svc-character-mode-row.compact{margin-bottom:8px!important}
.svc-event-lite .svc-character-editor{padding:10px!important;border-radius:14px!important;background:#fbfefd!important}
.svc-event-lite .svc-character-editor-head,.svc-event-lite .svc-event-extra-rows,.svc-event-lite .svc-event-quick-add{margin-top:8px!important}
.svc-event-lite .svc-character-empty,.svc-event-lite .svc-event-empty-note{min-height:34px!important;padding:9px 10px!important;border-radius:12px!important;font-size:10px!important}
.svc-event-lite .svc-help-text{font-size:10px!important;margin-top:6px!important}

@media(max-width:820px){
  .svc-events-mode .svc-option1-main{grid-template-columns:1fr!important}
  .svc-events-mode .svc-experience-price-row{grid-template-columns:1fr!important;gap:7px!important}
  .svc-events-mode .svc-option1-presentation,.svc-events-mode .svc-option1-presentation-controls{grid-template-columns:1fr!important}
  .svc-event-lite-head{flex-direction:column;align-items:flex-start;gap:6px}
  .svc-event-lite-summary{grid-template-columns:minmax(0,1fr) auto!important}
  .svc-event-lite-summary>div{grid-column:1/-1}
  .svc-event-lite-summary em{justify-self:start;white-space:normal}
  .svc-event-lite-summary i{justify-self:end}
}

/* =========================================================
   SERVICE ICON LIBRARY 1C — LOOP PROFESSIONAL SVG ICON PICKER
   ========================================================= */
.svc-service-icon-visual {
  width: 54px;
  height: 54px;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 30% 10%, rgba(20,184,166,.18), transparent 42%),
    linear-gradient(180deg, #ffffff, #f0fdfa);
  border: 1px solid rgba(20,184,166,.22);
  color: #00675f;
  overflow: hidden;
  box-shadow: 0 10px 22px rgba(15,23,42,.055);
  flex: 0 0 auto;
}
.svc-service-icon-visual.compact {
  width: 42px;
  height: 42px;
  border-radius: 15px;
  box-shadow: none;
}
.svc-service-icon-visual img {
  width: 72%;
  height: 72%;
  display: block;
  object-fit: contain;
}
.svc-service-icon-visual i {
  font-style: normal;
  font-size: 22px;
  font-weight: 950;
  line-height: 1;
}
.svc-icon-picker {
  grid-column: 1 / -1;
  width: 100%;
  border: 1px solid rgba(226,232,240,.92);
  border-radius: 22px;
  background: #ffffff;
  padding: 14px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.9);
}
.svc-icon-picker-top label {
  display: grid;
  gap: 7px;
}
.svc-icon-picker-top span {
  color: #64748b;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: .08em;
  text-transform: uppercase;
}
.svc-icon-picker-top input {
  width: 100%;
  height: 44px;
  border: 1px solid #e2e8f0;
  border-radius: 15px;
  background: #f8fafc;
  color: #0f172a;
  padding: 0 13px;
  font-size: 14px;
  font-weight: 760;
  outline: none;
  box-sizing: border-box;
}
.svc-icon-picker-top input:focus {
  border-color: rgba(0,103,95,.34);
  box-shadow: 0 0 0 4px rgba(20,184,166,.10);
  background: #ffffff;
}
.svc-icon-category-tabs {
  display: flex;
  gap: 7px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 12px 0 10px;
}
.svc-icon-category-tabs::-webkit-scrollbar { display: none; }
.svc-icon-category-tabs button {
  flex: 0 0 auto;
  height: 34px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
}
.svc-icon-category-tabs button.active {
  color: #ffffff;
  background: #00675f;
  border-color: #00675f;
}
.svc-icon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
  gap: 9px;
  max-height: 330px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 1px 2px 4px;
}
.svc-icon-choice {
  min-height: 104px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff, #fbfdfd);
  color: #0f172a;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 9px;
  padding: 10px 8px;
  cursor: pointer;
  transition: transform .14s ease, border-color .14s ease, box-shadow .14s ease, background .14s ease;
}
.svc-icon-choice:hover {
  transform: translateY(-1px);
  border-color: rgba(0,103,95,.26);
  box-shadow: 0 12px 26px rgba(15,23,42,.07);
}
.svc-icon-choice.active {
  border-color: #00675f;
  background: linear-gradient(180deg, #f0fdfa, #ffffff);
  box-shadow: 0 0 0 4px rgba(20,184,166,.12);
}
.svc-icon-choice span:last-child {
  color: #334155;
  font-size: 11.5px;
  line-height: 1.15;
  font-weight: 900;
  text-align: center;
}
.svc-icon-choice.active span:last-child {
  color: #00675f;
}
.svc-option1-preview-media .svc-service-icon-visual,
.svc-option1-mini-media .svc-service-icon-visual,
.svc-catalog-media .svc-service-icon-visual,
.svc-op-icon .svc-service-icon-visual {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  box-shadow: none;
  background: transparent;
  border: 0;
}
.svc-option1-preview-media .svc-service-icon-visual img,
.svc-option1-mini-media .svc-service-icon-visual img,
.svc-catalog-media .svc-service-icon-visual img,
.svc-op-icon .svc-service-icon-visual img {
  width: 68%;
  height: 68%;
}
@media(max-width:820px){
  .svc-icon-grid{grid-template-columns:repeat(3,minmax(0,1fr));max-height:280px;}
  .svc-icon-choice{min-height:92px;border-radius:16px;}
  .svc-icon-choice span:last-child{font-size:10.5px;}
}
@media(max-width:420px){
  .svc-icon-grid{grid-template-columns:repeat(2,minmax(0,1fr));}
}


  /* =========================================================
     SERVICES 1B — PROFESSIONAL ICON SYSTEM
     Clean transparent SVG illustrations. No cartoon placeholder.
     ========================================================= */
  .svc-service-icon-visual {
    width: 66px !important;
    height: 66px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 22px !important;
    background: linear-gradient(180deg, #f0fdfa 0%, #ffffff 100%) !important;
    border: 1px solid rgba(20,184,166,.18) !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
  }

  .svc-service-icon-visual.compact {
    width: 42px !important;
    height: 42px !important;
    border-radius: 15px !important;
  }

  .svc-service-icon-visual img {
    width: 78% !important;
    height: 78% !important;
    object-fit: contain !important;
    display: block !important;
  }

  .svc-option1-preview-media .svc-service-icon-visual {
    width: 104px !important;
    height: 104px !important;
    border-radius: 30px !important;
    background: linear-gradient(145deg, #effdfa 0%, #ffffff 70%) !important;
    box-shadow: 0 18px 42px rgba(0,103,95,.10) !important;
  }

  .svc-option1-preview-media .svc-service-icon-visual img {
    width: 74% !important;
    height: 74% !important;
  }

  .svc-icon-picker-card .svc-service-icon-visual {
    width: 50px !important;
    height: 50px !important;
    border-radius: 18px !important;
    background: #f8fffd !important;
  }

  .svc-icon-picker-card.active .svc-service-icon-visual {
    background: #e7fbf7 !important;
    border-color: rgba(0,103,95,.26) !important;
  }


/* =========================================================
   LOOP SERVICES ORDER — PUBLIC BUSINESS PAGE ORDER
   The admin order in this section is the public order shown in BusinessPage.
   ========================================================= */
.svc-order-note{
  border:1px solid rgba(0,103,95,.13);
  background:linear-gradient(180deg,#f0fdfa,#ffffff);
  color:#0f172a;
  border-radius:20px;
  padding:12px 14px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  box-shadow:0 10px 24px rgba(15,23,42,.035);
}
.svc-order-note strong{
  color:#00675f;
  font-size:12px;
  font-weight:950;
  letter-spacing:.09em;
  text-transform:uppercase;
  white-space:nowrap;
}
.svc-order-note span{
  color:#475569;
  font-size:12.5px;
  font-weight:780;
  line-height:1.35;
}
.svc-order-note em{
  color:#b45309;
  font-size:12px;
  font-style:normal;
  font-weight:900;
  background:#fffbeb;
  border:1px solid #fde68a;
  border-radius:999px;
  padding:6px 9px;
  white-space:nowrap;
}
.svc-order-controls{
  display:inline-grid;
  grid-template-columns:30px auto;
  align-items:center;
  gap:7px;
  min-width:92px;
}
.svc-order-controls > span{
  width:30px;
  height:30px;
  border-radius:999px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  background:#f0fdfa;
  border:1px solid #ccfbf1;
  color:#00675f;
  font-size:12px;
  font-weight:950;
}
.svc-order-controls > div{
  display:inline-flex;
  align-items:center;
  gap:5px;
}
.svc-order-controls button{
  width:28px;
  height:28px;
  border-radius:999px;
  border:1px solid #dbe3ee;
  background:#ffffff;
  color:#0f172a;
  font-size:14px;
  font-weight:950;
  line-height:1;
  cursor:pointer;
  box-shadow:0 7px 16px rgba(15,23,42,.045);
}
.svc-order-controls button:hover:not(:disabled){
  border-color:#99f6e4;
  background:#f0fdfa;
  color:#00675f;
}
.svc-order-controls button:disabled{
  opacity:.35;
  cursor:not-allowed;
  box-shadow:none;
}
@media(max-width:760px){
  .svc-order-note{
    align-items:flex-start;
    flex-direction:column;
    gap:7px;
  }
  .svc-order-note em{
    white-space:normal;
  }
  .svc-order-controls{
    min-width:84px;
    grid-template-columns:28px auto;
  }
  .svc-order-controls > span{
    width:28px;
    height:28px;
  }
}


/* =========================================================
   LOOP SERVICES ORDER — CLEAN ACTION CELL FIX
   Fixes overlapping arrows / badges / visibility button.
   The order controls now live inside one stable right-side action cell.
   ========================================================= */
.svc-op-row{
  grid-template-columns:44px minmax(210px,1fr) 96px 96px minmax(276px,auto)!important;
  gap:14px!important;
  align-items:center!important;
  overflow:visible!important;
}
.svc-op-actions{
  justify-self:end!important;
  width:100%!important;
  max-width:360px!important;
  display:flex!important;
  align-items:center!important;
  justify-content:flex-end!important;
  gap:8px!important;
  min-width:0!important;
  overflow:visible!important;
}
.svc-op-actions .svc-op-status{
  flex:0 0 auto!important;
  min-width:92px!important;
  padding:0 12px!important;
}
.svc-op-actions .svc-op-warning{
  flex:0 0 auto!important;
  min-width:76px!important;
  padding:0 10px!important;
}
.svc-op-actions .svc-op-visibility{
  flex:0 0 auto!important;
  min-width:76px!important;
  padding:0 13px!important;
  white-space:nowrap!important;
  position:static!important;
  right:auto!important;
  bottom:auto!important;
}
.svc-order-controls{
  flex:0 0 auto!important;
  display:grid!important;
  grid-template-columns:30px 62px!important;
  align-items:center!important;
  gap:6px!important;
  min-width:98px!important;
  width:98px!important;
}
.svc-order-controls > span{
  width:30px!important;
  height:30px!important;
}
.svc-order-controls > div{
  width:62px!important;
  display:grid!important;
  grid-template-columns:28px 28px!important;
  gap:6px!important;
}
.svc-order-controls button{
  width:28px!important;
  height:28px!important;
  min-width:28px!important;
  min-height:28px!important;
  padding:0!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  line-height:1!important;
}
@media(max-width:1180px){
  .svc-op-row{
    grid-template-columns:44px minmax(170px,1fr) 86px 86px minmax(214px,auto)!important;
    gap:10px!important;
  }
  .svc-op-actions{
    max-width:260px!important;
    gap:6px!important;
  }
  .svc-op-actions .svc-op-status{
    min-width:auto!important;
    width:32px!important;
    padding:0!important;
    font-size:0!important;
  }
  .svc-op-actions .svc-op-status i{
    margin:0!important;
  }
  .svc-op-actions .svc-op-warning{
    display:none!important;
  }
  .svc-op-actions .svc-op-visibility{
    min-width:70px!important;
    padding:0 10px!important;
  }
}
@media(max-width:880px){
  .svc-op-row{
    grid-template-columns:42px minmax(0,1fr)!important;
    grid-template-areas:
      "icon main"
      "icon meta"
      "actions actions"!important;
    gap:8px 10px!important;
    padding:13px!important;
    min-height:auto!important;
  }
  .svc-op-icon{grid-area:icon!important;}
  .svc-op-main{grid-area:main!important;}
  .svc-op-value{
    grid-area:meta!important;
    display:inline-flex!important;
    align-items:center!important;
    gap:5px!important;
  }
  .svc-op-value:nth-of-type(2){
    display:none!important;
  }
  .svc-op-actions{
    grid-area:actions!important;
    width:100%!important;
    max-width:none!important;
    justify-content:space-between!important;
    padding-top:8px!important;
    border-top:1px solid #f1f5f9!important;
  }
  .svc-op-actions .svc-op-status{
    display:inline-flex!important;
    width:auto!important;
    min-width:78px!important;
    padding:0 10px!important;
    font-size:11px!important;
  }
  .svc-op-actions .svc-op-visibility{
    min-width:78px!important;
  }
  .svc-order-controls{
    width:96px!important;
    min-width:96px!important;
  }
}
@media(max-width:420px){
  .svc-op-actions{
    gap:6px!important;
  }
  .svc-op-actions .svc-op-status{
    min-width:34px!important;
    width:34px!important;
    padding:0!important;
    font-size:0!important;
  }
  .svc-op-actions .svc-op-visibility{
    min-width:68px!important;
    padding:0 9px!important;
  }
  .svc-order-controls{
    grid-template-columns:28px 58px!important;
    width:92px!important;
    min-width:92px!important;
  }
  .svc-order-controls > span{
    width:28px!important;
    height:28px!important;
  }
  .svc-order-controls > div{
    width:58px!important;
    grid-template-columns:26px 26px!important;
  }
  .svc-order-controls button{
    width:26px!important;
    height:26px!important;
    min-width:26px!important;
    min-height:26px!important;
  }
}

.svc-inventory-usage-card{border:1px solid rgba(0,103,95,.16);background:linear-gradient(180deg,#ffffff,#f8fffd);border-radius:24px;padding:14px;display:grid;gap:12px}.svc-inventory-usage-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}.svc-inventory-usage-head strong{display:block;color:#0f172a;font-size:15px;font-weight:950}.svc-inventory-usage-head span{display:block;color:#64748b;font-size:12px;line-height:1.35;font-weight:750;margin-top:3px}.svc-inventory-usage-head em{font-style:normal;border-radius:999px;background:#ecfdf5;color:#047857;border:1px solid #99f6e4;padding:7px 10px;font-size:11px;font-weight:950;white-space:nowrap}.svc-inventory-empty{border-radius:18px;background:#f8fafc;border:1px dashed #cbd5e1;color:#64748b;padding:12px;font-size:12px;font-weight:850}.svc-inventory-usage-row{display:grid;grid-template-columns:minmax(180px,1fr) 130px 96px 72px;gap:8px;align-items:end}.svc-inventory-usage-row label{display:grid;gap:5px}.svc-inventory-usage-row label span,.svc-inventory-row-cost span{color:#64748b;font-size:10.5px;font-weight:950;text-transform:uppercase;letter-spacing:.08em}.svc-inventory-usage-row select,.svc-inventory-usage-row input{min-height:42px;border-radius:14px;border:1px solid #dbe5ee;background:#fff;color:#0f172a;padding:0 10px;font-size:12.5px;font-weight:850;outline:none}.svc-inventory-row-cost{border:1px solid #e2e8f0;background:#f8fafc;border-radius:14px;padding:7px 9px}.svc-inventory-row-cost strong{display:block;margin-top:3px;color:#00675f;font-size:13px;font-weight:950}.svc-inventory-usage-row>button{min-height:42px;border:0;border-radius:14px;background:#fef2f2;color:#dc2626;font-size:12px;font-weight:950;cursor:pointer}.svc-inventory-add-btn{min-height:40px;border:0;border-radius:14px;background:#00675f;color:#fff;font-size:13px;font-weight:950;cursor:pointer}.svc-inventory-add-btn:disabled{opacity:.55;cursor:not-allowed}@media(max-width:780px){.svc-inventory-usage-head{display:grid}.svc-inventory-usage-row{grid-template-columns:1fr}.svc-inventory-usage-row>button{width:100%}}

  .svc-materials-stock-pill.ok { background:#ecfdf5; border-color:#bbf7d0; color:#047857; }
  .svc-materials-stock-pill.warning { background:#fff7ed; border-color:#fed7aa; color:#9a3412; }
  .svc-materials-stock-pill.critical,
  .svc-materials-stock-pill.missing { background:#fef2f2; border-color:#fecaca; color:#b91c1c; }


/* =========================================================
   LOOP SERVICES REVAMP — clean main page, compact editor,
   professional icon picker and materials popup.
   ========================================================= */
.svc-minimal-page{
  --svc-brand:#00675f!important;
  --svc-brand-dark:#004f49!important;
  --svc-ink:#101828!important;
  --svc-muted:#667085!important;
  --svc-line:#e7ded2!important;
  --svc-cream:#fbf6ee!important;
}
.svc-minimal-shell{
  border-radius:26px!important;
  border:1px solid rgba(231,222,210,.96)!important;
  background:rgba(255,255,255,.90)!important;
  box-shadow:0 18px 46px rgba(16,24,40,.055)!important;
  padding:18px!important;
  gap:14px!important;
}
.svc-minimal-header{
  padding:0 0 12px!important;
  border-bottom:1px solid rgba(231,222,210,.78)!important;
  align-items:center!important;
}
.svc-minimal-header span{
  color:var(--svc-brand)!important;
  letter-spacing:.14em!important;
}
.svc-minimal-header h3{
  margin-top:7px!important;
  font-size:28px!important;
  letter-spacing:-.055em!important;
}
.svc-minimal-header p{
  margin-top:7px!important;
  max-width:760px!important;
  font-size:13px!important;
  color:var(--svc-muted)!important;
}
.svc-minimal-primary,
.svc-actions .primary,
.svc-op-empty button{
  border-radius:14px!important;
  background:linear-gradient(135deg,var(--svc-brand-dark),var(--svc-brand))!important;
  box-shadow:0 14px 30px rgba(0,79,73,.18)!important;
}
.svc-minimal-toolbar{
  grid-template-columns:minmax(260px,1fr) auto auto!important;
  align-items:center!important;
  gap:10px!important;
}
.svc-minimal-search span{display:none!important;}
.svc-minimal-search input{
  margin-top:0!important;
  height:44px!important;
  border-radius:999px!important;
  border-color:rgba(231,222,210,.96)!important;
  background:#fff!important;
  box-shadow:0 8px 18px rgba(16,24,40,.025)!important;
}
.svc-minimal-filters{
  min-height:40px!important;
  height:40px!important;
  border-color:rgba(231,222,210,.96)!important;
  box-shadow:0 8px 18px rgba(16,24,40,.022)!important;
}
.svc-minimal-filters button{height:30px!important;font-size:12px!important;padding:0 12px!important;}
.svc-minimal-filters button.active{background:#101828!important;color:#fff!important;}
.svc-minimal-mode{
  height:40px!important;
  border-color:rgba(231,222,210,.96)!important;
  box-shadow:0 8px 18px rgba(16,24,40,.022)!important;
}
.svc-order-note{
  min-height:42px!important;
  border-radius:16px!important;
  border-color:rgba(0,103,95,.14)!important;
  background:rgba(0,103,95,.045)!important;
  padding:0 14px!important;
}
.svc-order-note strong{letter-spacing:.12em!important;text-transform:uppercase!important;color:var(--svc-brand)!important;font-size:11px!important;}
.svc-order-note span{font-size:12px!important;color:#475467!important;}
.svc-minimal-summary{padding:0!important;gap:7px!important;}
.svc-minimal-summary span{
  min-height:26px!important;
  padding:0 10px!important;
  border-color:rgba(231,222,210,.96)!important;
  background:rgba(255,255,255,.72)!important;
  font-size:11.5px!important;
}
.svc-op-table{
  border-radius:20px!important;
  border-color:rgba(231,222,210,.96)!important;
  overflow:visible!important;
  background:transparent!important;
  display:grid!important;
  gap:10px!important;
}
.svc-op-row{
  border:1px solid rgba(231,222,210,.96)!important;
  border-radius:18px!important;
  background:rgba(255,255,255,.92)!important;
  box-shadow:0 10px 26px rgba(16,24,40,.04)!important;
  grid-template-columns:48px minmax(220px,1fr) 92px 92px 130px minmax(180px,auto)!important;
  min-height:82px!important;
  padding:14px!important;
  gap:14px!important;
}
.svc-op-row:hover{background:#fff!important;box-shadow:0 16px 36px rgba(16,24,40,.07)!important;transform:translateY(-1px)!important;}
.svc-op-row.is-hidden{opacity:.78!important;background:#fbfaf7!important;}
.svc-op-icon{width:48px!important;height:48px!important;border-radius:14px!important;}
.svc-op-main strong{font-size:15px!important;letter-spacing:-.025em!important;color:var(--svc-ink)!important;}
.svc-op-main span{font-size:11.5px!important;color:var(--svc-muted)!important;}
.svc-op-value span{font-size:10px!important;color:#667085!important;letter-spacing:.04em!important;}
.svc-op-value strong{font-size:12.5px!important;color:var(--svc-ink)!important;}
.svc-op-value em{font-size:11px!important;color:#475467!important;}
.svc-op-actions{max-width:none!important;width:auto!important;gap:7px!important;}
.svc-op-actions .svc-op-status{min-width:auto!important;height:30px!important;border-radius:999px!important;padding:0 10px!important;font-size:11px!important;background:#ecfdf5!important;color:#047857!important;border:1px solid #bbf7d0!important;}
.svc-op-row.is-hidden .svc-op-status{background:#f1f5f9!important;color:#667085!important;border-color:#e2e8f0!important;}
.svc-op-warning{display:none!important;}
.svc-order-controls{width:84px!important;min-width:84px!important;grid-template-columns:26px 52px!important;}
.svc-order-controls>span{width:26px!important;height:26px!important;font-size:10px!important;}
.svc-order-controls>div{width:52px!important;grid-template-columns:24px 24px!important;gap:4px!important;}
.svc-order-controls button{width:24px!important;height:24px!important;min-width:24px!important;min-height:24px!important;}
.svc-op-visibility{min-width:68px!important;height:32px!important;border-radius:999px!important;font-size:11px!important;padding:0 10px!important;}

.svc-modal{z-index:9999!important;background:rgba(15,23,42,.52)!important;backdrop-filter:blur(8px)!important;-webkit-backdrop-filter:blur(8px)!important;padding:28px 18px!important;}
.svc-sheet{
  width:min(980px,calc(100vw - 36px))!important;
  max-height:calc(100dvh - 56px)!important;
  border-radius:28px!important;
  overflow:hidden!important;
  box-shadow:0 30px 90px rgba(15,23,42,.30)!important;
}
.svc-sheet-head{
  padding:16px 20px!important;
  border-bottom:1px solid rgba(231,222,210,.82)!important;
}
.svc-sheet-head h3{font-size:22px!important;letter-spacing:-.04em!important;}
.svc-sheet-head span{font-size:12px!important;color:#667085!important;font-weight:750!important;}
.svc-sheet-body{padding:18px 20px!important;}
.svc-sheet-foot{
  padding:14px 20px!important;
  border-top:1px solid rgba(231,222,210,.82)!important;
  background:rgba(255,255,255,.96)!important;
}
.svc-actions{display:flex!important;justify-content:flex-end!important;gap:10px!important;}
.svc-sheet-danger{margin-right:auto!important;}
.svc-delete-link{color:#dc2626!important;background:transparent!important;border:0!important;font-weight:900!important;}

.svc-option1-editor{
  border:1px solid rgba(231,222,210,.96)!important;
  background:#fff!important;
  border-radius:22px!important;
  padding:16px!important;
  box-shadow:none!important;
}
.svc-option1-main{grid-template-columns:190px minmax(0,1fr)!important;gap:16px!important;}
.svc-option1-preview{min-height:168px!important;border-color:rgba(231,222,210,.96)!important;border-radius:18px!important;background:#fbfdfc!important;}
.svc-option1-preview>span{font-size:11px!important;text-transform:uppercase!important;letter-spacing:.08em!important;color:#667085!important;}
.svc-option1-preview-media{width:68px!important;height:68px!important;border-radius:22px!important;}
.svc-option1-preview strong{font-size:15px!important;}
.svc-option1-preview p{font-size:12px!important;}
.svc-option1-fields .svc-field input,.svc-option1-input-unit{height:44px!important;min-height:44px!important;border-radius:14px!important;}
.svc-option1-presentation{
  margin-top:14px!important;
  padding:14px!important;
  border:1px solid rgba(231,222,210,.96)!important;
  border-radius:20px!important;
  background:#fbfefd!important;
  display:grid!important;
  grid-template-columns:1fr!important;
  gap:12px!important;
}
.svc-option1-presentation>div:first-child{
  display:flex!important;
  align-items:flex-end!important;
  justify-content:space-between!important;
  gap:12px!important;
}
.svc-option1-presentation>div:first-child strong{font-size:14px!important;}
.svc-option1-presentation>div:first-child span{max-width:420px!important;text-align:right!important;font-size:11.5px!important;}
.svc-option1-presentation-controls{
  grid-template-columns:200px 50px minmax(0,1fr)!important;
  gap:10px!important;
  align-items:start!important;
}
.svc-option1-segment{height:40px!important;border-radius:14px!important;}
.svc-option1-mini-media{width:48px!important;height:48px!important;border-radius:15px!important;}
.svc-icon-picker{
  grid-column:1 / -1!important;
  border-radius:18px!important;
  padding:12px!important;
  border-color:rgba(231,222,210,.96)!important;
  background:#fff!important;
  box-shadow:none!important;
}
.svc-icon-picker-top input{height:40px!important;border-radius:13px!important;background:#fbfaf7!important;border-color:rgba(231,222,210,.96)!important;}
.svc-icon-category-tabs{padding:10px 0 8px!important;gap:6px!important;}
.svc-icon-category-tabs button{height:30px!important;font-size:11.5px!important;padding:0 10px!important;}
.svc-icon-category-tabs button.active{background:#101828!important;border-color:#101828!important;}
.svc-icon-grid{grid-template-columns:repeat(auto-fill,minmax(108px,1fr))!important;max-height:236px!important;gap:8px!important;padding-right:4px!important;}
.svc-icon-choice{min-height:88px!important;border-radius:16px!important;padding:9px!important;gap:7px!important;}
.svc-icon-choice span:last-child{font-size:10.8px!important;}
.svc-service-icon-visual{background:linear-gradient(180deg,#f0fdfa,#fff)!important;border-color:rgba(20,184,166,.18)!important;color:#00675f!important;}
.svc-service-icon-visual img{width:72%!important;height:72%!important;}
.svc-option1-preview-media .svc-service-icon-visual{width:86px!important;height:86px!important;border-radius:26px!important;}

.svc-materials-compact-card{
  border:1px solid rgba(231,222,210,.96);
  border-radius:20px;
  background:linear-gradient(180deg,#ffffff,#fbfefd);
  padding:14px;
  display:grid;
  grid-template-columns:minmax(0,1fr) auto auto;
  align-items:center;
  gap:14px;
}
.svc-materials-compact-copy span{display:block;color:#00675f;font-size:11px;font-weight:950;letter-spacing:.1em;text-transform:uppercase;}
.svc-materials-compact-copy strong{display:block;margin-top:4px;color:#101828;font-size:15px;font-weight:950;letter-spacing:-.02em;}
.svc-materials-compact-copy p{margin:4px 0 0;color:#667085;font-size:12px;line-height:1.35;font-weight:750;}
.svc-materials-compact-metrics{display:flex;gap:8px;}
.svc-materials-compact-metrics div{min-width:76px;border:1px solid #edf2f7;background:#fff;border-radius:14px;padding:8px 10px;}
.svc-materials-compact-metrics span{display:block;color:#667085;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.06em;}
.svc-materials-compact-metrics strong{display:block;margin-top:3px;color:#101828;font-size:13px;font-weight:950;}
.svc-materials-config-btn{min-height:40px;border:0;border-radius:14px;background:#00675f;color:white;font-size:12.5px;font-weight:950;padding:0 14px;cursor:pointer;white-space:nowrap;box-shadow:0 10px 22px rgba(0,103,95,.16);}
.svc-materials-config-btn:disabled{opacity:.55;cursor:not-allowed;}
.svc-materials-sheet-body{display:grid;gap:14px;}
.svc-materials-sheet-intro{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;border:1px solid rgba(231,222,210,.96);border-radius:20px;background:#fbfefd;padding:14px;}
.svc-materials-sheet-intro span{display:block;color:#00675f;font-size:11px;font-weight:950;letter-spacing:.1em;text-transform:uppercase;}
.svc-materials-sheet-intro strong{display:block;margin-top:4px;color:#101828;font-size:18px;font-weight:950;letter-spacing:-.035em;}
.svc-materials-sheet-intro p{margin:4px 0 0;color:#667085;font-size:12.5px;font-weight:750;line-height:1.4;}
.svc-materials-sheet-intro em{font-style:normal;border-radius:999px;background:#ecfdf5;color:#047857;border:1px solid #bbf7d0;padding:7px 10px;font-size:11px;font-weight:950;white-space:nowrap;}
.svc-materials-sheet-summary{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:8px;}
.svc-materials-sheet-summary div{border:1px solid #edf2f7;background:#fff;border-radius:16px;padding:10px;}
.svc-materials-sheet-summary span{display:block;color:#667085;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.06em;}
.svc-materials-sheet-summary strong{display:block;margin-top:4px;color:#101828;font-size:14px;font-weight:950;}
.svc-materials-editor-list{display:grid;gap:10px;}
.svc-materials-editor-row{display:grid;grid-template-columns:minmax(220px,1fr) 110px 108px 132px auto;gap:8px;align-items:end;border:1px solid rgba(231,222,210,.96);background:#fff;border-radius:18px;padding:12px;}
.svc-materials-editor-row label{display:grid;gap:6px;}
.svc-materials-editor-row label span,.svc-materials-line-cost span{color:#667085;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.06em;}
.svc-materials-editor-row select,.svc-materials-editor-row input{min-height:40px;border:1px solid #dbe5ee;border-radius:13px;background:#fff;color:#101828;padding:0 10px;font-size:12.5px;font-weight:850;outline:none;}
.svc-materials-line-cost{border:1px solid #edf2f7;background:#fbfaf7;border-radius:13px;padding:7px 9px;}
.svc-materials-line-cost strong{display:block;margin-top:3px;color:#00675f;font-size:13px;font-weight:950;}
.svc-materials-line-cost small{display:block;color:#667085;font-size:10.5px;font-weight:800;}
.svc-materials-stock-pill{min-height:40px;border-radius:13px;border:1px solid #e2e8f0;padding:7px 9px;display:grid;align-content:center;}
.svc-materials-stock-pill span{font-size:10px!important;font-weight:950!important;text-transform:uppercase!important;letter-spacing:.04em!important;}
.svc-materials-stock-pill strong{font-size:12px!important;font-weight:950!important;}
.svc-materials-remove{min-height:40px;border:0;border-radius:13px;background:#fef2f2;color:#dc2626;font-size:12px;font-weight:950;cursor:pointer;padding:0 12px;}
.svc-materials-empty-state{min-height:160px;border:1px dashed #cbd5e1;border-radius:18px;background:#f8fafc;display:grid;place-items:center;text-align:center;padding:22px;color:#667085;}
.svc-materials-empty-state strong{display:block;color:#101828;font-size:16px;font-weight:950;}
.svc-materials-empty-state span{display:block;margin-top:4px;font-size:12px;font-weight:750;}
.svc-materials-empty-state button{margin-top:12px;min-height:38px;border:0;border-radius:13px;background:#00675f;color:white;font-size:12px;font-weight:950;padding:0 14px;cursor:pointer;}
.svc-materials-sheet-actions{width:100%;}

@media(max-width:1050px){
  .svc-op-row{grid-template-columns:48px minmax(180px,1fr) 86px 86px minmax(160px,auto)!important;}
  .svc-op-materials{display:none!important;}
  .svc-materials-sheet-summary{grid-template-columns:repeat(2,minmax(0,1fr));}
  .svc-materials-editor-row{grid-template-columns:1fr 110px;}
  .svc-materials-editor-row .product{grid-column:1 / -1;}
}
@media(max-width:820px){
  .svc-minimal-toolbar{grid-template-columns:1fr!important;}
  .svc-minimal-header{display:grid!important;}
  .svc-minimal-primary{width:100%!important;}
  .svc-op-row{grid-template-columns:48px minmax(0,1fr)!important;grid-template-areas:"icon main" "icon meta" "actions actions"!important;}
  .svc-op-value:nth-of-type(3),.svc-op-value:nth-of-type(4){display:inline-flex!important;}
  .svc-op-actions{justify-content:space-between!important;width:100%!important;}
  .svc-option1-main{grid-template-columns:1fr!important;}
  .svc-option1-presentation>div:first-child{display:grid!important;}
  .svc-option1-presentation>div:first-child span{text-align:left!important;}
  .svc-option1-presentation-controls{grid-template-columns:1fr!important;}
  .svc-materials-compact-card{grid-template-columns:1fr!important;align-items:start;}
  .svc-materials-compact-metrics{width:100%;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));}
}
@media(max-width:560px){
  .svc-sheet{width:100vw!important;max-height:100dvh!important;border-radius:22px 22px 0 0!important;}
  .svc-modal{padding:18px 0 0!important;align-items:flex-end!important;}
  .svc-sheet-body{padding:14px!important;}
  .svc-sheet-head,.svc-sheet-foot{padding:14px!important;}
  .svc-actions{display:grid!important;grid-template-columns:1fr 1fr!important;}
  .svc-materials-sheet-summary{grid-template-columns:1fr!important;}
  .svc-materials-editor-row{grid-template-columns:1fr!important;}
  .svc-materials-compact-metrics{grid-template-columns:1fr!important;}
}


/* =========================================================
   LOOP MOBILE FEED FIX — ServicesSection Phase 2
   Purpose: compact mobile experience inside BusinessEditPage
   ========================================================= */
@media (max-width: 720px){
  .svc-page,
  .svc-minimal-page{
    gap:10px!important;
    width:100%!important;
    overflow-x:hidden!important;
  }

  .svc-minimal-shell{
    border-radius:20px!important;
    padding:14px!important;
    box-shadow:0 12px 28px rgba(16,24,40,.055)!important;
    border-color:rgba(231,222,210,.94)!important;
    overflow:visible!important;
  }

  /* In mobile the BusinessEditPage selector already says “Servicios”;
     this section should feel like content, not another page header. */
  .svc-minimal-header{
    display:grid!important;
    grid-template-columns:1fr!important;
    gap:12px!important;
    padding:0 0 12px!important;
    border-bottom:1px solid rgba(231,222,210,.78)!important;
  }
  .svc-minimal-header > div > span{
    display:none!important;
  }
  .svc-minimal-header h3{
    margin:0!important;
    font-size:24px!important;
    line-height:1!important;
    letter-spacing:-.055em!important;
  }
  .svc-minimal-header p{
    margin:7px 0 0!important;
    max-width:100%!important;
    font-size:12.5px!important;
    line-height:1.35!important;
  }
  .svc-minimal-primary{
    width:100%!important;
    min-height:44px!important;
    border-radius:14px!important;
    font-size:14px!important;
    box-shadow:0 10px 22px rgba(0,103,95,.16)!important;
  }

  .svc-minimal-toolbar{
    display:grid!important;
    grid-template-columns:1fr!important;
    gap:10px!important;
    padding:0!important;
  }
  .svc-minimal-search{
    height:44px!important;
    min-height:44px!important;
    border-radius:16px!important;
    padding:0 13px!important;
  }
  .svc-minimal-search > span{
    display:none!important;
  }
  .svc-minimal-search input{
    font-size:13px!important;
  }
  .svc-minimal-filters{
    display:flex!important;
    width:100%!important;
    gap:7px!important;
    overflow-x:auto!important;
    padding-bottom:2px!important;
    scrollbar-width:none!important;
  }
  .svc-minimal-filters::-webkit-scrollbar{display:none!important;}
  .svc-minimal-filters button{
    flex:0 0 auto!important;
    height:34px!important;
    min-height:34px!important;
    padding:0 12px!important;
    border-radius:999px!important;
    font-size:11.5px!important;
  }
  .svc-minimal-mode{
    display:none!important;
  }

  .svc-order-note{
    display:none!important;
  }

  .svc-minimal-summary{
    display:flex!important;
    width:100%!important;
    overflow-x:auto!important;
    gap:7px!important;
    padding:0 0 2px!important;
    scrollbar-width:none!important;
  }
  .svc-minimal-summary::-webkit-scrollbar{display:none!important;}
  .svc-minimal-summary span{
    flex:0 0 auto!important;
    min-height:28px!important;
    border-radius:999px!important;
    padding:0 10px!important;
    font-size:11px!important;
    background:rgba(255,255,255,.86)!important;
  }

  .svc-op-table{
    border:0!important;
    background:transparent!important;
    box-shadow:none!important;
    border-radius:0!important;
    display:grid!important;
    gap:10px!important;
  }

  .svc-op-row{
    width:100%!important;
    min-height:0!important;
    padding:13px!important;
    border-radius:18px!important;
    display:grid!important;
    grid-template-columns:44px minmax(0,1fr)!important;
    gap:10px 12px!important;
    align-items:start!important;
    border:1px solid rgba(231,222,210,.96)!important;
    background:rgba(255,255,255,.94)!important;
    box-shadow:0 10px 24px rgba(16,24,40,.045)!important;
  }
  .svc-op-row:hover{
    transform:none!important;
    box-shadow:0 10px 24px rgba(16,24,40,.045)!important;
  }
  .svc-op-icon{
    width:44px!important;
    height:44px!important;
    border-radius:14px!important;
    grid-column:1!important;
    grid-row:1 / span 2!important;
  }
  .svc-op-main{
    grid-column:2!important;
    min-width:0!important;
  }
  .svc-op-main strong{
    font-size:15px!important;
    line-height:1.12!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .svc-op-main span{
    margin-top:3px!important;
    font-size:11.2px!important;
    line-height:1.25!important;
  }

  .svc-op-value{
    display:none!important;
  }
  .svc-op-row > .svc-op-value:nth-of-type(3),
  .svc-op-row > .svc-op-value:nth-of-type(4){
    display:inline-flex!important;
    align-items:center!important;
    gap:5px!important;
    min-width:0!important;
    width:fit-content!important;
    max-width:100%!important;
    border:1px solid #edf2f7!important;
    background:#fbfaf7!important;
    border-radius:999px!important;
    padding:4px 8px!important;
  }
  .svc-op-row > .svc-op-value:nth-of-type(3){
    grid-column:2!important;
    grid-row:2!important;
    justify-self:start!important;
  }
  .svc-op-row > .svc-op-value:nth-of-type(4){
    grid-column:2!important;
    grid-row:2!important;
    justify-self:end!important;
  }
  .svc-op-value span{
    display:none!important;
  }
  .svc-op-value strong{
    font-size:11.5px!important;
    line-height:1!important;
  }
  .svc-op-materials{
    display:none!important;
  }

  .svc-op-actions{
    grid-column:1 / -1!important;
    width:100%!important;
    min-width:0!important;
    display:grid!important;
    grid-template-columns:minmax(0,1fr) auto auto!important;
    gap:7px!important;
    align-items:center!important;
    justify-content:stretch!important;
    margin-top:2px!important;
  }
  .svc-op-status{
    justify-self:start!important;
    height:30px!important;
    min-width:0!important;
    max-width:120px!important;
    padding:0 10px!important;
    font-size:11px!important;
  }
  .svc-order-controls{
    width:76px!important;
    min-width:76px!important;
    grid-template-columns:24px 48px!important;
    justify-self:end!important;
  }
  .svc-order-controls > span{
    width:24px!important;
    height:24px!important;
  }
  .svc-order-controls > div{
    width:48px!important;
    grid-template-columns:22px 22px!important;
    gap:4px!important;
  }
  .svc-order-controls button{
    width:22px!important;
    height:22px!important;
    min-width:22px!important;
    min-height:22px!important;
  }
  .svc-op-visibility{
    min-width:64px!important;
    height:30px!important;
    padding:0 9px!important;
    font-size:10.8px!important;
  }

  /* Mobile sheet becomes a true app bottom sheet. */
  .svc-modal{
    align-items:flex-end!important;
    padding:0!important;
    z-index:60000!important;
  }
  .svc-sheet{
    width:100vw!important;
    max-width:100vw!important;
    height:min(92dvh,860px)!important;
    max-height:92dvh!important;
    border-radius:24px 24px 0 0!important;
    border-left:0!important;
    border-right:0!important;
  }
  .svc-sheet-head{
    min-height:62px!important;
    padding:14px 16px!important;
  }
  .svc-sheet-head h3{
    font-size:20px!important;
  }
  .svc-sheet-head span{
    display:none!important;
  }
  .svc-sheet-head button{
    width:38px!important;
    height:38px!important;
    border-radius:999px!important;
  }
  .svc-sheet-body{
    padding:14px 14px 104px!important;
    overflow-y:auto!important;
    -webkit-overflow-scrolling:touch!important;
  }
  .svc-sheet-foot{
    position:absolute!important;
    left:0!important;
    right:0!important;
    bottom:0!important;
    padding:12px 14px calc(12px + env(safe-area-inset-bottom,0px))!important;
    background:rgba(255,255,255,.96)!important;
    backdrop-filter:blur(16px)!important;
    -webkit-backdrop-filter:blur(16px)!important;
  }
  .svc-actions{
    display:grid!important;
    grid-template-columns:1fr 1fr!important;
    gap:9px!important;
    width:100%!important;
  }
  .svc-actions button{
    min-height:44px!important;
    border-radius:14px!important;
  }
  .svc-sheet-danger{
    position:absolute!important;
    left:14px!important;
    top:-34px!important;
  }
  .svc-delete-link{
    font-size:12px!important;
  }

  .svc-option1-editor{
    border:0!important;
    padding:0!important;
    background:transparent!important;
    display:grid!important;
    gap:12px!important;
  }
  .svc-option1-main{
    display:grid!important;
    grid-template-columns:1fr!important;
    gap:12px!important;
  }
  .svc-option1-preview{
    display:none!important;
  }
  .svc-option1-fields{
    display:grid!important;
    gap:11px!important;
  }
  .svc-option1-field-grid{
    display:grid!important;
    grid-template-columns:1fr 1fr!important;
    gap:9px!important;
  }
  .svc-option1-fields .svc-field input,
  .svc-option1-input-unit{
    min-height:46px!important;
    height:46px!important;
    border-radius:14px!important;
  }

  .svc-option1-presentation{
    margin-top:0!important;
    padding:12px!important;
    border-radius:18px!important;
    gap:10px!important;
  }
  .svc-option1-presentation > div:first-child{
    display:none!important;
  }
  .svc-option1-presentation-controls{
    display:grid!important;
    grid-template-columns:1fr!important;
    gap:10px!important;
  }
  .svc-option1-mini-media{
    display:none!important;
  }
  .svc-option1-segment{
    height:40px!important;
    border-radius:14px!important;
  }
  .svc-icon-picker{
    border-radius:18px!important;
    padding:11px!important;
  }
  .svc-icon-picker-top label span{
    display:none!important;
  }
  .svc-icon-picker-top input{
    height:42px!important;
    font-size:12.5px!important;
  }
  .svc-icon-category-tabs{
    display:flex!important;
    overflow-x:auto!important;
    gap:6px!important;
    padding:9px 0!important;
    scrollbar-width:none!important;
  }
  .svc-icon-category-tabs::-webkit-scrollbar{display:none!important;}
  .svc-icon-category-tabs button{
    flex:0 0 auto!important;
  }
  .svc-icon-grid{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    max-height:310px!important;
    gap:8px!important;
  }
  .svc-icon-choice{
    min-height:86px!important;
    border-radius:16px!important;
  }

  .svc-materials-compact-card{
    grid-template-columns:1fr!important;
    gap:10px!important;
    padding:12px!important;
    border-radius:18px!important;
  }
  .svc-materials-compact-copy span{
    display:none!important;
  }
  .svc-materials-compact-copy strong{
    font-size:14px!important;
  }
  .svc-materials-compact-copy p{
    font-size:11.5px!important;
  }
  .svc-materials-compact-metrics{
    display:none!important;
  }
  .svc-materials-config-btn{
    width:100%!important;
    min-height:42px!important;
    border-radius:14px!important;
  }

  .svc-show-advanced{
    min-height:42px!important;
    border-radius:14px!important;
  }
}

@media (max-width: 380px){
  .svc-op-row > .svc-op-value:nth-of-type(4){
    display:none!important;
  }
  .svc-op-actions{
    grid-template-columns:1fr auto!important;
  }
  .svc-order-controls{
    display:none!important;
  }
}



/* =========================================================
   LOOP SERVICES — LUCIDE ICON SYSTEM + TOP SHEET HARDLOCK
   Professional service icons now come from lucide-react through
   src/data/serviceIconOptions.js instead of handmade SVG data URIs.
   ========================================================= */

.svc-modal.svc-modal-top-layer,
.svc-modal-top-layer{
  position:fixed!important;
  inset:0!important;
  z-index:2147483646!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  padding:22px!important;
  background:rgba(15,23,42,.58)!important;
  backdrop-filter:blur(14px)!important;
  -webkit-backdrop-filter:blur(14px)!important;
}

.svc-sheet.svc-sheet-top-layer,
.svc-sheet-top-layer{
  z-index:2147483647!important;
  position:relative!important;
  width:min(1080px,calc(100vw - 36px))!important;
  max-height:calc(100dvh - 36px)!important;
  border-radius:30px!important;
  overflow:hidden!important;
  box-shadow:0 34px 90px rgba(15,23,42,.32)!important;
}

.svc-sheet-top-layer .svc-sheet-head{
  position:sticky!important;
  top:0!important;
  z-index:3!important;
  background:rgba(255,255,255,.98)!important;
  backdrop-filter:blur(18px)!important;
  -webkit-backdrop-filter:blur(18px)!important;
}

.svc-sheet-top-layer .svc-sheet-body{
  max-height:calc(100dvh - 190px)!important;
  overflow:auto!important;
}

.svc-icon-picker-lucide{
  border:1px solid rgba(226,232,240,.92)!important;
  border-radius:22px!important;
  background:linear-gradient(180deg,#ffffff 0%,#fbfaf7 100%)!important;
  padding:14px!important;
  display:grid!important;
  gap:12px!important;
}

.svc-icon-selected-card{
  display:flex!important;
  align-items:center!important;
  justify-content:space-between!important;
  gap:12px!important;
  border:1px solid rgba(0,103,95,.13)!important;
  background:#f0fdfa!important;
  border-radius:18px!important;
  padding:12px!important;
}

.svc-icon-selected-card span{
  display:block!important;
  color:#00675f!important;
  font-size:10.5px!important;
  font-weight:950!important;
  letter-spacing:.12em!important;
  text-transform:uppercase!important;
}

.svc-icon-selected-card strong{
  display:block!important;
  margin-top:3px!important;
  color:#101828!important;
  font-size:14px!important;
  line-height:1.1!important;
  font-weight:950!important;
}

.svc-icon-picker-lucide .svc-icon-picker-top label{
  display:grid!important;
  gap:7px!important;
}

.svc-icon-picker-lucide .svc-icon-picker-top span{
  color:#475467!important;
  font-size:11px!important;
  font-weight:950!important;
  letter-spacing:.08em!important;
  text-transform:uppercase!important;
}

.svc-icon-picker-lucide .svc-icon-picker-top input{
  height:42px!important;
  border-radius:14px!important;
  border:1px solid rgba(203,213,225,.92)!important;
  background:#fff!important;
  color:#101828!important;
  padding:0 13px!important;
  font-size:13px!important;
  font-weight:800!important;
  outline:none!important;
}

.svc-icon-picker-lucide .svc-icon-picker-top input:focus{
  border-color:rgba(0,103,95,.45)!important;
  box-shadow:0 0 0 4px rgba(0,103,95,.08)!important;
}

.svc-icon-category-tabs{
  display:flex!important;
  align-items:center!important;
  gap:7px!important;
  overflow:auto!important;
  scrollbar-width:none!important;
}

.svc-icon-category-tabs::-webkit-scrollbar{
  display:none!important;
}

.svc-icon-category-tabs button{
  flex:0 0 auto!important;
  height:32px!important;
  border-radius:999px!important;
  border:1px solid rgba(203,213,225,.92)!important;
  background:#fff!important;
  color:#475467!important;
  padding:0 12px!important;
  font-size:11.5px!important;
  font-weight:900!important;
}

.svc-icon-category-tabs button.active{
  background:#101828!important;
  border-color:#101828!important;
  color:#fff!important;
}

.svc-icon-grid{
  display:grid!important;
  grid-template-columns:repeat(auto-fill,minmax(92px,1fr))!important;
  gap:9px!important;
  max-height:330px!important;
  overflow:auto!important;
  padding:1px!important;
}

.svc-icon-choice{
  min-height:86px!important;
  border-radius:18px!important;
  border:1px solid rgba(226,232,240,.92)!important;
  background:#fff!important;
  color:#101828!important;
  display:grid!important;
  place-items:center!important;
  align-content:center!important;
  gap:8px!important;
  padding:10px 8px!important;
  cursor:pointer!important;
  transition:transform .14s ease, border-color .14s ease, background .14s ease, box-shadow .14s ease!important;
}

.svc-icon-choice:hover{
  transform:translateY(-1px)!important;
  border-color:rgba(0,103,95,.24)!important;
  box-shadow:0 12px 28px rgba(15,23,42,.055)!important;
}

.svc-icon-choice.active{
  background:#f0fdfa!important;
  border-color:rgba(0,103,95,.42)!important;
  box-shadow:0 0 0 3px rgba(0,103,95,.08)!important;
}

.svc-icon-choice span{
  color:#344054!important;
  font-size:11.2px!important;
  font-weight:900!important;
  line-height:1.08!important;
  text-align:center!important;
}

.svc-service-icon-visual{
  width:42px!important;
  height:42px!important;
  border-radius:14px!important;
  display:inline-grid!important;
  place-items:center!important;
  background:#e8f6f3!important;
  border:1px solid rgba(0,103,95,.10)!important;
  overflow:hidden!important;
  flex:0 0 auto!important;
  color:#00675f!important;
}

.svc-service-icon-visual.compact{
  width:34px!important;
  height:34px!important;
  border-radius:12px!important;
}

.svc-service-icon-visual svg{
  width:62%!important;
  height:62%!important;
  display:block!important;
  stroke:currentColor!important;
}

.svc-service-icon-visual i{
  color:#00675f!important;
  font-style:normal!important;
  font-size:16px!important;
  font-weight:950!important;
  line-height:1!important;
}

.svc-option1-mini-media .svc-service-icon-visual,
.svc-option1-preview-media .svc-service-icon-visual,
.svc-catalog-media .svc-service-icon-visual{
  background:#e8f6f3!important;
  border:1px solid rgba(0,103,95,.10)!important;
}

@media(max-width:760px){
  .svc-modal.svc-modal-top-layer,
  .svc-modal-top-layer{
    padding:10px!important;
  }

  .svc-sheet.svc-sheet-top-layer,
  .svc-sheet-top-layer{
    width:calc(100vw - 20px)!important;
    max-height:calc(100dvh - 20px)!important;
    border-radius:24px!important;
  }

  .svc-sheet-top-layer .svc-sheet-body{
    max-height:calc(100dvh - 166px)!important;
  }

  .svc-icon-grid{
    grid-template-columns:repeat(3,minmax(0,1fr))!important;
    max-height:285px!important;
  }

  .svc-icon-choice{
    min-height:78px!important;
    border-radius:16px!important;
  }
}

@media(max-width:390px){
  .svc-icon-grid{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
  }
}



/* =========================================================
   LOOP SERVICES — HYBRID PREMIUM ICONS V2
   Uses lucide-react + react-icons packs through namespace imports.
   Goal: recognizable service icons, larger visual weight, less tiny bubble effect.
   ========================================================= */
.svc-service-icon-visual.premium{
  width:58px!important;
  height:58px!important;
  min-width:58px!important;
  border-radius:19px!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  color:#00675f!important;
  background:linear-gradient(180deg,#ecfdf7 0%,#dff5ef 100%)!important;
  border:1px solid rgba(0,103,95,.16)!important;
  box-shadow:inset 0 1px 0 rgba(255,255,255,.9), 0 10px 20px rgba(0,103,95,.055)!important;
  overflow:hidden!important;
}

.svc-service-icon-visual.premium.compact{
  width:48px!important;
  height:48px!important;
  min-width:48px!important;
  border-radius:16px!important;
}

.svc-service-icon-visual.premium svg,
.svc-service-icon-visual.premium .svc-premium-icon-svg{
  width:32px!important;
  height:32px!important;
  min-width:32px!important;
  min-height:32px!important;
  display:block!important;
  color:#00675f!important;
  stroke:currentColor!important;
  fill:currentColor;
}

.svc-service-icon-visual.premium.compact svg,
.svc-service-icon-visual.premium.compact .svc-premium-icon-svg{
  width:27px!important;
  height:27px!important;
  min-width:27px!important;
  min-height:27px!important;
}

/* Lucide line icons should stay outline. React-icons can keep their fill. */
.svc-service-icon-visual.premium svg[fill="none"]{
  fill:none!important;
}

.svc-option1-preview-media .svc-service-icon-visual.premium{
  width:82px!important;
  height:82px!important;
  min-width:82px!important;
  border-radius:27px!important;
}

.svc-option1-preview-media .svc-service-icon-visual.premium svg,
.svc-option1-preview-media .svc-service-icon-visual.premium .svc-premium-icon-svg{
  width:46px!important;
  height:46px!important;
}

.svc-option1-mini-media .svc-service-icon-visual.premium{
  width:42px!important;
  height:42px!important;
  min-width:42px!important;
  border-radius:14px!important;
}

.svc-option1-mini-media .svc-service-icon-visual.premium svg,
.svc-option1-mini-media .svc-service-icon-visual.premium .svc-premium-icon-svg{
  width:24px!important;
  height:24px!important;
}

.svc-icon-selected-card .svc-service-icon-visual.premium{
  width:46px!important;
  height:46px!important;
  min-width:46px!important;
  border-radius:15px!important;
}

.svc-icon-selected-card .svc-service-icon-visual.premium svg,
.svc-icon-selected-card .svc-service-icon-visual.premium .svc-premium-icon-svg{
  width:26px!important;
  height:26px!important;
}

.svc-icon-grid{
  grid-template-columns:repeat(auto-fill,minmax(112px,1fr))!important;
  gap:10px!important;
}

.svc-icon-choice{
  min-height:112px!important;
  padding:13px 10px!important;
  border-radius:20px!important;
  background:linear-gradient(180deg,#ffffff 0%,#fcfbf8 100%)!important;
  align-content:center!important;
}

.svc-icon-choice .svc-service-icon-visual.premium.compact{
  width:52px!important;
  height:52px!important;
  min-width:52px!important;
  border-radius:18px!important;
}

.svc-icon-choice .svc-service-icon-visual.premium.compact svg,
.svc-icon-choice .svc-service-icon-visual.premium.compact .svc-premium-icon-svg{
  width:31px!important;
  height:31px!important;
}

.svc-icon-choice span{
  font-size:11.8px!important;
  line-height:1.1!important;
  color:#101828!important;
  max-width:100%!important;
}

.svc-icon-choice.active{
  border-color:rgba(0,103,95,.45)!important;
  background:linear-gradient(180deg,#effdf8 0%,#ffffff 100%)!important;
  box-shadow:0 0 0 3px rgba(0,103,95,.08), 0 16px 30px rgba(15,23,42,.055)!important;
}

.svc-icon-choice.active .svc-service-icon-visual.premium{
  background:#00675f!important;
  color:#ffffff!important;
  border-color:#00675f!important;
}

.svc-icon-choice.active .svc-service-icon-visual.premium svg,
.svc-icon-choice.active .svc-service-icon-visual.premium .svc-premium-icon-svg{
  color:#ffffff!important;
  stroke:currentColor!important;
}

.svc-catalog-media .svc-service-icon-visual.premium{
  width:54px!important;
  height:54px!important;
  min-width:54px!important;
}

.svc-catalog-media .svc-service-icon-visual.premium svg,
.svc-catalog-media .svc-service-icon-visual.premium .svc-premium-icon-svg{
  width:30px!important;
  height:30px!important;
}

@media(max-width:760px){
  .svc-icon-grid{
    grid-template-columns:repeat(3,minmax(0,1fr))!important;
    gap:9px!important;
  }
  .svc-icon-choice{
    min-height:102px!important;
    padding:11px 7px!important;
  }
  .svc-icon-choice .svc-service-icon-visual.premium.compact{
    width:48px!important;
    height:48px!important;
  }
  .svc-icon-choice .svc-service-icon-visual.premium.compact svg,
  .svc-icon-choice .svc-service-icon-visual.premium.compact .svc-premium-icon-svg{
    width:28px!important;
    height:28px!important;
  }
}

@media(max-width:390px){
  .svc-icon-grid{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
  }
}



/* =========================================================
   LOOP SERVICES — PRESENTATION PICKER AS MODAL
   Makes icon/image choice a minimal popup instead of a large inline block.
   ========================================================= */

.svc-presentation-compact{
  display:grid!important;
  grid-template-columns:minmax(0,1fr)!important;
  gap:12px!important;
  padding:16px!important;
  border-radius:24px!important;
  background:#fff!important;
}

.svc-presentation-copy{
  display:flex!important;
  align-items:flex-start!important;
  justify-content:space-between!important;
  gap:14px!important;
}

.svc-presentation-copy strong{
  color:#101828!important;
  font-size:15px!important;
  font-weight:950!important;
}

.svc-presentation-copy span{
  color:#667085!important;
  font-size:12px!important;
  font-weight:800!important;
  line-height:1.32!important;
  text-align:right!important;
  max-width:360px!important;
}

.svc-presentation-summary-card{
  display:grid!important;
  grid-template-columns:50px minmax(0,1fr) auto!important;
  align-items:center!important;
  gap:12px!important;
  padding:12px!important;
  border:1px solid rgba(226,232,240,.92)!important;
  border-radius:18px!important;
  background:linear-gradient(180deg,#ffffff 0%,#fbfaf7 100%)!important;
}

.svc-presentation-summary-card > div:nth-child(2){
  display:grid!important;
  gap:2px!important;
  min-width:0!important;
}

.svc-presentation-summary-card span{
  color:#00675f!important;
  font-size:10px!important;
  font-weight:950!important;
  letter-spacing:.12em!important;
  text-transform:uppercase!important;
}

.svc-presentation-summary-card strong{
  color:#101828!important;
  font-size:14px!important;
  font-weight:950!important;
  line-height:1.1!important;
  white-space:nowrap!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
}

.svc-presentation-summary-card small{
  color:#667085!important;
  font-size:11.5px!important;
  font-weight:750!important;
  line-height:1.25!important;
}

.svc-presentation-open-btn{
  height:42px!important;
  border-radius:14px!important;
  border:1px solid rgba(203,213,225,.95)!important;
  background:#fff!important;
  color:#00675f!important;
  padding:0 14px!important;
  font-size:12px!important;
  font-weight:950!important;
  cursor:pointer!important;
  white-space:nowrap!important;
}

.svc-presentation-open-btn:hover{
  border-color:rgba(0,103,95,.32)!important;
  box-shadow:0 12px 26px rgba(15,23,42,.06)!important;
}

.svc-presentation-sheet{
  display:grid!important;
  gap:14px!important;
}

.svc-presentation-sheet-hero{
  display:grid!important;
  grid-template-columns:86px minmax(0,1fr)!important;
  gap:14px!important;
  align-items:center!important;
  padding:14px!important;
  border-radius:22px!important;
  border:1px solid rgba(0,103,95,.14)!important;
  background:linear-gradient(135deg,#f0fdfa 0%,#ffffff 65%)!important;
}

.svc-presentation-sheet-preview{
  width:86px!important;
  height:86px!important;
  border-radius:24px!important;
  background:#e8f6f3!important;
  border:1px solid rgba(0,103,95,.15)!important;
  display:grid!important;
  place-items:center!important;
  overflow:hidden!important;
}

.svc-presentation-sheet-preview.has-image img{
  width:100%!important;
  height:100%!important;
  object-fit:cover!important;
}

.svc-presentation-sheet-preview .svc-service-icon-visual{
  width:60px!important;
  height:60px!important;
  border:0!important;
  background:transparent!important;
  box-shadow:none!important;
}

.svc-presentation-sheet-hero span{
  color:#00675f!important;
  font-size:10.5px!important;
  font-weight:950!important;
  letter-spacing:.13em!important;
  text-transform:uppercase!important;
}

.svc-presentation-sheet-hero strong{
  display:block!important;
  margin-top:4px!important;
  color:#101828!important;
  font-size:20px!important;
  font-weight:950!important;
  line-height:1.05!important;
}

.svc-presentation-sheet-hero p{
  margin:6px 0 0!important;
  color:#667085!important;
  font-size:12.5px!important;
  font-weight:800!important;
  line-height:1.35!important;
}

.svc-presentation-mode-cards{
  display:grid!important;
  grid-template-columns:1fr 1fr!important;
  gap:10px!important;
}

.svc-presentation-mode-cards button{
  min-height:92px!important;
  border-radius:20px!important;
  border:1px solid rgba(226,232,240,.95)!important;
  background:#fff!important;
  padding:14px!important;
  display:grid!important;
  grid-template-columns:46px minmax(0,1fr)!important;
  gap:12px!important;
  align-items:center!important;
  text-align:left!important;
  cursor:pointer!important;
}

.svc-presentation-mode-cards button.active{
  border-color:rgba(0,103,95,.38)!important;
  background:#f0fdfa!important;
  box-shadow:0 0 0 3px rgba(0,103,95,.08)!important;
}

.svc-presentation-mode-cards strong{
  display:block!important;
  color:#101828!important;
  font-size:14px!important;
  font-weight:950!important;
  line-height:1.1!important;
}

.svc-presentation-mode-cards span{
  display:block!important;
  margin-top:4px!important;
  color:#667085!important;
  font-size:11.5px!important;
  font-weight:750!important;
  line-height:1.3!important;
}

.svc-presentation-image-glyph{
  width:46px!important;
  height:46px!important;
  border-radius:16px!important;
  background:#f2f4f7!important;
  color:#00675f!important;
  display:grid!important;
  place-items:center!important;
  font-size:11px!important;
  font-weight:950!important;
  border:1px solid rgba(203,213,225,.9)!important;
}

.svc-icon-picker-modal{
  padding:0!important;
  border:0!important;
  background:transparent!important;
  box-shadow:none!important;
}

.svc-icon-picker-modal .svc-icon-picker-top{
  margin-bottom:10px!important;
}

.svc-icon-grid-modal{
  max-height:300px!important;
  grid-template-columns:repeat(auto-fill,minmax(104px,1fr))!important;
  padding:2px!important;
}

.svc-icon-grid-modal .svc-icon-choice{
  min-height:96px!important;
  border-radius:18px!important;
}

.svc-image-mode-panel{
  display:grid!important;
  grid-template-columns:minmax(0,1fr) auto!important;
  gap:14px!important;
  align-items:center!important;
  padding:16px!important;
  border-radius:20px!important;
  border:1px solid rgba(226,232,240,.95)!important;
  background:#fff!important;
}

.svc-image-mode-panel strong{
  display:block!important;
  color:#101828!important;
  font-size:15px!important;
  font-weight:950!important;
}

.svc-image-mode-panel span{
  display:block!important;
  margin-top:4px!important;
  color:#667085!important;
  font-size:12px!important;
  font-weight:780!important;
  line-height:1.35!important;
}

.svc-presentation-sheet-actions{
  justify-content:flex-end!important;
  width:100%!important;
}

@media(max-width:760px){
  .svc-presentation-copy{
    display:grid!important;
  }

  .svc-presentation-copy span{
    text-align:left!important;
    max-width:none!important;
  }

  .svc-presentation-summary-card{
    grid-template-columns:46px minmax(0,1fr)!important;
  }

  .svc-presentation-open-btn{
    grid-column:1 / -1!important;
    width:100%!important;
  }

  .svc-presentation-sheet-hero{
    grid-template-columns:70px minmax(0,1fr)!important;
    padding:12px!important;
  }

  .svc-presentation-sheet-preview{
    width:70px!important;
    height:70px!important;
    border-radius:20px!important;
  }

  .svc-presentation-mode-cards{
    grid-template-columns:1fr!important;
  }

  .svc-icon-grid-modal{
    grid-template-columns:repeat(3,minmax(0,1fr))!important;
    max-height:280px!important;
  }

  .svc-image-mode-panel{
    grid-template-columns:1fr!important;
  }
}

@media(max-width:430px){
  .svc-icon-grid-modal{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
  }
}



/* =========================================================
   LOOP SERVICES — PRESENTATION MINIMAL TRIGGER + SAVE BUTTON FIX
   - The first edit-service popup no longer repeats icon/image preview.
   - Presentation becomes one clean row with only "Cambiar presentación".
   - Footer primary save button is forced visible.
   ========================================================= */

.svc-option1-presentation.svc-presentation-minimal-trigger{
  display:grid!important;
  grid-template-columns:minmax(0,1fr) auto!important;
  align-items:center!important;
  gap:14px!important;
  padding:16px!important;
  border:1px solid rgba(226,232,240,.95)!important;
  border-radius:22px!important;
  background:#fff!important;
  box-shadow:none!important;
}

.svc-presentation-minimal-copy{
  display:grid!important;
  gap:3px!important;
  min-width:0!important;
}

.svc-presentation-minimal-copy strong{
  color:#101828!important;
  font-size:15px!important;
  line-height:1.1!important;
  font-weight:950!important;
}

.svc-presentation-minimal-copy span{
  color:#667085!important;
  font-size:12px!important;
  line-height:1.25!important;
  font-weight:800!important;
  white-space:nowrap!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
}

.svc-presentation-minimal-trigger .svc-presentation-summary-card,
.svc-presentation-minimal-trigger > .svc-option1-mini-media{
  display:none!important;
}

.svc-presentation-minimal-trigger .svc-presentation-open-btn{
  height:44px!important;
  min-height:44px!important;
  border-radius:14px!important;
  border:1px solid rgba(203,213,225,.95)!important;
  background:#ffffff!important;
  color:#00675f!important;
  padding:0 16px!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  font-size:12px!important;
  line-height:1!important;
  font-weight:950!important;
  white-space:nowrap!important;
  cursor:pointer!important;
  box-shadow:0 8px 18px rgba(15,23,42,.035)!important;
}

.svc-presentation-minimal-trigger .svc-presentation-open-btn:hover{
  border-color:rgba(0,103,95,.32)!important;
  box-shadow:0 12px 26px rgba(15,23,42,.06)!important;
}

/* Footer/button visibility fix */
.svc-sheet-foot,
.svc-sheet-top-layer .svc-sheet-foot{
  position:sticky!important;
  bottom:0!important;
  z-index:5!important;
  display:flex!important;
  align-items:center!important;
  justify-content:space-between!important;
  gap:14px!important;
  background:rgba(255,255,255,.98)!important;
  border-top:1px solid rgba(226,232,240,.92)!important;
  backdrop-filter:blur(18px)!important;
  -webkit-backdrop-filter:blur(18px)!important;
}

.svc-sheet-foot .svc-actions,
.svc-sheet-foot .svc-edit-actions,
.svc-sheet-foot .svc-add-actions,
.svc-sheet-foot .svc-presentation-sheet-actions,
.svc-sheet-foot > div:not(.svc-sheet-danger){
  display:flex!important;
  align-items:center!important;
  justify-content:flex-end!important;
  gap:10px!important;
  margin-left:auto!important;
}

.svc-sheet-foot button,
.svc-sheet-foot .svc-actions button,
.svc-sheet-foot .svc-edit-actions button,
.svc-sheet-foot .svc-add-actions button{
  min-width:118px!important;
  height:48px!important;
  min-height:48px!important;
  border-radius:18px!important;
  border:1px solid rgba(203,213,225,.95)!important;
  background:#ffffff!important;
  color:#101828!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  padding:0 18px!important;
  font-size:13px!important;
  line-height:1!important;
  font-weight:950!important;
  opacity:1!important;
  visibility:visible!important;
  text-indent:0!important;
  overflow:visible!important;
  white-space:nowrap!important;
}

.svc-sheet-foot button.primary,
.svc-sheet-foot .primary,
.svc-sheet-foot button[type="submit"].primary,
.svc-sheet-foot .svc-actions .primary,
.svc-sheet-foot .svc-edit-actions .primary,
.svc-sheet-foot .svc-add-actions .primary{
  background:#00796f!important;
  border-color:#00796f!important;
  color:#ffffff!important;
  box-shadow:0 18px 36px rgba(0,103,95,.22)!important;
}

.svc-sheet-foot button.primary:disabled,
.svc-sheet-foot .primary:disabled{
  background:#e8f6f3!important;
  border-color:#e8f6f3!important;
  color:#00796f!important;
  box-shadow:none!important;
  opacity:.72!important;
}

/* In case an icon-only/blank class is winning over the primary save button */
.svc-sheet-foot button.primary::before,
.svc-sheet-foot button.primary::after{
  display:none!important;
}

@media(max-width:760px){
  .svc-option1-presentation.svc-presentation-minimal-trigger{
    grid-template-columns:1fr!important;
    padding:14px!important;
  }

  .svc-presentation-minimal-trigger .svc-presentation-open-btn{
    width:100%!important;
  }

  .svc-sheet-foot,
  .svc-sheet-top-layer .svc-sheet-foot{
    flex-direction:column!important;
    align-items:stretch!important;
  }

  .svc-sheet-foot .svc-actions,
  .svc-sheet-foot .svc-edit-actions,
  .svc-sheet-foot .svc-add-actions,
  .svc-sheet-foot .svc-presentation-sheet-actions,
  .svc-sheet-foot > div:not(.svc-sheet-danger){
    width:100%!important;
    margin-left:0!important;
    display:grid!important;
    grid-template-columns:1fr 1fr!important;
  }

  .svc-sheet-foot button{
    width:100%!important;
    min-width:0!important;
  }
}


/* =========================================================
   LOOP SERVICE EDITOR PREMIUM COMPACT V1
   Visual polish only: less nesting, clearer hierarchy, lighter typography.
   ========================================================= */
@media (min-width: 821px){
  .svc-sheet{width:min(920px,calc(100vw - 44px))!important;}
  .svc-sheet-head{padding:16px 20px 14px!important;}
  .svc-sheet-head h3{font-size:21px!important;font-weight:850!important;}
  .svc-sheet-head span{font-weight:600!important;}
  .svc-sheet-body{padding:18px 20px 16px!important;}

  .svc-standard-service-editor.svc-option1-editor{
    border:0!important;border-radius:0!important;padding:0!important;
    background:transparent!important;box-shadow:none!important;
  }
  .svc-standard-service-editor .svc-option1-main{
    grid-template-columns:176px minmax(0,1fr)!important;
    gap:20px!important;align-items:start!important;
  }
  .svc-standard-service-editor .svc-option1-preview{
    min-height:150px!important;border:0!important;border-radius:16px!important;
    background:#f7faf9!important;padding:14px 12px!important;box-shadow:none!important;
  }
  .svc-standard-service-editor .svc-option1-preview>span{
    justify-self:center!important;margin-bottom:10px!important;color:#7b8794!important;
    font-size:9.5px!important;font-weight:750!important;letter-spacing:.07em!important;
    text-transform:uppercase!important;
  }
  .svc-standard-service-editor .svc-option1-preview-media{
    width:58px!important;height:58px!important;margin-bottom:9px!important;border-radius:17px!important;
  }
  .svc-standard-service-editor .svc-option1-preview-media .svc-service-icon-visual{
    width:66px!important;height:66px!important;border-radius:20px!important;
  }
  .svc-standard-service-editor .svc-option1-preview strong{
    max-width:150px!important;color:#182230!important;font-size:14px!important;
    font-weight:800!important;line-height:1.2!important;
  }
  .svc-standard-service-editor .svc-option1-preview p{
    margin-top:5px!important;color:#475467!important;font-size:11px!important;font-weight:650!important;
  }

  .svc-standard-service-editor .svc-option1-fields{gap:12px!important;}
  .svc-standard-service-editor .svc-option1-fields .svc-field label,
  .svc-standard-service-editor .svc-option1-unit-field>span{
    color:#536071!important;font-size:11px!important;font-weight:700!important;
  }
  .svc-standard-service-editor .svc-option1-fields .svc-field input,
  .svc-standard-service-editor .svc-option1-input-unit{
    height:42px!important;min-height:42px!important;border-radius:12px!important;font-weight:700!important;
  }
  .svc-standard-service-editor .svc-option1-field-grid{
    grid-template-columns:minmax(150px,.52fr) minmax(360px,1.48fr)!important;
    gap:12px!important;align-items:start!important;
  }
  .svc-standard-service-editor .svc-option1-price-field{gap:6px!important;}
  .svc-standard-service-editor .svc-price-compact-row{
    display:grid!important;grid-template-columns:190px minmax(120px,1fr)!important;
    gap:8px!important;align-items:center!important;
  }
  .svc-standard-service-editor .svc-standard-price-mode{
    width:100%!important;max-width:none!important;min-height:42px!important;padding:3px!important;
    border-radius:12px!important;background:#f3f6f8!important;
  }
  .svc-standard-service-editor .svc-standard-price-mode button{
    min-height:34px!important;border-radius:9px!important;font-size:11px!important;font-weight:700!important;
  }
  .svc-standard-service-editor .svc-standard-price-mode button.active{
    background:#fff!important;color:#00675f!important;box-shadow:0 2px 7px rgba(15,23,42,.07)!important;
  }
  .svc-standard-service-editor .svc-price-public-preview{
    margin-top:0!important;color:#7b8794!important;font-size:10px!important;font-weight:550!important;
  }
  .svc-standard-service-editor .svc-price-public-preview strong{color:#00675f!important;font-weight:750!important;}

  .svc-standard-service-editor .svc-option1-presentation.svc-presentation-minimal-trigger{
    margin-top:16px!important;padding:13px 2px 0!important;border:0!important;
    border-top:1px solid #e7edf1!important;border-radius:0!important;background:transparent!important;
    box-shadow:none!important;display:grid!important;grid-template-columns:minmax(0,1fr) auto!important;
    align-items:center!important;gap:14px!important;
  }
  .svc-standard-service-editor .svc-presentation-minimal-copy{
    display:grid!important;grid-template-columns:auto minmax(0,1fr)!important;align-items:center!important;gap:12px!important;
  }
  .svc-standard-service-editor .svc-presentation-minimal-copy strong{
    color:#182230!important;font-size:12.5px!important;font-weight:800!important;
  }
  .svc-standard-service-editor .svc-presentation-minimal-copy span{
    color:#667085!important;font-size:11px!important;font-weight:600!important;text-align:left!important;
  }
  .svc-standard-service-editor .svc-presentation-open-btn{
    min-height:36px!important;height:36px!important;border-radius:11px!important;padding:0 13px!important;
    font-size:11.5px!important;font-weight:750!important;box-shadow:none!important;
  }

  .svc-materials-compact-card{
    margin-top:10px!important;border:0!important;border-top:1px solid #e7edf1!important;
    border-radius:0!important;background:transparent!important;padding:14px 2px 0!important;
    box-shadow:none!important;grid-template-columns:minmax(0,1fr) auto auto!important;gap:12px!important;
  }
  .svc-materials-compact-card.empty{grid-template-columns:minmax(0,1fr) auto!important;}
  .svc-materials-compact-copy strong{
    margin:0!important;color:#182230!important;font-size:12.5px!important;font-weight:800!important;letter-spacing:0!important;
  }
  .svc-materials-compact-copy p{
    margin:3px 0 0!important;color:#667085!important;font-size:11px!important;font-weight:550!important;
  }
  .svc-materials-compact-metrics{gap:6px!important;}
  .svc-materials-compact-metrics div{
    min-width:68px!important;border:0!important;border-radius:10px!important;background:#f7f9fa!important;padding:6px 8px!important;
  }
  .svc-materials-compact-metrics span{font-size:8.5px!important;font-weight:700!important;}
  .svc-materials-compact-metrics strong{font-size:11.5px!important;font-weight:750!important;}
  .svc-materials-config-btn{
    min-height:36px!important;height:36px!important;border-radius:11px!important;background:#fff!important;
    color:#00675f!important;border:1px solid rgba(0,103,95,.24)!important;box-shadow:none!important;
    padding:0 13px!important;font-size:11.5px!important;font-weight:750!important;
  }

  .svc-sheet-foot{padding:12px 20px!important;}
  .svc-sheet-foot button{min-height:42px!important;border-radius:12px!important;font-weight:750!important;}
}

@media (max-width: 820px){
  .svc-standard-service-editor .svc-option1-price-field{gap:6px!important;}
  .svc-standard-service-editor .svc-price-compact-row{
    display:grid!important;grid-template-columns:1fr minmax(110px,.72fr)!important;gap:8px!important;
  }
  .svc-standard-service-editor .svc-standard-price-mode{max-width:none!important;width:100%!important;}
  .svc-standard-service-editor .svc-price-public-preview{font-size:10.5px!important;}
  .svc-standard-service-editor .svc-presentation-minimal-copy span{font-weight:600!important;}
  .svc-materials-compact-copy strong{font-weight:800!important;}
  .svc-materials-compact-copy p{font-weight:550!important;}
}


/* =========================================================
   LOOP SERVICE DESCRIPTION V1
   Optional 160-char service summary + compact preview/editor.
   ========================================================= */
.svc-service-description-field{
  display:grid;
  gap:6px;
}
.svc-service-description-field>span{
  display:flex;
  align-items:center;
  gap:7px;
  color:#536071;
  font-size:11px;
  line-height:1.2;
}
.svc-service-description-field>span strong{
  font-weight:700;
}
.svc-service-description-field>span em{
  font-style:normal;
  color:#98a2b3;
  font-size:9.5px;
  font-weight:650;
}
.svc-service-description-field textarea{
  width:100%;
  min-height:62px;
  max-height:92px;
  resize:vertical;
  border:1px solid #dbe5ea;
  border-radius:12px;
  background:#fff;
  color:#182230;
  padding:10px 11px;
  outline:none;
  font:inherit;
  font-size:12px;
  font-weight:600;
  line-height:1.4;
  box-sizing:border-box;
  transition:border-color .16s ease, box-shadow .16s ease;
}
.svc-service-description-field textarea:focus{
  border-color:rgba(0,103,95,.42);
  box-shadow:0 0 0 3px rgba(0,103,95,.07);
}
.svc-service-description-field textarea::placeholder{
  color:#a0aab5;
  font-weight:500;
}
.svc-service-description-field>small{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
  color:#98a2b3;
  font-size:9.5px;
  font-weight:550;
  line-height:1.25;
}
.svc-service-description-field>small b{
  flex:0 0 auto;
  color:#667085;
  font-weight:700;
}
.svc-option1-preview-description{
  width:100%;
  max-width:150px;
  margin:4px auto 1px;
  color:#667085;
  font-size:9.5px;
  font-weight:550;
  line-height:1.25;
  text-align:center;
  display:-webkit-box;
  -webkit-line-clamp:2;
  -webkit-box-orient:vertical;
  overflow:hidden;
}
@media (min-width:821px){
  .svc-standard-service-editor .svc-option1-fields{
    gap:10px!important;
  }
  .svc-standard-service-editor .svc-option1-preview{
    min-height:166px!important;
  }
}
@media (max-width:820px){
  .svc-service-description-field textarea{
    min-height:68px;
  }
  .svc-option1-preview-description{
    max-width:220px;
  }
}


/* =========================================================
   LOOP SERVICES ADMIN POLISH V1
   Cleaner operating list: description visibility, collision-safe
   price/material columns, meaningful material economics, compact
   ordering controls, and clearer row click affordance.
   ========================================================= */
.svc-minimal-shell{
  gap:11px!important;
}
.svc-order-note{
  min-height:34px!important;
  padding:0 12px!important;
  border-radius:13px!important;
}
.svc-order-note strong{
  font-size:10px!important;
}
.svc-order-note span{
  font-size:11px!important;
}
.svc-minimal-summary{
  gap:6px!important;
  margin-top:-1px!important;
}
.svc-op-row{
  grid-template-columns:48px minmax(260px,1.4fr) 86px 118px 172px minmax(265px,auto)!important;
  min-height:84px!important;
  gap:14px!important;
  transition:background 140ms ease, transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease!important;
  outline:none!important;
}
.svc-op-row:hover,
.svc-op-row:focus-visible{
  background:#fff!important;
  border-color:rgba(0,103,95,.24)!important;
  box-shadow:0 16px 36px rgba(16,24,40,.075)!important;
  transform:translateY(-1px)!important;
}
.svc-op-row:focus-visible{
  box-shadow:0 0 0 3px rgba(0,103,95,.08),0 16px 36px rgba(16,24,40,.075)!important;
}
.svc-op-main{
  min-width:0!important;
  gap:2px!important;
  align-content:center!important;
}
.svc-op-main strong{
  line-height:1.12!important;
}
.svc-op-description{
  display:block!important;
  min-width:0!important;
  max-width:100%!important;
  margin:1px 0 0!important;
  color:#475467!important;
  font-size:11.3px!important;
  line-height:1.22!important;
  font-weight:600!important;
  white-space:nowrap!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
}
.svc-op-main .svc-op-meta{
  margin-top:1px!important;
  color:#98a2b3!important;
  font-size:10.4px!important;
  line-height:1.18!important;
  font-weight:600!important;
}
.svc-op-value{
  min-width:0!important;
}
.svc-op-value strong{
  white-space:nowrap!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
}
.svc-op-price.is-missing strong{
  color:#9a3412!important;
}
.svc-op-materials strong{
  white-space:nowrap!important;
}
.svc-op-materials em{
  display:block!important;
  margin-top:2px!important;
  max-width:100%!important;
  color:#667085!important;
  font-size:9.7px!important;
  line-height:1.16!important;
  font-weight:600!important;
  white-space:nowrap!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
}
.svc-op-materials.no-materials em{
  display:none!important;
}
.svc-op-actions{
  display:flex!important;
  align-items:center!important;
  justify-content:flex-end!important;
  gap:7px!important;
  min-width:265px!important;
  width:auto!important;
}
.svc-order-controls{
  width:auto!important;
  min-width:108px!important;
  height:32px!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:flex-end!important;
  grid-template-columns:none!important;
  gap:4px!important;
  padding:3px 4px 3px 8px!important;
  border:1px solid #e7ded2!important;
  border-radius:999px!important;
  background:#fbfaf7!important;
  box-sizing:border-box!important;
}
.svc-order-controls>span{
  width:auto!important;
  min-width:42px!important;
  height:24px!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:flex-start!important;
  padding:0!important;
  border:0!important;
  border-radius:0!important;
  background:transparent!important;
  color:#667085!important;
  font-size:10px!important;
  font-weight:750!important;
  white-space:nowrap!important;
}
.svc-order-controls>div{
  width:auto!important;
  display:flex!important;
  grid-template-columns:none!important;
  gap:3px!important;
}
.svc-order-controls button{
  width:22px!important;
  height:22px!important;
  min-width:22px!important;
  min-height:22px!important;
  border-radius:999px!important;
}
.svc-op-visibility{
  flex:0 0 auto!important;
}

@media (min-width:821px) and (max-width:1050px){
  .svc-op-row{
    grid-template-columns:48px minmax(190px,1fr) 82px 108px minmax(250px,auto)!important;
    gap:11px!important;
  }
  .svc-op-materials{
    display:none!important;
  }
  .svc-op-actions{
    min-width:250px!important;
  }
}

@media (max-width:820px){
  .svc-minimal-shell{
    gap:10px!important;
  }
  .svc-op-row{
    grid-template-columns:44px minmax(0,1fr)!important;
    gap:9px 11px!important;
  }
  .svc-op-main{
    gap:2px!important;
  }
  .svc-op-description{
    font-size:10.8px!important;
    line-height:1.18!important;
  }
  .svc-op-main .svc-op-meta{
    font-size:10px!important;
  }
  .svc-op-row > .svc-op-price{
    max-width:132px!important;
  }
  .svc-op-actions{
    min-width:0!important;
    width:100%!important;
    display:grid!important;
    grid-template-columns:minmax(0,1fr) auto auto!important;
    gap:7px!important;
  }
  .svc-order-controls{
    min-width:104px!important;
    height:30px!important;
  }
  .svc-order-controls>span{
    min-width:40px!important;
    font-size:9.7px!important;
  }
}

@media (max-width:430px){
  .svc-order-controls{
    min-width:96px!important;
    padding-left:6px!important;
  }
  .svc-order-controls>span{
    min-width:35px!important;
    font-size:9.2px!important;
  }
}

@media (max-width:380px){
  .svc-order-controls{
    display:none!important;
  }
}


/* =========================================================
   LOOP A5.3A — PAYMENT POLICY PER SERVICE
   Local-first owner configuration for pre-booking payment.
   ========================================================= */
.svc-booking-payment-policy{
  display:grid;
  gap:12px;
  padding:16px!important;
  border:1px solid rgba(0,103,95,.14)!important;
  border-radius:22px!important;
  background:linear-gradient(180deg,#ffffff 0%,#fbfffe 100%)!important;
}
.svc-booking-payment-head{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:14px;
}
.svc-booking-payment-head>div{
  min-width:0;
  display:grid;
  gap:3px;
}
.svc-booking-payment-head strong{
  color:#101828;
  font-size:14px;
  font-weight:900;
}
.svc-booking-payment-head span{
  color:#667085;
  font-size:11.5px;
  line-height:1.35;
  font-weight:650;
}
.svc-booking-payment-status{
  flex:0 0 auto;
  min-height:28px;
  display:inline-flex;
  align-items:center;
  padding:0 10px;
  border:1px solid #e4e7ec;
  border-radius:999px;
  background:#f9fafb;
  color:#667085!important;
  font-size:10.5px!important;
  font-weight:850!important;
  white-space:nowrap;
}
.svc-booking-payment-status.active{
  border-color:rgba(0,103,95,.22);
  background:#ecfdf7;
  color:#00675f!important;
}
.svc-booking-payment-options{
  display:grid;
  grid-template-columns:repeat(4,minmax(0,1fr));
  gap:8px;
}
.svc-booking-payment-option{
  min-height:78px;
  display:grid;
  align-content:start;
  gap:4px;
  padding:11px;
  border:1px solid #e4e7ec;
  border-radius:16px;
  background:#fff;
  text-align:left;
  cursor:pointer;
  transition:border-color 140ms ease,background 140ms ease,box-shadow 140ms ease,transform 140ms ease;
}
.svc-booking-payment-option:hover:not(:disabled){
  transform:translateY(-1px);
  border-color:rgba(0,103,95,.22);
}
.svc-booking-payment-option.active{
  border-color:rgba(0,103,95,.34);
  background:#f2fffb;
  box-shadow:0 0 0 2px rgba(0,103,95,.05);
}
.svc-booking-payment-option:disabled{
  opacity:.48;
  cursor:not-allowed;
}
.svc-booking-payment-option strong{
  color:#101828;
  font-size:11.5px;
  font-weight:900;
}
.svc-booking-payment-option span{
  color:#667085;
  font-size:10px;
  line-height:1.28;
  font-weight:650;
}
.svc-booking-payment-note{
  display:grid;
  gap:2px;
  padding:10px 12px;
  border:1px solid #fedf89;
  border-radius:14px;
  background:#fffaeb;
}
.svc-booking-payment-note strong{
  color:#93370d;
  font-size:10.8px;
  font-weight:900;
}
.svc-booking-payment-note span{
  color:#b54708;
  font-size:10.5px;
  line-height:1.3;
  font-weight:650;
}
.svc-booking-payment-control{
  display:flex;
  align-items:flex-end;
  gap:10px;
}
.svc-booking-payment-control>label{
  display:grid;
  gap:5px;
}
.svc-booking-payment-control>label>span{
  color:#475467;
  font-size:10.5px;
  font-weight:850;
}
.svc-booking-payment-control>small{
  padding-bottom:8px;
  color:#667085;
  font-size:10px;
  font-weight:650;
}
.svc-booking-payment-input{
  width:150px;
  min-height:38px;
  display:flex;
  align-items:center;
  overflow:hidden;
  border:1px solid #d0d5dd;
  border-radius:12px;
  background:#fff;
}
.svc-booking-payment-input input{
  width:100%;
  min-width:0;
  height:36px;
  padding:0 11px;
  border:0!important;
  outline:0!important;
  background:transparent!important;
  box-shadow:none!important;
  color:#101828;
  font-size:13px;
  font-weight:850;
}
.svc-booking-payment-input em{
  flex:0 0 auto;
  color:#475467;
  font-size:12px;
  font-style:normal;
  font-weight:850;
}
.svc-booking-payment-input.prefix em{
  padding-left:11px;
}
.svc-booking-payment-input.suffix em{
  padding-right:11px;
}
.svc-booking-payment-preview{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  min-height:42px;
  padding:10px 12px;
  border:1px solid #e4e7ec;
  border-radius:14px;
  background:#f9fafb;
}
.svc-booking-payment-preview.active{
  border-color:rgba(0,103,95,.18);
  background:#f5fffc;
}
.svc-booking-payment-preview strong{
  color:#344054;
  font-size:10.5px;
  font-weight:900;
}
.svc-booking-payment-preview span{
  color:#475467;
  font-size:10.5px;
  line-height:1.3;
  font-weight:700;
  text-align:right;
}
@media (max-width:820px){
  .svc-booking-payment-options{
    grid-template-columns:repeat(2,minmax(0,1fr));
  }
  .svc-booking-payment-head,
  .svc-booking-payment-preview{
    align-items:flex-start;
    flex-direction:column;
  }
  .svc-booking-payment-preview span{
    text-align:left;
  }
}
@media (max-width:430px){
  .svc-booking-payment-options{
    grid-template-columns:1fr;
  }
  .svc-booking-payment-control{
    align-items:stretch;
    flex-direction:column;
  }
  .svc-booking-payment-input{
    width:100%;
  }
}



.svc-op-price strong{white-space:normal!important;overflow:visible!important;text-overflow:clip!important;overflow-wrap:anywhere;}
.svc-op-actions{flex-wrap:wrap;}
.svc-option1-preview{align-self:start;}

/* Events catalog and focused editor */
.event-catalog-redesign .svc-op-row{grid-template-columns:52px minmax(180px,1.4fr) minmax(190px,1fr) minmax(180px,auto)!important;gap:16px!important;padding:20px!important;align-items:center!important;}
.event-catalog-redesign .svc-op-row>.svc-op-value:not(.svc-op-price),.event-catalog-redesign .svc-op-materials{display:none!important;}
.event-catalog-redesign .svc-op-price{min-width:0!important;width:auto!important;grid-column:auto!important;grid-area:auto!important;}
.event-catalog-redesign .svc-op-price>span{display:none!important;}
.event-tariff-list{display:grid;gap:8px;min-width:0;}
.event-tariff-list>div{display:grid;gap:3px;}
.event-tariff-list span{font-size:12px;color:#536963;font-weight:400;}
.event-tariff-list strong{font-size:14px!important;font-weight:600!important;line-height:1.5!important;white-space:normal!important;}
.event-catalog-redesign .svc-op-actions{display:flex!important;flex-wrap:wrap!important;gap:8px!important;justify-content:flex-end!important;grid-area:auto!important;position:static!important;}
.event-catalog-redesign .svc-op-actions>button:first-child{border:1px solid #b8d2cc;background:white;border-radius:10px;min-height:40px;padding:0 16px;color:#00675f;font-weight:600;cursor:pointer;}
.event-catalog-redesign:not(.is-reordering) .svc-order-controls{display:none!important;}
.event-catalog-redesign .svc-order-note{background:transparent!important;border:0!important;padding:0!important;justify-content:flex-end!important;}
.event-catalog-redesign .svc-order-note>button{border:1px solid #d6e4df;background:white;padding:10px 14px;border-radius:10px;color:#00675f;cursor:pointer;}
.event-catalog-redesign .svc-order-note>span,.event-catalog-redesign .svc-minimal-summary .materials,.event-catalog-redesign .svc-minimal-summary .warn{display:none!important;}
.event-catalog-attention{display:flex;flex-direction:column;gap:5px;text-align:left;background:#fff9ec;border:1px solid #ead9b6;border-radius:12px;padding:14px 16px;color:#74531b;cursor:pointer;}
.event-catalog-attention span{font-size:13px;font-weight:400;}
.event-catalog-redesign .svc-op-warning{display:block!important;font-size:11px;color:#8b571f;}
.event-editor-workspace{display:grid;grid-template-columns:190px minmax(0,1fr);gap:24px;align-items:start;}
.event-editor-nav{display:grid;gap:8px;position:sticky;top:0;}
.event-editor-nav button{display:flex;align-items:flex-start;gap:10px;text-align:left;border:0;background:transparent;border-radius:12px;padding:13px 10px;color:#425751;cursor:pointer;}
.event-editor-nav button[aria-current]{background:#e9f5f1;color:#00675f;}
.event-editor-nav button>span{display:grid;place-items:center;flex-shrink:0;width:22px;height:22px;border:1px solid #c8dad4;border-radius:50%;font-size:12px;}
.event-editor-nav strong{display:block;font-size:13px;font-weight:600;}
.event-editor-nav small{display:block;font-size:11px;line-height:1.5;margin-top:5px;font-weight:400;}
.event-editor-content{min-width:0;display:grid;gap:18px;}
.event-editor-content>header{display:flex;gap:12px;align-items:center;justify-content:space-between;}
.event-editor-content h3{font-size:21px;margin:0;letter-spacing:-.4px;}
.event-editor-content header p,.event-editor-help{font-size:13px;color:#60736d;line-height:1.6;margin:5px 0 0;}
.event-editor-content header button{border:1px solid #cfdfda;background:white;border-radius:10px;padding:10px;color:#00675f;font-size:12px;cursor:pointer;}
.event-editor-preview{border:1px solid #c9ded7;border-radius:14px;background:#f7fbf9;padding:18px;}
.event-editor-preview p{font-size:13px;line-height:1.5;}
.event-presentation-only .svc-option1-preview,.event-presentation-only .svc-option1-field-grid,.event-presentation-only .svc-experience-pricing-editor{display:none!important;}
.event-presentation-only .svc-option1-main{display:block!important;}
.event-editor-content .svc-editor-section{padding:0!important;border:0!important;background:transparent!important;box-shadow:none!important;}
.event-editor-content .svc-event-lite-head{display:none!important;}
.event-editor-content .svc-experience-price-row{display:grid!important;grid-template-columns:auto minmax(0,1fr)!important;gap:14px!important;padding:18px!important;border:1px solid #dbe6e2!important;border-radius:14px!important;background:white!important;}
.event-editor-content .svc-price-mode-mini,.event-editor-content .svc-money-input{width:100%!important;min-width:0!important;}
.event-editor-content .svc-money-input input{min-width:0;width:100%;}
.event-editor-content .svc-experience-price-row details{border-top:1px solid #e4ece8;padding-top:12px;}
.event-editor-content details p{font-size:13px;color:#60736d;line-height:1.6;}
.event-editor-content details summary{font-size:13px;font-weight:600;cursor:pointer;padding:7px 0;}
.event-editor-content details button{border:1px solid #caddd6;border-radius:9px;padding:9px 12px;background:white;color:#00675f;cursor:pointer;}
.event-editor-content details input{box-sizing:border-box;min-width:0;max-width:100%;border:1px solid #d6e2dd;border-radius:9px;padding:10px;}
.event-editor-advanced{border:1px solid #dce7e2;border-radius:12px;padding:14px;}
@media(max-width:1000px){.event-catalog-redesign .svc-op-row{grid-template-columns:48px minmax(0,1fr)!important;}.event-catalog-redesign .svc-op-price{grid-column:2!important;}.event-catalog-redesign .svc-op-actions{grid-column:1/-1!important;}.event-catalog-redesign .event-tariff-list{grid-template-columns:repeat(2,minmax(0,1fr));}}
@media(max-width:640px){.event-editor-workspace{grid-template-columns:minmax(0,1fr);gap:16px;}.event-editor-nav{position:static;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px;}.event-editor-nav small{display:none;}.event-editor-nav button{padding:10px 8px;}.event-editor-nav strong{font-size:12px;}.event-editor-content>header{align-items:flex-start;}.event-editor-content h3{font-size:19px;}.event-editor-content header button{max-width:95px;}.event-editor-content .svc-experience-price-row{padding:12px!important;}.event-catalog-redesign .svc-op-row{padding:14px!important;}.event-catalog-redesign .event-tariff-list{grid-template-columns:1fr;}}

/* Focused event editing: one primary decision at a time. */
.event-editor-workspace.event-editor-quiet{grid-template-columns:140px minmax(0,1fr);gap:28px;}
.event-editor-quiet .event-editor-nav{gap:4px;}
.event-editor-quiet .event-editor-nav button{padding:12px 14px;border-radius:8px;}
.event-editor-quiet .event-editor-nav strong{font-size:14px;font-weight:500;}
.event-editor-quiet .event-editor-content{gap:20px;}
.event-editor-quiet .svc-option1-presentation{border:0!important;border-top:1px solid #e8eeeb!important;border-radius:0!important;padding:16px 0!important;margin-top:10px!important;box-shadow:none!important;}
.event-editor-quiet .svc-option1-presentation .svc-presentation-minimal-copy strong{font-size:0!important;}
.event-editor-quiet .svc-option1-presentation .svc-presentation-minimal-copy strong::after{content:'Imagen';font-size:13px;font-weight:500;}
.event-editor-quiet .svc-service-description-field small{display:none!important;}
.event-editor-quiet .svc-experience-pricing-editor>.svc-event-mini-head{display:none!important;}
.event-editor-quiet .svc-experience-price-row{grid-template-columns:minmax(0,1fr) 140px!important;gap:12px!important;padding:16px 0!important;border:0!important;border-bottom:1px solid #e4ebe7!important;border-radius:0!important;box-shadow:none!important;}
.event-editor-quiet .svc-experience-price-row>strong{grid-row:1;grid-column:1;font-size:15px!important;font-weight:600!important;}
.event-editor-quiet .svc-experience-price-row>.svc-mini-toggle{grid-row:1;grid-column:2;justify-self:end;width:auto!important;min-width:0!important;min-height:28px!important;padding:4px 8px!important;border:0!important;background:transparent!important;color:#60756d!important;font-size:12px!important;}
.event-editor-quiet .svc-price-mode-mini{width:fit-content!important;background:#f4f7f5!important;box-shadow:none!important;}
.event-editor-quiet .svc-money-input{min-height:40px!important;}
.event-editor-quiet .svc-experience-price-row details{border:0!important;padding-top:0!important;}
.event-editor-quiet details summary{font-weight:500;font-size:13px;color:#49685e;}
.event-editor-quiet .svc-experience-price-row details>p{display:none;}
.event-editor-quiet .event-add-experience{margin-top:14px;border:0;background:transparent;padding:8px 0;color:#00675f;font-weight:600;cursor:pointer;}
.event-editor-quiet .event-editor-advanced,.event-editor-quiet .event-more-options{padding:12px 0;border:0;border-top:1px solid #e4ebe7;border-radius:0;}
.event-editor-quiet .svc-event-lite-block{border:0!important;border-bottom:1px solid #e4ebe7!important;border-radius:0!important;box-shadow:none!important;background:transparent!important;}
.event-editor-quiet .svc-event-lite-summary{padding:12px 0!important;}
.event-editor-quiet .svc-event-lite-summary span,.event-editor-quiet .svc-event-lite-summary em{display:none!important;}
.event-editor-quiet .svc-event-lite-body{padding:12px 0!important;border:0!important;}
.event-editor-quiet .svc-field{display:grid;gap:8px;margin-bottom:14px;min-width:0;}
.event-editor-quiet .svc-field>span{margin:0!important;font-weight:500!important;}
.event-editor-quiet .svc-field select{display:block;box-sizing:border-box;width:100%;min-width:0;min-height:44px;padding:10px 12px;border:1px solid #d7e3dd;border-radius:9px;background:white;font-size:14px;font-weight:400;}
.event-editor-quiet .svc-field small{display:block;font-size:12px;font-weight:400;line-height:1.5;color:#687c72;}
.event-editor-quiet .svc-character-editor{padding:0!important;border:0!important;background:transparent!important;box-shadow:none!important;}
.event-editor-quiet .svc-character-empty,.event-editor-quiet .svc-character-editor>.svc-help-text{display:none!important;}
.event-editor-quiet .svc-character-editor-head{margin:12px 0!important;}
.event-editor-quiet .svc-character-editor-head strong{font-weight:600!important;}
.event-characters-simple{display:grid;gap:12px;}
.event-booking-overview dl{margin:0;display:grid;gap:0;}
.event-booking-overview dl>div{padding:16px 0;border-bottom:1px solid #e4ebe7;display:grid;grid-template-columns:1fr 1fr;gap:16px;}
.event-booking-overview dt{font-size:14px;color:#566e62;}
.event-booking-overview dd{margin:0;text-align:right;font-size:14px;font-weight:500;}
.event-booking-overview>button{margin-top:18px;padding:10px 16px;background:white;border:1px solid #cadcd2;border-radius:9px;color:#00675f;cursor:pointer;}
.event-editor-quiet .svc-booking-payment-options{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
.event-editor-quiet .svc-booking-payment-option span,.event-editor-quiet .svc-booking-payment-preview{display:none!important;}
.event-delete-menu{position:relative;font-size:12px;color:#67766f;}
.event-delete-menu summary{cursor:pointer;padding:10px;}
.event-delete-menu>button{position:absolute;bottom:100%;left:0;background:white;white-space:nowrap;box-shadow:0 4px 20px #0002;}
.svc-sheet:has(.event-editor-quiet)>.svc-sheet-head span{display:none!important;}
@media(max-width:640px){.event-editor-workspace.event-editor-quiet{grid-template-columns:1fr;gap:20px;}.event-editor-quiet .event-editor-nav{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:2px;}.event-editor-quiet .event-editor-nav button{padding:10px 3px;justify-content:center;}.event-editor-quiet .event-editor-nav strong{font-size:12px;}.event-editor-quiet .svc-experience-price-row{grid-template-columns:minmax(0,1fr) 120px!important;}.event-booking-overview dl>div{grid-template-columns:1fr;gap:6px;}.event-booking-overview dd{text-align:left;}}

.event-editor-quiet .svc-presentation-minimal-trigger{display:flex!important;align-items:center!important;gap:12px!important;}
.event-image-thumbnail{width:52px;height:52px;object-fit:cover;border-radius:10px;}
.event-editor-quiet .svc-presentation-minimal-copy{flex:1;}

/* Compact events catalog. Editor styles remain independent. */
.event-catalog-redesign .svc-minimal-shell{padding:20px!important;gap:16px!important;border-radius:16px!important;box-shadow:none!important;}
.event-catalog-redesign .svc-minimal-header{padding:0!important;border:0!important;min-height:0!important;}
.event-catalog-redesign .svc-minimal-header>div>span,.event-catalog-redesign .svc-minimal-header p,.event-catalog-redesign .svc-minimal-toolbar,.event-catalog-redesign .svc-order-note,.event-catalog-redesign .event-catalog-attention,.event-catalog-redesign .svc-minimal-summary{display:none!important;}
.event-catalog-redesign .svc-minimal-header h3{font-size:22px!important;line-height:1.3!important;margin:0!important;letter-spacing:-.4px!important;}
.event-catalog-toolbar{display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
.event-catalog-toolbar>input{flex:1;min-width:150px;border:1px solid #d5e2dc;border-radius:9px;padding:11px 12px;font-size:14px;}
.event-catalog-toolbar select,.event-catalog-toolbar button{background:white;border:1px solid #d5e2dc;border-radius:9px;padding:11px 12px;color:#365b4e;font-size:13px;cursor:pointer;}
.event-catalog-tools,.event-catalog-more{position:relative;}
.event-catalog-popover{position:absolute;right:0;top:calc(100% + 5px);min-width:205px;padding:6px;background:white;border:1px solid #dce7e1;border-radius:10px;box-shadow:0 8px 24px #17352b24;z-index:20;}
.event-catalog-popover button{display:block;width:100%;text-align:left;border:0!important;border-radius:6px!important;white-space:normal!important;padding:12px!important;color:#294e41;background:white;cursor:pointer;}
.event-catalog-popover button:hover{background:#edf6f1;}
.event-catalog-redesign .svc-op-table{display:block!important;border:0!important;border-radius:0!important;overflow:visible!important;box-shadow:none!important;background:white!important;}
.event-catalog-row{display:flex;align-items:center;gap:18px;padding:16px 0;border-bottom:1px solid #e5ece8;}
.event-catalog-row:last-child{border-bottom:0;}
.event-catalog-identity{display:flex;align-items:center;gap:14px;min-width:0;flex:1;text-align:left;border:0;background:transparent;cursor:pointer;padding:0;}
.event-catalog-identity img,.event-catalog-placeholder{width:46px;height:46px;flex-shrink:0;object-fit:cover;border-radius:9px;}
.event-catalog-placeholder{display:grid;place-items:center;background:#edf5f1;color:#007467;}
.event-catalog-identity>span{min-width:0;}
.event-catalog-identity strong{display:block;font-size:14px;font-weight:600;color:#182923;line-height:1.5;overflow-wrap:anywhere;}
.event-catalog-tariffs{display:block;margin-top:4px;color:#68796f;font-size:13px;font-weight:400;line-height:1.5;overflow-wrap:anywhere;}
.event-catalog-state{min-width:70px;font-size:12px;color:#5b7365;}
.event-catalog-state small{display:block;color:#96652b;font-size:11px;margin-top:4px;}
.event-catalog-actions,.event-catalog-order{display:flex;align-items:center;gap:6px;flex-shrink:0;}
.event-catalog-actions>button,.event-catalog-more>button,.event-catalog-order button{min-height:38px;border:0;border-radius:8px;background:transparent;color:#006c60;padding:8px 10px;cursor:pointer;font-size:13px;}
.event-catalog-more>button{font-size:21px;min-width:38px;}
.event-catalog-order button:disabled{opacity:.35;cursor:default;}
.event-catalog-resources[hidden]{display:none!important;}
.event-catalog-actions button:hover{background:#edf6f1;}
@media(max-width:640px){.event-catalog-redesign .svc-minimal-shell{padding:14px!important;}.event-catalog-toolbar>input{flex-basis:100%;}.event-catalog-toolbar select{flex:1;min-width:0;}.event-catalog-row{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px 12px;padding:14px 0;}.event-catalog-identity{grid-column:1/-1;}.event-catalog-state{padding-left:60px;}.event-catalog-actions{justify-self:end;}.event-catalog-order{grid-column:1/-1;justify-content:flex-end;}.event-catalog-redesign .svc-minimal-header h3{font-size:19px!important;}.event-catalog-redesign .svc-minimal-primary{font-size:12px!important;padding:10px!important;min-height:40px!important;}}

/* Event package workspace: consistent typography and visible decisions. */
.event-editor-workspace.event-editor-v2{grid-template-columns:160px minmax(0,1fr);gap:28px;font-family:inherit;color:#263d37;font-size:14px;line-height:1.5}
.event-editor-v2 .event-editor-content :is(p,small,label,span,input,select,textarea,button){font-family:inherit;letter-spacing:normal}
.event-editor-v2 .event-editor-content :is(input,select,textarea){font-size:14px!important;font-weight:400!important;border-radius:8px!important;min-height:42px;box-shadow:none!important}
.event-editor-v2 .event-editor-content input[type=checkbox]{min-height:0}
.event-editor-v2 .event-editor-content :is(h3,h4){font-size:16px!important;font-weight:600!important;line-height:1.4;letter-spacing:0;margin:0 0 10px}
.event-editor-v2 .event-editor-content>header h3{font-size:20px!important;margin:0 0 6px}
.event-editor-v2 .event-editor-content p{font-size:13px!important;line-height:1.65;color:#61716a;margin:8px 0 16px}
.event-editor-v2 .event-editor-content strong{font-size:14px!important;font-weight:600!important;line-height:1.5}
.event-editor-v2 .event-editor-content :is(small,.svc-field>span){font-size:12px!important;font-weight:400!important}
.event-editor-v2 .event-editor-content button{font-size:13px!important;font-weight:500!important;line-height:1.4}
.event-editor-v2 .event-editor-nav button{min-height:42px}
.event-editor-v2 .event-editor-nav strong{font-size:13px;font-weight:500}
.event-editor-v2 .event-editor-content>header{padding-bottom:16px;border-bottom:1px solid #e4eae6}
.event-editor-v2 .event-editor-note{background:#f5f8f6;padding:12px 14px;border-left:2px solid #a2bcae;border-radius:4px}
.event-editor-v2 .event-visibility{border-top:1px solid #e4eae6;padding-top:18px}
.event-editor-v2 .svc-experience-price-row{grid-template-columns:minmax(0,1fr) 150px!important;padding:18px!important;border:1px solid #dce5df!important;border-radius:10px!important;margin-bottom:16px;background:#fff!important}
.event-editor-v2 .svc-price-mode-mini{display:inline-flex!important;gap:4px!important;padding:4px!important;border:1px solid #e1e8e3!important;border-radius:8px!important;align-self:center;max-width:100%}
.event-editor-v2 .svc-price-mode-mini button{padding:8px 14px!important;min-height:36px!important;width:auto!important;min-width:0!important;border-radius:5px!important;white-space:nowrap;font-size:13px!important}
.event-editor-v2 .svc-price-mode-mini button.active{background:#fff!important;box-shadow:0 1px 4px #18382b12!important}
.event-editor-v2 .svc-quote-chip{background:#f5f7f4!important;border:0!important;border-radius:6px!important;padding:10px!important;font-size:12px!important;font-weight:400!important;align-self:center}
.event-editor-v2 .event-experience-duration{border-top:1px solid #e4eae6!important;padding-top:12px!important}
.event-editor-v2 .svc-experience-price-row details>p{display:block!important}
.event-editor-v2 .event-experience-duration label{flex:1;min-width:100px;font-size:12px;display:grid;gap:6px}
.event-editor-v2 .event-experience-duration input{width:100%;max-width:100%;box-sizing:border-box}
.event-editor-v2 .event-experience-duration button{align-self:end;min-height:40px}
.event-editor-v2 :is(.svc-character-editor-head,.svc-event-mini-head,.svc-booking-payment-head)>div{display:grid;gap:4px}
.event-editor-v2 :is(.svc-character-editor-head,.svc-event-mini-head,.svc-booking-payment-head) span{font-size:12px!important;font-weight:400!important;color:#65766d;display:block}
.event-editor-v2 .svc-character-empty{display:block!important;font-size:13px;font-weight:400;padding:16px;background:#f6f8f6}
.event-editor-v2 :is(.svc-character-row,.svc-event-extra-row){display:grid;grid-template-columns:minmax(0,1fr) 100px 38px;gap:8px;align-items:center;margin:10px 0}
.event-editor-v2 :is(.svc-character-row,.svc-event-extra-row) button{width:36px;height:36px;min-height:36px;border-radius:6px!important;background:#fff5f3!important;color:#a74e40!important}
.event-editor-v2 .svc-event-extras-editor,.event-editor-v2 .svc-duration-pricing-editor{border:0!important;padding:0!important;background:none!important;box-shadow:none!important}
.event-editor-v2 .svc-booking-payment-option{min-height:0!important;padding:12px 14px!important;border-radius:8px!important;box-shadow:none!important;gap:4px!important}
.event-editor-v2 .svc-booking-payment-option span{display:block!important;font-size:12px!important;font-weight:400!important;color:#65766d}
.event-editor-v2 .svc-booking-payment-preview{display:grid!important;padding:12px!important;border-radius:8px!important;font-size:13px!important}
.event-editor-v2 .event-reservation-settings>h3:not(:first-child){border-top:1px solid #e4eae6;padding-top:20px;margin-top:20px}
.event-editor-v2 .event-resources{border:0;padding:0;margin:0}
.event-editor-v2 .svc-pricing-mode-card{border-radius:8px!important;box-shadow:none!important;padding:14px!important}
.event-editor-v2 .svc-pricing-mode-card span{font-size:12px!important;font-weight:400!important;line-height:1.5;display:block;margin-top:5px}
.event-catalog-details{display:block;font-size:12px!important;font-weight:400!important;color:#67786f;line-height:1.6;margin-top:4px;white-space:normal}
.event-editor-v2 button:focus-visible,.event-editor-v2 input:focus-visible,.event-editor-v2 select:focus-visible{outline:2px solid #168675;outline-offset:2px}
@media(max-width:640px){.event-editor-workspace.event-editor-v2{grid-template-columns:minmax(0,1fr);gap:18px}.event-editor-v2 .event-editor-nav{display:flex;overflow-x:auto;gap:6px;position:static;padding-bottom:6px}.event-editor-v2 .event-editor-nav button{flex:0 0 auto;padding:10px 12px}.event-editor-v2 .svc-experience-price-row{grid-template-columns:minmax(0,1fr)!important;padding:14px!important}.event-editor-v2 .svc-experience-price-row>.svc-mini-toggle{grid-column:1!important}.event-editor-v2 .svc-experience-price-row>strong{padding-right:70px}.event-editor-v2 .svc-booking-payment-options{grid-template-columns:1fr!important}.event-editor-v2 :is(.svc-character-row,.svc-event-extra-row){grid-template-columns:minmax(0,1fr) 76px 32px}.event-editor-v2 .svc-price-mode-mini{width:fit-content!important}}

.svc-sheet:has(.event-editor-v2){width:min(960px,100%)!important;max-width:100%!important}
.svc-sheet:has(.event-editor-v2) .svc-sheet-head h2{font-family:inherit!important;font-size:20px!important;font-weight:600!important;letter-spacing:0!important}
.event-editor-v2 .svc-character-editor{gap:6px!important}
.event-editor-v2 .svc-character-editor>p{margin:2px 0 6px!important}
.event-editor-v2 :is(.svc-character-row,.svc-event-extra-row){margin:4px 0!important}
.event-editor-v2 .svc-character-editor-head{margin:8px 0!important}
.event-editor-v2 .svc-event-extra-row input:first-child{width:100%;min-width:0}
`;

export default function ServicesSection(props) {
 useLanguage(); return <BusinessRegionContext.Provider value={props.businessDoc || {}}><ServicesSectionContent {...props}/></BusinessRegionContext.Provider>; }

function ServiceCurrency(){const region=useBusinessRegion();return regionMoney(region).resolvedOptions().currency;}
